import { authorizedFetch } from '../auth/authorizedFetch';
import { API_BASE_URL as BASE } from '../auth/config';

export interface DealSummary {
  dealId: string;
  title: string;
  counterpart: string;
  primaryProducerId?: string;
  startDate: string;
  headcount: number;
  status: string;
  species?: string | null;
  deal_reference_no?: string;
  registrationTotalAnimals?: number;
  registrationAnimalsWithEid?: number;
  registrationAnimalsWithoutEid?: number;
  // The overview split. `isScpDeal` false means this deal sits in the AVCS
  // Deal Matrix; `scpName` is the programme it belongs to, or null.
  isScpDeal?: boolean;
  scpName?: string | null;
  valueChainId?: string;
  purchaserId?: string;
  purchaserName?: string;
  /** Closed under an approved close-out override rather than on its paperwork. */
  completedUnderOverride?: boolean;
  // Derived server-side from Animal.sex where the deal has animals, and only
  // falling back to the captured totals when it does not. `source` says which,
  // so the UI can be honest about where the number came from.
  sexSplit?: DealSexSplit;
}

export interface DealSexSplit {
  male: number;
  female: number;
  unknown: number;
  source: 'animals' | 'captured';
}

/** One document the deal owes (or has). Derived server-side from what each
 *  party is — never recomputed here, or the two would drift. */
export interface DealDocumentRequirement {
  // '' is a deal-level requirement (weight receipt, traceability) that
  // evidences the consignment rather than one of the two parties.
  party: 'SELLER' | 'PURCHASER' | '';
  code: string;
  label: string;
  reason: string;
  satisfied: boolean;
  /**
   * DOCUMENT is an upload; FIELD is a capture field the deal's Supply Chain
   * Program marks required. Both travel in one list because the operator sees
   * one checklist, but only a DOCUMENT can be satisfied by uploading a file —
   * anything rendering an upload control must filter on this.
   *
   * Defaulted when absent so a backend predating the field still parses.
   */
  kind: 'DOCUMENT' | 'FIELD';
  /** Outstanding, but covered by an approved close-out override. Shown, not hidden. */
  waived?: boolean;
  /**
   * Who met the row: 'entity' when a document filed once on the entity itself
   * satisfies it, 'deal' when it was uploaded on this deal, '' when open.
   */
  satisfied_by?: 'entity' | 'deal' | '';
  /** The date on the entity document that met the row (ISO), when satisfied_by is 'entity'. */
  entity_document_date?: string | null;
}

export interface RmisOutcome {
  status: string;
  error?: string | null;
  last_attempt_at?: string | null;
}

export interface TrackingData {
  dealId: string;
  deal_no?: string;
  deal_reference_no?: string;
  primaryProducerId?: string;
  headcount: number;
  total_mortalities?: number;
  feedlotPerformance?: {
    fcr?: number | null;
    fcrEstimated?: number | null;
    fcrIsEstimated?: boolean;
    avg_adg?: number;
    avgFeedlotDays?: number;
    feedlotDaysCount?: number;
    feedConsumedKg?: number;
    totalWeightGainKg?: number;
  };
  glns: any[]; // TODO: refine types when schema is finalized
  dates?: Record<string,string>;
  documents?: any[];
  transportDocuments?: any[];
  woolDocuments?: any[];
  woolSessions?: any[];
  woolBales?: any[];
  vaccinations?: any[];
  dealEvents?: any[];
  rmisOutcomes?: Record<string, RmisOutcome>;
}

export interface DealDocumentUploadInput {
  dealId: string;
  documentType?: string;
  file: File;
  referenceNumber?: string;
  dateOfDocument: string;
  saleValue?: string;
  notes?: string;
  visibility?: 'ALL' | 'ADMIN';
  sharedWithProducer?: boolean;
  // Which side of the deal the document evidences — the same document kind
  // (ID copy, GLN certificate) is required of both parties, so an upload
  // must say whose requirement it satisfies. Blank/omitted = the deal itself.
  party?: 'SELLER' | 'PURCHASER' | '';
  // The health-declaration answer this file evidences, as the answer's own
  // field name (`examined_before_and_on_loading`, …). Only set by the
  // declaration step, where each question carries its own optional upload.
  declarationField?: string;
}

export interface DealUpsertInput {
  /** What is being bought before any animal is registered (wizard step 3). */
  plannedSpecies?: string;
  plannedBreedId?: string | null;
  isScpDeal?: boolean;
  dealNo?: string;
  dealReferenceNo?: string;
  dealDate?: string;
  totalAnimals?: number;
  totalMortalities?: number;
  isCompleted?: boolean;
  isCancelled?: boolean;
  primaryProducerId?: string;
  valueChainId?: string;
  purchaserId?: string | null;
  sellerAgentName?: string;
  sellerAgentContact?: string;
  purchaserAgentName?: string;
  purchaserAgentContact?: string;
  totalMale?: number | null;
  totalFemale?: number | null;
  identificationType?: string;
  totalWeightKg?: string | null;
  priceMale?: string | null;
  priceFemale?: string | null;
  // Purchase price per kg live weight, split like the counts. Distinct from
  // priceMale/priceFemale, which are per head.
  pricePerKgMale?: string | null;
  pricePerKgFemale?: string | null;
  pricePerKgUnknown?: string | null;
  totalPriceExVat?: string | null;
  totalPriceInclVat?: string | null;
  penaltyTerms?: string;
  origin?: string;
  purchaseChannel?: string;
  traceabilityDocumented?: boolean;
  examinedBeforeAndOnLoading?: boolean | null;
  freeOfListedDiseases?: boolean | null;
  diseasesPresent?: string[];
  diseasesOther?: string;
  clinicalReportAvailable?: boolean | null;
  withdrawalPeriodsAdhered?: boolean | null;
  purchaserDocumentsRequired?: boolean | null;
  purchaserDocumentsCompleted?: boolean | null;
  feedingSystem?: string;
  feedingSystemOther?: string;
  animalByProductsFed?: boolean | null;
  animalByProducts?: string[];
  animalByProductsOther?: string;
  // The declaration's own yes/no questions, distinct from the medication
  // rows (null = not asked).
  treatedWithMedications?: boolean | null;
  treatedWithAntibiotics?: boolean | null;
}

export interface WoolFinanceUploadInput {
  dealId: string;
  documentTypeId: string;
  saleValue: number;
  saleDate: string;
  file: File;
  notes?: string;
  visibility?: 'ALL' | 'ADMIN';
}

export interface TransportJourneyUploadInput {
  dealEventId: string;
  dispatchEventId?: string;
  courierId: string;
  loadingDate: string;
  offloadingDate?: string;
  vehicleRegistration: string;
  startKm: number;
  endKm: number;
  waybillNumber?: string;
  totalCost?: number;
  waybillFile?: File;
  invoiceFile?: File;
  documentVisibility?: 'ALL' | 'ADMIN';
  // Transportation detail (deal capture spec): where and when loading
  // happened, who drove, the Stock Theft Act removal certificate, and any
  // animals down/dead on arrival.
  loadingTime?: string;
  loadingPoint?: string;
  offloadingPoint?: string;
  driverName?: string;
  driverContact?: string;
  removalCertFile?: File | null;
  animalsDown?: number | null;
  animalsDownReason?: string;
}

export interface CourierSummary {
  id: string;
  name: string;
  contactNumber?: string;
  email?: string;
  active?: boolean;
}

export interface CreateCourierInput {
  name: string;
  contactNumber?: string;
  email?: string;
  active?: boolean;
}

export interface FeedOption {
  id: string;
  name: string;
  description?: string;
  costPerKg?: number;
  stockQty?: number;
}

export interface FeedConsumptionItem {
  id: string;
  feedId: string;
  dealId: string;
  quantityKg: number;
  date: string;
}

export interface FeedConsumptionInput {
  feedId: string;
  dealId: string;
  quantityKg: number;
  date: string;
}

export interface CreateFeedOptionInput {
  name: string;
  description?: string;
  costPerKg?: number;
  stockQty?: number;
}

export interface AnimalUpsertInput {
  dealId: string;
  species: 'SHEEP' | 'GOAT' | 'CATTLE';
  vid?: string;
  eidTag?: string;
  currentEntity?: string;
  status?: 'ACTIVE' | 'SOLD' | 'DECEASED';
  breed?: string;
  dob?: string | null;
  sex?: 'M' | 'F' | null;
}

export interface DealAdminOverviewItem {
  id: string;
  deal_no?: string;
  deal_reference_no?: string;
  deal_date?: string;
  total_animals?: number;
  is_completed?: boolean;
  primary_producer?: {
    id: string;
    entity_name?: string;
  };
  value_chain?: {
    id: string;
    name?: string;
  };
}

export interface AnimalLocationEntry {
  animal_id: string;
  eid_tag?: string | null;
  vid?: string | null;
  species?: string | null;
  breed_id?: string | null;
  breed_name?: string | null;
  sex?: string | null;
  dob?: string | null;
  age_days?: number | null;
  age_months?: number | null;
  status?: string | null;
  deal_id?: string | null;
  deal_no?: string | null;
  current_location?: {
    entity_id?: string | null;
    entity_name?: string | null;
    entity_type?: string;
    gln?: string | null;
    value_chain_id?: string;
    value_chain_name?: string;
    source_entity_id?: string | null;
    source_entity_name?: string | null;
    source_entity_type?: string | null;
    source_gln?: string | null;
    destination_entity_id?: string | null;
    destination_entity_name?: string | null;
    destination_entity_type?: string | null;
    destination_gln?: string | null;
    latest_event_date?: string;
    latest_event_type?: string;
    latest_event_category?: string;
  } | null;
  last_weight?: {
    weight_kg?: number;
    weigh_date?: string;
    weight_type?: string;
  } | null;
  feedlot_metrics?: {
    feedlot_receive_weight?: number | null;
    feedlot_receive_date?: string | null;
    days_on_feed?: number | null;
    weight_gain_kg?: number | null;
    adg_kg_per_day?: number | null;
    fcr?: number | null;
    fcr_scope?: string | null;
  } | null;
}

export interface SelectOption {
  id: string;
  label: string;
  location?: string;
  /** Head on the event, where the option is an event (used to split a shared journey cost). */
  animalCount?: number;
}

export interface EntityContactSummary {
  id: string;
  entityId?: string;
  userId?: string;
  /** The linked portal account, where one exists. Not an identity for the
   *  contact -- one account can be a contact on several entities -- so use it
   *  only when the *account* is the subject (e.g. who has portal access). */
  username?: string;
  firstName?: string;
  lastName?: string;
  /** Resolved display name, from the contact record. Never empty. */
  name: string;
  email?: string;
  phoneNo?: string;
  isPrimary?: boolean;
}

export interface EntitySummary {
  id: string;
  entityNo?: string;
  entityName: string;
  // Legal name on the company registration, kept separate from entityName:
  // entityName is the trading name, this is what appears on invoices and VAT
  // returns. Blank means the two are the same.
  registeredBusinessName?: string;
  vatNumber?: string;
  // Deal-capture party detail. Drives which supporting documents the deal's
  // completeness gate requires of this party.
  partyKind?: '' | 'INDIVIDUAL' | 'ENTITY';
  identityNumber?: string;
  entityTypeOther?: string;
  farmName?: string;
  district?: string;
  province?: string;
  vatRegistered?: boolean;
  glnApplicable?: boolean;
  entityType?: string;
  valueChainId?: string;
  valueChainName?: string;
  wooliesProducer?: boolean;
  costPerDay?: string | null;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  latitude?: string;
  longitude?: string;
  contacts: EntityContactSummary[];
}

export interface CreateEntityInput {
  entityName: string;
  entityNo?: string;
  // Legal name on the company registration, kept separate from entityName:
  // entityName is the trading name, this is what appears on invoices and VAT
  // returns. Blank means the two are the same.
  registeredBusinessName?: string;
  vatNumber?: string;
  // Deal-capture party detail. Drives which supporting documents the deal's
  // completeness gate requires of this party.
  partyKind?: '' | 'INDIVIDUAL' | 'ENTITY';
  identityNumber?: string;
  entityTypeOther?: string;
  farmName?: string;
  district?: string;
  province?: string;
  vatRegistered?: boolean;
  glnApplicable?: boolean;
  entityType?: string;
  valueChainId?: string;
  wooliesProducer?: boolean;
  costPerDay?: string | null;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  latitude?: string;
  longitude?: string;
}

export interface CreateEntityContactInput {
  entityId: string;
  // Optional — a contact can exist without a linked user account. When omitted
  // or empty, the contact is created from its own name/email/phone only.
  userId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNo?: string;
  isPrimary?: boolean;
}

export interface UpdateEntityInput {
  entityName?: string;
  entityNo?: string;
  // Legal name on the company registration, kept separate from entityName:
  // entityName is the trading name, this is what appears on invoices and VAT
  // returns. Blank means the two are the same.
  registeredBusinessName?: string;
  vatNumber?: string;
  // Deal-capture party detail. Drives which supporting documents the deal's
  // completeness gate requires of this party.
  partyKind?: '' | 'INDIVIDUAL' | 'ENTITY';
  identityNumber?: string;
  entityTypeOther?: string;
  farmName?: string;
  district?: string;
  province?: string;
  vatRegistered?: boolean;
  glnApplicable?: boolean;
  entityType?: string;
  valueChainId?: string;
  wooliesProducer?: boolean;
  costPerDay?: string | null;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  latitude?: string;
  longitude?: string;
}

export interface UpdateEntityContactInput {
  entityId?: string;
  // Pass a valid id to link a user, or an empty string to unlink (contact with
  // no user account). Omit entirely to leave the existing link unchanged.
  userId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNo?: string;
  isPrimary?: boolean;
}

export interface DealDocumentSummary {
  id: string;
  documentTypeId?: string;
  documentTypeName?: string;
  fileUrl?: string;
  referenceNumber?: string;
  dateOfDocument?: string;
  saleValue?: string;
  notes?: string;
  visibility?: 'ALL' | 'ADMIN';
  /** Released to the producer on their own view of the deal. */
  sharedWithProducer?: boolean;
  /** Filed by the producer through their own view. */
  uploadedByProducer?: boolean;
  /** Invoice total the producer typed when sending. Unverified; never booked. */
  producerStatedAmount?: string;
  /** What a value on this document books (SALE, FEED, PURCHASE …, NONE, '' = sale). */
  financeRecordType?: string;
}

export interface UpdateDealDocumentInput {
  documentType?: string;
  file?: File | null;
  referenceNumber?: string;
  dateOfDocument?: string;
  saleValue?: string;
  notes?: string;
  visibility?: 'ALL' | 'ADMIN';
  sharedWithProducer?: boolean;
}

// --- Cross-app document index (GET /api/documents/) ----------------------
export type DocumentSource = 'deal' | 'transport' | 'wool' | 'feedlot' | 'data_session';

export interface DocumentIndexRow {
  id: string;
  source: DocumentSource;
  sourceLabel: string;
  documentType?: string;
  documentTypeId?: string;
  title: string;
  dealId?: string;
  dealNo?: string;
  primaryProducerId?: string;
  primaryProducerName?: string;
  fileUrl?: string;
  fileName?: string;
  referenceNumber?: string;
  documentDate?: string;
  saleValue?: string;
  notes?: string;
  visibility?: 'ALL' | 'ADMIN';
  createdAt?: string;
}

export interface DocumentIndexResponse {
  count: number;
  page: number;
  pageSize: number;
  numPages: number;
  sources: DocumentSource[];
  results: DocumentIndexRow[];
}

export interface FetchDocumentsParams {
  sources?: DocumentSource[];
  dealId?: string;
  documentType?: string;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export interface TransportJourneySummary {
  id: string;
  dealEventId?: string;
  dispatchEventId?: string;
  loadingDate?: string;
  offloadingDate?: string;
  vehicleRegistration?: string;
  courierId?: string;
  waybillNo?: string;
  startKm?: number;
  endKm?: number;
  waybillUrl?: string;
  invoiceUrl?: string;
  totalCost?: number;
  weighbridgeRecordId?: string;
  weighbridgeGrossWeightKg?: string;
  weighbridgeTareWeightKg?: string;
  weighbridgeNetWeightKg?: string;
  weighbridgeRecordedAt?: string;
  documentVisibility?: 'ALL' | 'ADMIN';
  loadingTime?: string;
  loadingPoint?: string;
  offloadingPoint?: string;
  driverName?: string;
  driverContact?: string;
  animalsDown?: number;
  animalsDownReason?: string;
  removalCertificateUrl?: string;
}

export interface UpdateTransportJourneyInput {
  dealEventId?: string;
  dispatchEventId?: string | null;
  courierId?: string;
  loadingDate?: string;
  offloadingDate?: string;
  vehicleRegistration?: string;
  startKm?: number;
  endKm?: number;
  waybillNumber?: string;
  totalCost?: number;
  waybillFile?: File | null;
  invoiceFile?: File | null;
  documentVisibility?: 'ALL' | 'ADMIN';
  loadingTime?: string;
  loadingPoint?: string;
  offloadingPoint?: string;
  driverName?: string;
  driverContact?: string;
  removalCertFile?: File | null;
  animalsDown?: number | null;
  animalsDownReason?: string;
}

export interface WoolFinanceSummary {
  id: string;
  dealId?: string;
  documentTypeId?: string;
  documentTypeName?: string;
  saleValue?: number;
  saleDate?: string;
  documentScanUrl?: string;
  notes?: string;
  visibility?: 'ALL' | 'ADMIN';
}

export interface UpdateWoolFinanceInput {
  dealId?: string;
  documentTypeId?: string;
  saleValue?: number;
  saleDate?: string;
  file?: File | null;
  notes?: string;
  visibility?: 'ALL' | 'ADMIN';
}

export interface GS1CodeSummary {
  id: string;
  gs1Code: string;
  gs1Type: string;
  entityId?: string;
  entityName: string;
  productTypeId?: string;
  productTypeName: string;
  associatedGlnId?: string;
  associatedGlnCode: string;
  source?: 'POOL' | 'CUSTOMER_OWNED' | string;
  isAvailable?: boolean;
  isAllocated?: boolean;
}

export interface GS1CodeUpsertInput {
  gs1Code: string;
  gs1Type: 'GLN' | 'GTIN';
  entityId: string;
  productTypeId?: string;
  associatedGlnId?: string;
}

function mapGs1Code(row: any): GS1CodeSummary {
  return {
    id: String(row.id),
    gs1Code: String(row.gs1_code || ''),
    gs1Type: String(row.gs1_type || ''),
    entityId: row.entity != null ? String(row.entity) : undefined,
    entityName: row.entity_name || '',
    productTypeId: row.product_type != null ? String(row.product_type) : undefined,
    productTypeName: row.product_type_name || '',
    associatedGlnId: row.associated_gln != null ? String(row.associated_gln) : undefined,
    associatedGlnCode: row.associated_gln_code || '',
    source: row.source ? String(row.source) : undefined,
    isAvailable: typeof row.is_available === 'boolean' ? row.is_available : undefined,
    isAllocated: typeof row.is_allocated === 'boolean' ? row.is_allocated : undefined,
  };
}

async function handleJson(res: Response){
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
  return res.json();
}

function parseOptionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const RECEIVE_EVENT_TYPES = new Set(['FARM_REC', 'FEED_REC', 'ABAT_REC']);

function buildFallbackTransportDocuments(dealEvents: any[]): any[] {
  if (!Array.isArray(dealEvents) || dealEvents.length === 0) {
    return [];
  }

  return dealEvents
    .filter((row: any) => RECEIVE_EVENT_TYPES.has(String(row?.event_type || '')))
    .sort((a: any, b: any) => {
      const aTime = Date.parse(a?.event_date || a?.created_at || '') || 0;
      const bTime = Date.parse(b?.event_date || b?.created_at || '') || 0;
      return bTime - aTime;
    })
    .map((row: any) => ({
      id: `event-${String(row?.id || '')}`,
      deal_event: row?.id != null ? String(row.id) : '',
      loading_date: row?.event_date ? String(row.event_date) : '',
      vehicle_registration: '',
      courier: '',
      waybill_no: '',
      start_km: undefined,
      end_km: undefined,
      waybill: '',
      invoice: '',
      total_cost: undefined,
    }));
}

let dealsInFlight: Promise<DealSummary[]> | null = null;
let dealsLastValue: DealSummary[] = [];
let dealsCooldownUntil = 0;

let couriersInFlight: Promise<CourierSummary[]> | null = null;
let couriersLastValue: CourierSummary[] = [];
let couriersCooldownUntil = 0;

let feedOptionsInFlight: Promise<FeedOption[]> | null = null;
let feedOptionsLastValue: FeedOption[] = [];
let feedOptionsCooldownUntil = 0;

const feedConsumptionInFlight = new Map<string, Promise<FeedConsumptionItem[]>>();
const feedConsumptionLastValue = new Map<string, FeedConsumptionItem[]>();
const feedConsumptionCooldownUntil = new Map<string, number>();

// --- Per-key dedup for deal-scoped fetches ---
const dealEventsInFlight = new Map<string, Promise<any[]>>();
const dealEventsLastValue = new Map<string, any[]>();
const dealEventsCooldownUntil = new Map<string, number>();

const transportJourneysInFlight = new Map<string, Promise<TransportJourneySummary[]>>();
const transportJourneysLastValue = new Map<string, TransportJourneySummary[]>();
const transportJourneysCooldownUntil = new Map<string, number>();

const woolFinancesInFlight = new Map<string, Promise<WoolFinanceSummary[]>>();
const woolFinancesLastValue = new Map<string, WoolFinanceSummary[]>();
const woolFinancesCooldownUntil = new Map<string, number>();

const woolSessionsInFlight = new Map<string, Promise<WoolSessionSummary[]>>();
const woolSessionsLastValue = new Map<string, WoolSessionSummary[]>();
const woolSessionsCooldownUntil = new Map<string, number>();

// --- Singleton dedup for global fetches ---
let entitiesInFlight: Promise<EntitySummary[]> | null = null;
let entitiesLastValue: EntitySummary[] = [];
let entitiesCooldownUntil = 0;

let entityContactsAllInFlight: Promise<EntityContactSummary[]> | null = null;
let entityContactsAllLastValue: EntityContactSummary[] = [];
let entityContactsAllCooldownUntil = 0;

let gs1CodesInFlight: Promise<GS1CodeSummary[]> | null = null;
let gs1CodesLastValue: GS1CodeSummary[] = [];
let gs1CodesCooldownUntil = 0;

let productTypesInFlight: Promise<SelectOption[]> | null = null;
let productTypesLastValue: SelectOption[] = [];
let productTypesCooldownUntil = 0;

let valueChainOptionsInFlight: Promise<SelectOption[]> | null = null;
let valueChainOptionsLastValue: SelectOption[] = [];
let valueChainOptionsCooldownUntil = 0;

let adminUsersInFlight: Promise<UserSummary[]> | null = null;
let adminUsersLastValue: UserSummary[] = [];
let adminUsersCooldownUntil = 0;

function resetEntitiesCache() {
  entitiesInFlight = null;
  entitiesLastValue = [];
  entitiesCooldownUntil = 0;
}

function resetEntityContactsCache() {
  entityContactsAllInFlight = null;
  entityContactsAllLastValue = [];
  entityContactsAllCooldownUntil = 0;
}

function resetGs1CodesCache() {
  gs1CodesInFlight = null;
  gs1CodesLastValue = [];
  gs1CodesCooldownUntil = 0;
}

/** The full deal detail record, as the API serves it.
 *  Used where the admin-overview aggregate does not carry a field — notably the
 *  animals/origin/pricing capture block, which the overview endpoint has no
 *  reason to include. */
/** One medication declared on a deal, with its withdrawal arithmetic. */
export interface DealMedication {
  id: string;
  medicineId?: string;
  medicationName: string;
  displayName: string;
  isAntibiotic: boolean;
  lastTreatmentDate?: string | null;
  withdrawalPeriodDays?: number | null;
  effectiveWithdrawalDays?: number | null;
  clearsOn?: string | null;
  daysSinceTreatment?: number | null;
  notes: string;
}

/** The deal's withdrawal picture as one answer — the LATEST clearance across
 *  every medication, since the deal is only safe once the last has cleared.
 *  `allCleared` is null when nothing can be judged: "we cannot tell" is a
 *  different answer from "no". */
export interface DealMedicationSummary {
  count: number;
  latestClearsOn?: string | null;
  daysUntilClear?: number | null;
  allCleared?: boolean | null;
  referenceDate?: string | null;
}

export const LISTED_DISEASES: Array<{ code: string; label: string }> = [
  { code: 'FMD', label: 'Foot and Mouth Disease' },
  { code: 'BRUCELLOSIS', label: 'Brucellosis' },
  { code: 'TB', label: 'Tuberculosis' },
  { code: 'ANTHRAX', label: 'Anthrax' },
  { code: 'RABIES', label: 'Rabies' },
  { code: 'LUMPY_SKIN', label: 'Lumpy Skin Disease' },
  { code: 'RIFT_VALLEY', label: 'Rift Valley Fever' },
  { code: 'BLUETONGUE', label: 'Bluetongue' },
  { code: 'SHEEP_SCAB', label: 'Sheep Scab' },
  { code: 'CONTAGIOUS_ABORTION', label: 'Contagious Abortion' },
];

export const FEEDING_SYSTEMS: Array<{ code: string; label: string }> = [
  { code: 'GRAZING', label: 'Grazing (pasture/veld)' },
  { code: 'GRAZING_SUPPLEMENTARY', label: 'Grazing + supplementary' },
  { code: 'FEED_ADAPTED', label: 'Feed-adapted' },
  { code: 'FEEDLOT_FINISHED', label: 'Feedlot finished' },
  { code: 'OTHER', label: 'Other' },
];

export const ANIMAL_BY_PRODUCTS: Array<{ code: string; label: string }> = [
  { code: 'MEAT_MEAL', label: 'Meat meal' },
  { code: 'BONE_MEAL', label: 'Bone meal' },
  { code: 'BLOOD_MEAL', label: 'Blood meal' },
  { code: 'RENDERED_RUMINANT', label: 'Rendered ruminant proteins' },
  { code: 'POULTRY_BY_PRODUCT', label: 'Poultry by-product' },
  { code: 'OTHER', label: 'Other' },
];

export function mapDealMedication(row: any): DealMedication {
  return {
    id: String(row?.id || ''),
    medicineId: row?.medicine ? String(row.medicine) : undefined,
    medicationName: row?.medication_name ? String(row.medication_name) : '',
    displayName: String(row?.display_name || 'Unnamed medication'),
    isAntibiotic: row?.is_antibiotic === true,
    lastTreatmentDate: row?.last_treatment_date ?? null,
    withdrawalPeriodDays: row?.withdrawal_period_days ?? null,
    effectiveWithdrawalDays: row?.effective_withdrawal_days ?? null,
    clearsOn: row?.clears_on ?? null,
    daysSinceTreatment: row?.days_since_treatment ?? null,
    notes: row?.notes ? String(row.notes) : '',
  };
}

export function mapDealMedicationSummary(row: any): DealMedicationSummary {
  return {
    count: Number(row?.count || 0),
    latestClearsOn: row?.latest_clears_on ?? null,
    daysUntilClear: row?.days_until_clear ?? null,
    // Preserved as null rather than coerced to false — the distinction is the
    // whole point of the field.
    allCleared: row?.all_cleared === null || row?.all_cleared === undefined
      ? null
      : Boolean(row.all_cleared),
    referenceDate: row?.reference_date ?? null,
  };
}

export async function fetchDealById(dealId: string): Promise<any> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/`);
  return handleJson(res);
}

/** The deal's document checklist, derived server-side from what each party is. */
export async function fetchDealDocumentRequirements(
  dealId: string,
): Promise<{ requirements: DealDocumentRequirement[]; canComplete: boolean }> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/`);
  const data: any = await handleJson(res);
  const rows = Array.isArray(data?.document_requirements) ? data.document_requirements : [];
  return {
    requirements: rows.map((row: any) => ({
      ...row,
      kind: row?.kind === 'FIELD' ? 'FIELD' : 'DOCUMENT',
    })),
    canComplete: data?.can_complete === true,
  };
}

export async function fetchDeals(options?: { enrichHeadcount?: boolean }): Promise<DealSummary[]> {
  const now = Date.now();
  if (now < dealsCooldownUntil) {
    return dealsLastValue;
  }
  if (dealsInFlight) {
    return dealsInFlight;
  }

  dealsInFlight = (async () => {
    try {
      const res = await authorizedFetch('/api/deals/');
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
          ? retryAfterSeconds * 1000
          : 30000;
        dealsCooldownUntil = Date.now() + waitMs;
        return dealsLastValue;
      }

      const data = await handleJson(res);
      const rows = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.results)
          ? (data as any).results
          : [];

      if (!Array.isArray(rows)) {
        return dealsLastValue;
      }

      const mapped: DealSummary[] = rows.map((d: any) => ({
        dealId: d.id,
        title: d.deal_no || 'Untitled Deal',
        deal_reference_no: d.deal_reference_no,
        counterpart: d.primary_producer_name || '—',
        primaryProducerId: d.primary_producer != null ? String(d.primary_producer) : undefined,
        startDate: d.deal_date || d.created_at,
        headcount: d.headcount ?? d.total_animals ?? 0,
        // Cancelled outranks completed — a called-off deal did not run to completion.
        status: d.is_cancelled ? 'Cancelled' : d.is_completed ? 'Completed' : 'Active',
        completedUnderOverride: d.completed_under_override === true,
        species: d.species ?? null,
        registrationTotalAnimals: d.registration_animal_summary?.total_animals,
        registrationAnimalsWithEid: d.registration_animal_summary?.animals_with_eid,
        registrationAnimalsWithoutEid: d.registration_animal_summary?.animals_without_eid,
        isScpDeal: d.is_scp_deal === true,
        scpName: d.scp_name ?? null,
        purchaserId: d.purchaser != null ? String(d.purchaser) : undefined,
        purchaserName: d.purchaser_name ? String(d.purchaser_name) : undefined,
        sexSplit: d.sex_split ? {
          male: Number(d.sex_split.male || 0),
          female: Number(d.sex_split.female || 0),
          unknown: Number(d.sex_split.unknown || 0),
          source: d.sex_split.source === 'animals' ? 'animals' : 'captured',
        } : undefined,
        valueChainId: d.value_chain != null ? String(d.value_chain) : undefined,
      }));

      dealsLastValue = mapped;
      return mapped;
    } catch {
      return dealsLastValue;
    } finally {
      dealsInFlight = null;
    }
  })();

  return dealsInFlight;
}

export async function fetchTracking(
  dealId: string,
  options?: { includeSupplementary?: boolean },
): Promise<TrackingData> {
  try {
    const includeSupplementary = options?.includeSupplementary !== false;
    // console.log('[fetchTracking] Fetching tracking for deal:', dealId);
    
    // Parallelize API calls to reduce loading time
    const [trackingRes, dealRes] = await Promise.all([
      authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/tracking/`),
      authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/`)
    ]);
    
    const [data, dealData] = await Promise.all([
      handleJson(trackingRes),
      handleJson(dealRes)
    ]);
    
    let glns = data.glns || [];
    
    let transportDocuments = [];
    let woolDocuments = [];
    let woolSessions = [];
    let woolBales = [];
    let feedlotPerformance: { fcr?: number | null; fcrEstimated?: number | null; fcrIsEstimated?: boolean; avg_adg?: number; avgFeedlotDays?: number; feedlotDaysCount?: number; feedConsumedKg?: number; totalWeightGainKg?: number } | null = null;

    if (includeSupplementary) {
      // Fetch related resources in parallel for faster dashboard load
      const [
        journeysResult,
        dealEventsResult,
        woolResult,
        sessionsResult,
        balesResult,
        feedlotResult,
        slaughterResult,
      ] = await Promise.allSettled([
        authorizedFetch(`/api/transport/journeys/?deal=${encodeURIComponent(dealId)}`),
        authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/events/`),
        authorizedFetch(`/api/wool/finances/?deal=${encodeURIComponent(dealId)}`),
        authorizedFetch(`/api/wool/sessions/`),
        authorizedFetch(`/api/wool/bales/`),
        authorizedFetch(`/api/feedlot/deals/${encodeURIComponent(dealId)}/performance/`),
        authorizedFetch(`/api/abattoir/summaries/`),
      ]);

      let dealEvents: any[] = [];

      if (dealEventsResult.status === 'fulfilled') {
        if (dealEventsResult.value.ok) {
          const allDealEvents = await dealEventsResult.value.json();
          dealEvents = Array.isArray(allDealEvents)
            ? allDealEvents
            : Array.isArray(allDealEvents?.results) ? allDealEvents.results : [];
        }
      } else {
        console.warn('[fetchTracking] Failed to fetch deal events:', dealEventsResult.reason);
      }

      // Transport documents (journeys/waybills)
      if (journeysResult.status === 'fulfilled') {
        if (journeysResult.value.ok) {
          const allJourneys = await journeysResult.value.json();
          const journeyRows = Array.isArray(allJourneys)
            ? allJourneys
            : Array.isArray(allJourneys?.results) ? allJourneys.results : [];
          transportDocuments = journeyRows.map((row: any) => ({
            id: String(row?.id || ''),
            deal_event: row?.deal_event != null ? String(row.deal_event) : '',
            loading_date: row?.loading_date ? String(row.loading_date) : '',
            vehicle_registration: row?.vehicle_registration ? String(row.vehicle_registration) : '',
            courier: row?.courier != null ? String(row.courier) : '',
            waybill_no: row?.waybill_no ? String(row.waybill_no) : '',
            start_km: row?.start_km,
            end_km: row?.end_km,
            waybill: row?.waybill ? String(row.waybill) : '',
            invoice: row?.invoice ? String(row.invoice) : '',
            total_cost: row?.total_cost,
          }));
        }
      } else {
        console.warn('[fetchTracking] Failed to fetch transport documents:', journeysResult.reason);
      }

      if (transportDocuments.length === 0 && dealEvents.length > 0) {
        transportDocuments = buildFallbackTransportDocuments(dealEvents);
      }

      data.dealEvents = dealEvents;

      // RMIS outcomes map (deal_event_id → RmisOutcome) — derived directly from the
      // `rmis` field now embedded on each DealEvent by the backend serializer.
      // The separate /api/ingest/rmis/outcomes/ endpoint is no longer needed here.
      const rmisOutcomes: Record<string, RmisOutcome> = {};
      for (const evt of dealEvents) {
        if (evt?.rmis?.status && evt.id) {
          rmisOutcomes[String(evt.id)] = {
            status: evt.rmis.status,
            error: evt.rmis.error ?? null,
            last_attempt_at: evt.rmis.last_attempt_at ?? null,
          };
        }
      }
      data.rmisOutcomes = rmisOutcomes;

      // Slaughter summaries (deal-level aggregate from DealSlaughterSummary)
      let slaughterSummaries: any[] = [];
      if (slaughterResult.status === 'fulfilled' && slaughterResult.value.ok) {
        const slaughterData = await slaughterResult.value.json();
        const allSummaries = Array.isArray(slaughterData)
          ? slaughterData
          : Array.isArray(slaughterData?.results) ? slaughterData.results : [];
        slaughterSummaries = allSummaries.filter((s: any) => String(s.deal) === String(dealId));
      } else if (slaughterResult.status === 'rejected') {
        console.warn('[fetchTracking] Failed to fetch slaughter summaries:', slaughterResult.reason);
      }
      data.slaughterSummaries = slaughterSummaries;

      // Wool finance documents — backend doesn't filter by ?deal= so do it client-side.
      if (woolResult.status === 'fulfilled') {
        if (woolResult.value.ok) {
          const allWool = await woolResult.value.json();
          const woolRows = Array.isArray(allWool)
            ? allWool
            : Array.isArray(allWool?.results) ? allWool.results : [];
          woolDocuments = woolRows.filter((row: any) => String(row.deal) === String(dealId));
        }
      } else {
        console.warn('[fetchTracking] Failed to fetch wool documents:', woolResult.reason);
      }

      // Wool sessions and bales (filter by deal in this view)
      if (sessionsResult.status === 'fulfilled' && balesResult.status === 'fulfilled') {
        const rawSessions = sessionsResult.value.ok ? await sessionsResult.value.json() : [];
        const rawBales = balesResult.value.ok ? await balesResult.value.json() : [];
        const allSessions = Array.isArray(rawSessions) ? rawSessions : Array.isArray(rawSessions?.results) ? rawSessions.results : [];
        const allBales = Array.isArray(rawBales) ? rawBales : Array.isArray(rawBales?.results) ? rawBales.results : [];

        woolSessions = allSessions.filter((session: any) => String(session.deal) === String(dealId));

        const sessionIdsForDeal = new Set(woolSessions.map((session: any) => String(session.id)));
        woolBales = allBales.filter((bale: any) => (
          String(bale.deal) === String(dealId)
          || (bale.session != null && sessionIdsForDeal.has(String(bale.session)))
        ));
      } else {
        if (sessionsResult.status === 'rejected') {
          console.warn('[fetchTracking] Failed to fetch wool sessions:', sessionsResult.reason);
        }
        if (balesResult.status === 'rejected') {
          console.warn('[fetchTracking] Failed to fetch wool bales:', balesResult.reason);
        }
      }

      // Feedlot performance data
      if (feedlotResult.status === 'fulfilled') {
        if (feedlotResult.value.ok) {
          const feedlotData = await feedlotResult.value.json();
          const parsedFcr = parseOptionalNumber(feedlotData?.fcr);
          const parsedAvgAdg = parseOptionalNumber(feedlotData?.avg_adg);
          const parsedAvgFeedlotDays = parseOptionalNumber(feedlotData?.avg_feedlot_days);
          const parsedFeedlotDaysCount = parseOptionalNumber(feedlotData?.feedlot_days_count);

          feedlotPerformance = {
            fcr: parsedFcr,
            fcrEstimated: parseOptionalNumber(feedlotData?.fcr_estimated),
            fcrIsEstimated: Boolean(feedlotData?.fcr_is_estimated),
            avg_adg: parsedAvgAdg,
            avgFeedlotDays: parsedAvgFeedlotDays,
            feedlotDaysCount: parsedFeedlotDaysCount,
            feedConsumedKg: parseOptionalNumber(feedlotData?.feed_consumed_kg),
            totalWeightGainKg: parseOptionalNumber(feedlotData?.total_weight_gain_kg),
          };
        }
      } else {
        console.warn('[fetchTracking] Failed to fetch feedlot performance:', feedlotResult.reason);
      }

    }
    
    // Process deal detail data
    if (dealData) {
      // Store deal_no and deal_reference_no if available
      if (dealData.deal_no) data.deal_no = dealData.deal_no;
      if (dealData.deal_reference_no) data.deal_reference_no = dealData.deal_reference_no;
      // Store documents if available
      if (dealData.documents) data.documents = dealData.documents;
      // Store vaccinations if available
      if (dealData.deal_vaccinations) {
        data.vaccinations = dealData.deal_vaccinations;
      }
      // Prefer computed EID-filtered headcount from deal detail (already falls back to total_animals internally)
      // Fall back to manually-entered total_animals only if computed headcount is absent/zero
      if (dealData.headcount != null) {
        data.headcount = dealData.headcount;
      } else if (dealData.animal_summary?.total != null) {
        data.headcount = dealData.animal_summary.total;
      } else if (dealData.total_animals != null) {
        data.headcount = dealData.total_animals;
      }
      // Store total_mortalities — prefer the deal detail field (explicitly maintained
      // by the mortality upload handler) over the tracking endpoint's live count,
      // which may be 0 if deceased animals aren't linked to the deal in the DB.
      if (dealData.total_mortalities != null) {
        data.total_mortalities = dealData.total_mortalities;
      }
      // Store deal_date for accurate date display
      if (dealData.deal_date) data.deal_date = dealData.deal_date;
    }
    
    // Add inferred events based on source data
    if (Array.isArray(glns) && glns.length > 0 && dealData) {
      const totalHeadcount = data.headcount || 0;
      const primaryProducerName = dealData.primary_producer?.entity_name || dealData.primary_producer;
      const byLocation = dealData.animal_summary?.by_location || {};
      const valueChainEntityIds = dealData.value_chain || [];
      
      // Filter GLNs to only those in this deal's value chain
      if (Array.isArray(valueChainEntityIds) && valueChainEntityIds.length > 0) {
        glns = glns.filter((gln: any) => valueChainEntityIds.includes(gln.entity_id || gln.id));
      }
      
      // Build summed animal counts from actual deal events — more accurate than inferring
      // from total_animals (which may be a manually-entered override) or animal records
      // (which may lag behind the actual movements recorded in deal events).
      const dealEvtsList: any[] = (data.dealEvents as any[]) || [];
      const evtSumByType: Record<string, number> = {};
      for (const ev of dealEvtsList) {
        if (ev.event_type) {
          evtSumByType[ev.event_type] = (evtSumByType[ev.event_type] || 0) + (Number(ev.animal_count) || 0);
        }
      }
      const farmRegCount = (evtSumByType['FARM_REG'] || 0) + (evtSumByType['FARM_BIR'] || 0);
      const farmDisCount = evtSumByType['FARM_DIS'] || 0;
      const feedRecCount = evtSumByType['FEED_REC'] || 0;
      const feedDisCount = evtSumByType['FEED_DIS'] || 0;
      const farmMorCount = evtSumByType['FARM_MOR'] || 0;
      const feedMorCount = evtSumByType['FEED_MOR'] || 0;
      const abatMorCount = evtSumByType['ABAT_MOR'] || 0;

      // Detect whether an explicit Feedlot-type entity is in this value chain.
      // If not, the feedlot may be miscategorised as a Farm entity in the database —
      // in that case the first non-primary-producer Farm GLN is treated as the feedlot node.
      const hasFeedlotTyped = glns.some((g: any) => g.type === 'Feedlot');

      // The deal-level FEED_*/ABAT_* event sums below are deal-wide, not
      // per-entity, so they can only be safely applied when the value chain has
      // exactly ONE node of that type. With two feedlot (or abattoir) entities,
      // applying the deal-level received-minus-dispatched to EACH node
      // double-counts the animals (e.g. 187 on both feedlot cards → 374). When
      // there's more than one, trust the backend's authoritative per-entity
      // `current` instead.
      const feedlotNodeCount = glns.filter((g: any) =>
        g.type === 'Feedlot' ||
        (!hasFeedlotTyped && g.type === 'Farm' && g.name !== primaryProducerName),
      ).length;
      const abattoirNodeCount = glns.filter((g: any) => g.type === 'Abattoir').length;

      glns = glns.map((gln: any) => {
        const events = gln.events ? { ...gln.events } : {};
        
        // Try to match location count with multiple strategies
        let locationCount = byLocation[gln.name] || 0;
        if (locationCount === 0 && gln.gln) {
          // Try with GLN code in parentheses
          const nameWithGln = `${gln.name} (${gln.gln})`;
          locationCount = byLocation[nameWithGln] || 0;
        }
        
        // The tracking endpoint's `current` field is authoritative for live
        // value-chain location counts. Only fall back to deal detail
        // `animal_summary.by_location` when tracking did not provide current.
        const hasBackendCurrent = gln.current != null;
        let currentCount = hasBackendCurrent ? (Number(gln.current) || 0) : locationCount;

        // Determine whether this GLN should be treated as the feedlot node.
        const isFeedlotNode = gln.type === 'Feedlot' ||
          (!hasFeedlotTyped && gln.type === 'Farm' && gln.name !== primaryProducerName);

        // Add "dispatched" event for the primary producer farm using the actual FARM_DIS
        // deal event count (falls back to totalHeadcount if no deal event exists).
        if (gln.type === 'Farm' && !isFeedlotNode && primaryProducerName && gln.name === primaryProducerName && !events.dispatched) {
          const dispatchCount = farmDisCount > 0 ? farmDisCount : totalHeadcount;
          if (farmRegCount > 0 && !events.registered) events.registered = farmRegCount;
          if (dispatchCount > 0) events.dispatched = dispatchCount;
          if (farmMorCount > 0 && !events.deaths) events.deaths = farmMorCount;
        }
        
        // Add receive/dispatch events for the feedlot node.
        // Use the FEED_REC deal event count for 'received' display — this is authoritative
        // (actual animals scanned at the gate). Use the animal DB location count for
        // 'current' since that reflects what's physically there after dispatch.
        if (isFeedlotNode && feedlotNodeCount === 1) {
          const receivedForDisplay = feedRecCount > 0 ? feedRecCount : locationCount;
          const perFeedlotDispatched = feedDisCount; // dispatch is still deal-level (FEED_DIS sum)
          if (receivedForDisplay > 0 && !events.received) events.received = receivedForDisplay;
          if (perFeedlotDispatched > 0 && !events.dispatched) events.dispatched = perFeedlotDispatched;
          if (feedMorCount > 0 && !events.deaths) events.deaths = feedMorCount;
          // Deal events are fetched live on every load and are authoritative for
          // movements. The tracking endpoint's `current` aggregate can lag behind
          // a just-recorded dispatch, so prefer event math whenever we have a
          // receive count to work from; only fall back to the backend's current
          // count when there's no event data at all.
          if (receivedForDisplay > 0) {
            currentCount = Math.max(0, receivedForDisplay - perFeedlotDispatched - feedMorCount);
          }
        }
        
        // Add "received" event for other non-farm, non-feedlot, non-abattoir locations with animals.
        // Abattoir received is handled separately below from ABAT_REC deal events.
        if (!isFeedlotNode && gln.type !== 'Farm' && gln.type !== 'Feedlot' && gln.type !== 'Abattoir' && currentCount > 0 && !events.received) {
          events.received = currentCount;
        }
        
        // When an entity is acting as the feedlot node but is miscategorised as Farm,
        // override its display type to 'Feedlot' so the badge, RMIS lookup, and
        // event labels all work correctly.
        const effectiveType = (isFeedlotNode && gln.type !== 'Feedlot') ? 'Feedlot' : gln.type;

        // Add feedlot performance metrics if this is a Feedlot GLN
        let updatedGln = {
          ...gln,
          type: effectiveType,
          current: currentCount,
          events: Object.keys(events).length > 0 ? events : gln.events
        };
        
        if (isFeedlotNode && feedlotPerformance) {
          updatedGln.fcr = feedlotPerformance.fcr;
          updatedGln.fcr_estimated = feedlotPerformance.fcrEstimated;
          updatedGln.fcr_is_estimated = feedlotPerformance.fcrIsEstimated;
          updatedGln.avg_adg = feedlotPerformance.avg_adg;
          updatedGln.avg_feedlot_days = feedlotPerformance.avgFeedlotDays;
          updatedGln.feedlot_days_count = feedlotPerformance.feedlotDaysCount;
        }

        // Inject abattoir slaughter data — DealSlaughterSummary first, fall back to DealEvents
        if (gln.type === 'Abattoir') {
          const summaries = (data.slaughterSummaries as any[]) || [];
          const dealEvts: any[] = (data.dealEvents as any[]) || [];

          // Prefer summary counts; fall back to ABAT_SLA deal event counts
          const summarySlaughtered = summaries.reduce((sum: number, s: any) => sum + (Number(s.animals_slaughtered) || 0), 0);
          const evtSlaughtered = dealEvts
            .filter((ev: any) => ev.event_type === 'ABAT_SLA')
            .reduce((sum: number, ev: any) => sum + (Number(ev.animal_count) || 0), 0);
          const evtReceived = dealEvts
            .filter((ev: any) => ev.event_type === 'ABAT_REC')
            .reduce((sum: number, ev: any) => sum + (Number(ev.animals_with_eid_count) || Number(ev.animal_count) || 0), 0);

          const totalSlaughtered = summarySlaughtered || evtSlaughtered;
          const totalReceived = evtReceived || totalSlaughtered;

          if (totalSlaughtered > 0 || totalReceived > 0 || abatMorCount > 0) {
            const abattoirEvents: any = { ...(updatedGln.events || {}) };
            if (totalReceived > 0) abattoirEvents.received = totalReceived;
            if (totalSlaughtered > 0) abattoirEvents.slaughtered = totalSlaughtered;
            if (abatMorCount > 0 && !abattoirEvents.deaths) abattoirEvents.deaths = abatMorCount;
            // Same reasoning as the feedlot node above: deal events (received/slaughtered)
            // are fetched live and authoritative, so prefer event math over the backend's
            // possibly-stale current aggregate whenever we have a receive count to work from.
            const abattoirCurrent = (totalReceived > 0 && abattoirNodeCount === 1)
              ? Math.max(0, totalReceived - totalSlaughtered)
              : currentCount;
            updatedGln = { ...updatedGln, events: abattoirEvents, current: abattoirCurrent };
          }

          // HCM / CCM weight metrics from summaries only (deal events don't carry weights)
          const totalHcm = summaries.reduce((sum: number, s: any) => sum + (parseFloat(s.total_hcm) || 0), 0);
          const totalCcm = summaries.reduce((sum: number, s: any) => sum + (parseFloat(s.total_ccm) || 0), 0);
          const slaughterWeights: any = { ...(updatedGln.weights || {}) };
          if (totalHcm > 0 && totalSlaughtered > 0) {
            slaughterWeights.hcmTotalKg = parseFloat(totalHcm.toFixed(1));
            slaughterWeights.hcmAvgKg = parseFloat((totalHcm / totalSlaughtered).toFixed(1));
          }
          if (totalCcm > 0 && totalSlaughtered > 0) {
            slaughterWeights.ccmTotalKg = parseFloat(totalCcm.toFixed(1));
            slaughterWeights.ccmAvgKg = parseFloat((totalCcm / totalSlaughtered).toFixed(1));
          }
          if (Object.keys(slaughterWeights).length > 0) {
            updatedGln = { ...updatedGln, weights: slaughterWeights };
          }
        }

        return updatedGln;
      });

      // Fallback: if deal detail didn't supply total_mortalities, derive it from mortality
      // deal events (more reliable than animal.status when EIDs aren't all linked).
      if (data.total_mortalities == null) {
        const morFromEvts = farmMorCount + feedMorCount + abatMorCount;
        if (morFromEvts > 0) data.total_mortalities = morFromEvts;
      }

      // Deduplicate Abattoir GLNs: deal events are deal-level (not entity-specific) so
      // every abattoir in the value chain gets the same data injected. Keep only the best
      // one — prefer the one with a real GLN code, otherwise the first with events.
      const abattoirGlns = glns.filter((g: any) => g.type === 'Abattoir');
      if (abattoirGlns.length > 1) {
        const best =
          abattoirGlns.find((g: any) => g.gln) ||
          abattoirGlns.find((g: any) => g.events && Object.keys(g.events).length > 0) ||
          abattoirGlns[0];
        glns = glns.filter((g: any) => g.type !== 'Abattoir' || g === best);
      }

      // Deduplicate by name across types: if a Feedlot-typed entry and a Farm-typed entry
      // share the same entity name (stale DB record not yet retyped), drop the Farm one.
      const feedlotNames = new Set(
        glns.filter((g: any) => g.type === 'Feedlot').map((g: any) => g.name)
      );
      if (feedlotNames.size > 0) {
        glns = glns.filter((g: any) => !(g.type === 'Farm' && feedlotNames.has(g.name)));
      }
    }
    
    // Prefer backend deal/tracking headcount. This is the EID-linked animal count and
    // must stay separate from raw dataset/session row counts.
    // Event sums are only a fallback for older payloads without headcount.
    const dealEvtsForHeadcount: any[] = Array.isArray(data.dealEvents) ? data.dealEvents : [];
    const headcountFromFarmDis = dealEvtsForHeadcount
      .filter((ev: any) => ev.event_type === 'FARM_DIS')
      .reduce((sum: number, ev: any) => sum + (Number(ev.animal_count) || 0), 0);
    const headcountFromFeedRec = dealEvtsForHeadcount
      .filter((ev: any) => ev.event_type === 'FEED_REC')
      .reduce((sum: number, ev: any) => sum + (Number(ev.animal_count) || 0), 0);
    const headcountFromFarmReg = dealEvtsForHeadcount
      .filter((ev: any) => ev.event_type === 'FARM_REG' || ev.event_type === 'FARM_BIR')
      .reduce((sum: number, ev: any) => sum + (Number(ev.animal_count) || 0), 0);
    // Total farm animals = max(registered+born, dispatched) — births that stay on farm
    // are only in farmReg but not farmDis, so we take the greater of the two.
    const headcountFromFarmEvents = Math.max(headcountFromFarmReg, headcountFromFarmDis);
    const headcount = Number(data.headcount ?? data.animal_summary?.total ?? data.total_headcount ?? 0)
      || headcountFromFarmEvents
      || headcountFromFeedRec
      || 0;

    const tracking: TrackingData = {
      dealId: dealId,  // Keep frontend dealId (UUID) for consistency
      deal_no: data.deal_no,
      deal_reference_no: data.deal_reference_no,
      primaryProducerId: dealData?.primary_producer?.id ? String(dealData.primary_producer.id) : undefined,
      headcount,
      total_mortalities: data.total_mortalities,
      feedlotPerformance: feedlotPerformance || undefined,
      glns,
      documents: data.documents,
      transportDocuments,
      woolDocuments,
      woolSessions,
      woolBales,
      vaccinations: data.vaccinations,
      dealEvents: data.dealEvents,
      rmisOutcomes: data.rmisOutcomes,
    };
    
    return tracking;
  } catch (e) {
    console.error('[fetchTracking] Exception:', e);
    throw e;
  }
}

// Documents that belong to an entity (CIPC or ID copy, VAT certificate, bank
// letter, GLN certificate). Filed once here; a deal's checklist counts them
// when the entity is its seller or purchaser.
export interface EntityDocumentSummary {
  id: string;
  entityId: string;
  documentTypeId: string;
  documentTypeName: string;
  documentTypeCode: string;
  fileUrl: string;
  dateOfDocument: string;
  referenceNumber: string;
  notes: string;
}

function mapEntityDocument(row: any): EntityDocumentSummary {
  return {
    id: String(row?.id ?? ''),
    entityId: row?.entity != null ? String(row.entity) : '',
    documentTypeId: row?.document_type != null ? String(row.document_type) : '',
    documentTypeName: String(row?.document_type_name ?? ''),
    documentTypeCode: String(row?.document_type_code ?? ''),
    fileUrl: String(row?.file ?? ''),
    dateOfDocument: String(row?.date_of_document ?? ''),
    referenceNumber: String(row?.reference_number ?? ''),
    notes: String(row?.notes ?? ''),
  };
}

export async function fetchEntityDocuments(entityId: string): Promise<EntityDocumentSummary[]> {
  const res = await authorizedFetch(`/api/entity/entity-documents/?entity=${encodeURIComponent(entityId)}`);
  const data: any = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
  return rows.map(mapEntityDocument);
}

export async function uploadEntityDocument(input: {
  entityId: string;
  documentType: string;
  file: File;
  dateOfDocument: string;
  referenceNumber?: string;
  notes?: string;
}): Promise<EntityDocumentSummary> {
  const formData = new FormData();
  formData.append('entity', input.entityId);
  formData.append('document_type', input.documentType);
  formData.append('file', input.file);
  formData.append('date_of_document', input.dateOfDocument);
  if (input.referenceNumber) formData.append('reference_number', input.referenceNumber);
  if (input.notes) formData.append('notes', input.notes);
  const res = await authorizedFetch('/api/entity/entity-documents/', { method: 'POST', body: formData });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Document upload failed (${res.status})`);
  }
  return mapEntityDocument(await res.json());
}

export async function deleteEntityDocument(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/entity/entity-documents/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete document failed (${res.status})`);
  }
}

export async function uploadDealDocument(input: DealDocumentUploadInput): Promise<any> {
  const formData = new FormData();
  formData.append('deal', input.dealId);
  if (input.documentType) formData.append('document_type', input.documentType);
  formData.append('file', input.file);
  formData.append('date_of_document', input.dateOfDocument);
  if (input.referenceNumber) formData.append('reference_number', input.referenceNumber);
  if (input.saleValue !== undefined && input.saleValue !== '') formData.append('sale_value', input.saleValue);
  if (input.notes) formData.append('notes', input.notes);
  if (input.visibility) formData.append('visibility', input.visibility);
  if (input.sharedWithProducer !== undefined) formData.append('shared_with_producer', input.sharedWithProducer ? 'true' : 'false');
  if (input.party) formData.append('party', input.party);
  // Which health-declaration answer this file supports. Blank on every other
  // upload — evidence is the only thing filed against a single question.
  if (input.declarationField) formData.append('declaration_field', input.declarationField);

  const res = await authorizedFetch('/api/deals/documents/', {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const text = await res.text();
    if (res.status === 404 || res.status === 405) {
      throw new Error('Document upload is not available yet.');
    }
    throw new Error(text || `Document upload failed (${res.status})`);
  }

  return res.json();
}

// Deal finance records are TYPED: the operator captures a positive total and
// what it is (purchase, sale, transport, …); the backend derives the sign
// (cost types stored negative) so older sign-based reports keep working.
export interface CreateDealFinanceInput {
  dealId: string;
  recordType: 'PURCHASE' | 'SALE' | 'WOOL_SALE' | 'TRANSPORT' | 'FEED' | 'VETERINARY' | 'SHEARING' | 'PENALTY' | 'OTHER_COST' | 'OTHER_REVENUE';
  amount: string;
  transactionDate: string;
  description: string;
  referenceNumber?: string;
}

export interface DealFinanceRow {
  id: string;
  recordType: string;
  recordTypeLabel: string;
  amount: number;
  transactionDate: string;
  description: string;
  referenceNumber: string;
  /** Set when the row mirrors captured evidence (a sale document, a wool sale). */
  linkedToDocument: boolean;
  /** The file behind a mirrored row, when the evidence has one (be ≥ 2.19.9). */
  documentUrl?: string;
  documentName?: string;
}

/** The deal's money ledger -- every DealFinance row, newest first (server order). */
export async function fetchDealFinances(dealId: string): Promise<DealFinanceRow[]> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/finances/`);
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : (data?.results || []);
  return rows.map((row: any) => ({
    id: String(row.id),
    recordType: String(row.record_type || ''),
    recordTypeLabel: String(row.record_type_display || row.record_type || 'Untyped'),
    amount: Number(row.amount ?? 0),
    transactionDate: String(row.transaction_date || ''),
    description: String(row.description || ''),
    referenceNumber: String(row.reference_number || ''),
    linkedToDocument: Boolean(row.source_document || row.source_wool_finance || row.source_medication || row.source_shared_allocation || row.source_journey),
    documentUrl: row.document_url ? String(row.document_url) : undefined,
    documentName: row.document_name ? String(row.document_name) : undefined,
  }));
}

export const DEAL_FINANCE_TYPES: { value: CreateDealFinanceInput['recordType']; label: string; isCost: boolean }[] = [
  { value: 'PURCHASE', label: 'Animal purchase', isCost: true },
  { value: 'SALE', label: 'Animal / carcass sale', isCost: false },
  { value: 'WOOL_SALE', label: 'Wool sale', isCost: false },
  { value: 'TRANSPORT', label: 'Transport', isCost: true },
  { value: 'FEED', label: 'Feed', isCost: true },
  { value: 'VETERINARY', label: 'Veterinary & health', isCost: true },
  { value: 'SHEARING', label: 'Shearing', isCost: true },
  { value: 'PENALTY', label: 'Penalty', isCost: true },
  { value: 'OTHER_COST', label: 'Other cost', isCost: true },
  { value: 'OTHER_REVENUE', label: 'Other revenue', isCost: false },
];

/** Remove a hand-typed ledger row. Mirrored rows (document, journey, feed record, shared cost) are removed by deleting their source. */
export async function deleteDealFinance(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/deals/finances/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) {
    const text = await res.text();
    throw new Error(text || `Delete failed (${res.status})`);
  }
}

/** Change the amount (and optionally the date) of an existing ledger row. */
export async function updateDealFinance(id: string, input: { amount: string; transactionDate?: string }): Promise<any> {
  const payload: Record<string, unknown> = { amount: input.amount };
  if (input.transactionDate) payload.transaction_date = input.transactionDate;
  const res = await authorizedFetch(`/api/deals/finances/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export async function createDealFinance(input: CreateDealFinanceInput): Promise<any> {
  const res = await authorizedFetch('/api/deals/finances/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deal: input.dealId,
      record_type: input.recordType,
      amount: input.amount,
      transaction_date: input.transactionDate,
      description: input.description,
      reference_number: input.referenceNumber || null,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Create deal finance failed (${res.status})`);
  }
  return res.json();
}

/** Apportion the deal's PURCHASE finance total onto its animals'
 *  purchase_price (per-kg × weight where known, the remainder spread evenly).
 *  Recallable — animals often arrive after the purchase is recorded. */
export async function applyDealPurchasePrices(dealId: string): Promise<{
  updated: number; priced_per_kg: number; priced_average: number;
  animals: number; purchase_total: string; detail: string;
}> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/apply-purchase-prices/`, {
    method: 'POST',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Apply purchase prices failed (${res.status})`);
  }
  return res.json();
}

// The seller's medication declaration rows — one per product so the
// withdrawal arithmetic stays answerable per medicine. Distinct from the
// treatment records of what AVCS administered.
export interface CreateDealMedicationInput {
  dealId: string;
  medicineId?: string;
  medicationName?: string;
  isAntibiotic?: boolean;
  lastTreatmentDate?: string;
  withdrawalPeriodDays?: number | null;
  notes?: string;
}

export async function createDealMedication(input: CreateDealMedicationInput): Promise<any> {
  const res = await authorizedFetch('/api/deals/medications/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deal: input.dealId,
      medicine: input.medicineId || null,
      medication_name: input.medicationName || '',
      is_antibiotic: Boolean(input.isAntibiotic),
      last_treatment_date: input.lastTreatmentDate || null,
      withdrawal_period_days: input.withdrawalPeriodDays ?? null,
      notes: input.notes || '',
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Create deal medication failed (${res.status})`);
  }
  return res.json();
}

export async function deleteDealMedication(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/deals/medications/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete deal medication failed (${res.status})`);
  }
}

// ---- Feeding declarations -------------------------------------------------
// One row per feed the seller declares (a declaration routinely lists several
// — pellets, planted pasture, a finishing ration). The rows arrive nested on
// the deal detail as `feeding_declarations`; these calls add and remove them.

export interface CreateDealFeedingDeclarationInput {
  dealId: string;
  feedName: string;
  description?: string;
  nutritionalValue?: string;
  introducedDate?: string | null;
}

export async function createDealFeedingDeclaration(
  input: CreateDealFeedingDeclarationInput,
): Promise<any> {
  const res = await authorizedFetch('/api/deals/feeding-declarations/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deal: input.dealId,
      feed_name: input.feedName,
      description: input.description || '',
      nutritional_value: input.nutritionalValue || '',
      introduced_date: input.introducedDate || null,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Create feeding declaration failed (${res.status})`);
  }
  return res.json();
}

/** Every feeding declaration row the user can see, across deals — the pool the feed picker suggests from. */
export async function fetchDealFeedingDeclarations(): Promise<any[]> {
  const res = await authorizedFetch('/api/deals/feeding-declarations/');
  const data = await handleJson(res);
  return Array.isArray(data) ? data : (data?.results || []);
}

export async function deleteDealFeedingDeclaration(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/deals/feeding-declarations/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete feeding declaration failed (${res.status})`);
  }
}

/** Administer one medicine to every animal on the deal — the manual
 *  counterpart of a per-row vaccination upload (per-animal treatment records
 *  plus one administration event scoped to the treated animals). */
export async function createGroupTreatment(dealId: string, input: {
  medicineId: string;
  dateGiven: string;
  dosage?: string;
  batchNumber?: string;
  costPerAnimal?: string;
  notes?: string;
}): Promise<{ animals_treated: number; detail: string }> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/group-treatment/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      medicine: input.medicineId,
      date_given: input.dateGiven,
      dosage: input.dosage || '',
      batch_number: input.batchNumber || '',
      cost_per_animal: input.costPerAnimal || '',
      notes: input.notes || '',
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Group treatment failed (${res.status})`);
  }
  return res.json();
}

/** One medication captured as declaration, administration and cost at once.
 *
 *  Replaces the pair of calls the two old forms made (`createDealMedication`
 *  for the declaration, `createGroupTreatment` for the administration). Both
 *  still exist for the older screens; deal capture asks once and this does all
 *  three, including booking the VETERINARY finance row.
 *
 *  `totalCost` is the deal-wide total as it reads off the invoice — the
 *  backend divides it by the registered headcount. An empty deal does not
 *  fail: the declaration and the money are recorded and `treatmentPending`
 *  comes back true so the screen can offer a replay.
 */
export interface CaptureDealMedicationInput {
  medicineId: string;
  lastTreatmentDate?: string;
  withdrawalPeriodDays?: string;
  dosage?: string;
  totalCost?: string;
  isAntibiotic?: boolean;
  notes?: string;
  /** Where the animals were treated (an entity); blank means wherever they are. */
  entityId?: string;
}

export interface CaptureDealMedicationResult {
  medicationId: string;
  animalsTreated: number;
  treatmentPending: boolean;
  costPerAnimal: string | null;
  financeId: string | null;
  detail: string;
}

function mapMedicationCapture(r: any): CaptureDealMedicationResult {
  return {
    medicationId: String(r.medication_id || ''),
    animalsTreated: Number(r.animals_treated ?? 0),
    treatmentPending: Boolean(r.treatment_pending),
    costPerAnimal: r.cost_per_animal ?? null,
    financeId: r.finance_id ?? null,
    detail: String(r.detail || ''),
  };
}

export async function captureDealMedication(
  dealId: string, input: CaptureDealMedicationInput,
): Promise<CaptureDealMedicationResult> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/medication-capture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      medicine: input.medicineId,
      last_treatment_date: input.lastTreatmentDate || '',
      withdrawal_period_days: input.withdrawalPeriodDays || '',
      dosage: input.dosage || '',
      total_cost: input.totalCost || '',
      is_antibiotic: Boolean(input.isAntibiotic),
      notes: input.notes || '',
      entity: input.entityId || '',
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Medication capture failed (${res.status})`);
  }
  return mapMedicationCapture(await res.json());
}

/** Create the per-animal records a medication captured on an empty deal owes. */
export async function replayDealMedication(
  dealId: string, medicationId: string,
): Promise<{ animalsTreated: number; detail: string }> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/replay-medication/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ medication: medicationId }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Replay failed (${res.status})`);
  }
  const body = await res.json();
  return { animalsTreated: Number(body.animals_treated ?? 0), detail: String(body.detail || '') };
}

export async function createDeal(input: DealUpsertInput): Promise<any> {
  // deal_no is auto-generated by the backend — never send it on create.
  const payload: Record<string, any> = {
    primary_producer: input.primaryProducerId,
    is_completed: Boolean(input.isCompleted),
  };
  // A Deal Matrix deal can be captured without a chain — omit the field
  // rather than sending an empty string the backend would reject.
  if (input.valueChainId) payload.value_chain = input.valueChainId;
  if (input.isScpDeal !== undefined) payload.is_scp_deal = Boolean(input.isScpDeal);
  if (input.dealReferenceNo) payload.deal_reference_no = input.dealReferenceNo;
  if (input.dealDate) payload.deal_date = input.dealDate;
  if (input.totalAnimals !== undefined) payload.total_animals = Number(input.totalAnimals);
  if (input.totalMortalities !== undefined) payload.total_mortalities = Number(input.totalMortalities);

  const res = await authorizedFetch('/api/deals/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (res.ok) return res.json();

  const errText = await res.text();
  console.error('[createDeal] POST /api/deals/ failed', res.status, errText);
  throw new Error(errText || `Create deal failed (${res.status})`);
}

export async function updateDeal(id: string, input: DealUpsertInput): Promise<any> {
  const payload: Record<string, any> = {};
  if (input.dealReferenceNo !== undefined) payload.deal_reference_no = input.dealReferenceNo || null;
  if (input.dealDate !== undefined) payload.deal_date = input.dealDate || null;
  if (input.totalAnimals !== undefined) payload.total_animals = Number(input.totalAnimals || 0);
  if (input.totalMortalities !== undefined) payload.total_mortalities = Number(input.totalMortalities || 0);
  if (input.isCompleted !== undefined) payload.is_completed = Boolean(input.isCompleted);
  if (input.isCancelled !== undefined) payload.is_cancelled = Boolean(input.isCancelled);
  if (input.primaryProducerId) payload.primary_producer = input.primaryProducerId;
  if (input.valueChainId) payload.value_chain = input.valueChainId;
  if (input.isScpDeal !== undefined) payload.is_scp_deal = Boolean(input.isScpDeal);
  if (input.purchaserId !== undefined) payload.purchaser = input.purchaserId || null;
  if (input.totalMale !== undefined) payload.total_male = input.totalMale;
  if (input.totalFemale !== undefined) payload.total_female = input.totalFemale;
  if (input.identificationType !== undefined) payload.identification_type = input.identificationType || '';
  if (input.totalWeightKg !== undefined) payload.total_weight_kg = input.totalWeightKg || null;
  if (input.priceMale !== undefined) payload.price_male = input.priceMale || null;
  if (input.priceFemale !== undefined) payload.price_female = input.priceFemale || null;
  if (input.pricePerKgMale !== undefined) payload.price_per_kg_male = input.pricePerKgMale || null;
  if (input.pricePerKgFemale !== undefined) payload.price_per_kg_female = input.pricePerKgFemale || null;
  if (input.pricePerKgUnknown !== undefined) payload.price_per_kg_unknown = input.pricePerKgUnknown || null;
  if (input.totalPriceExVat !== undefined) payload.total_price_ex_vat = input.totalPriceExVat || null;
  if (input.totalPriceInclVat !== undefined) payload.total_price_incl_vat = input.totalPriceInclVat || null;
  if (input.plannedSpecies !== undefined) payload.planned_species = input.plannedSpecies || '';
  if (input.plannedBreedId !== undefined) payload.planned_breed = input.plannedBreedId || null;
  if (input.penaltyTerms !== undefined) payload.penalty_terms = input.penaltyTerms || '';
  if (input.origin !== undefined) payload.origin = input.origin || '';
  if (input.purchaseChannel !== undefined) payload.purchase_channel = input.purchaseChannel || '';
  if (input.traceabilityDocumented !== undefined) payload.traceability_documented = Boolean(input.traceabilityDocumented);
  if (input.examinedBeforeAndOnLoading !== undefined) payload.examined_before_and_on_loading = input.examinedBeforeAndOnLoading;
  if (input.freeOfListedDiseases !== undefined) payload.free_of_listed_diseases = input.freeOfListedDiseases;
  if (input.diseasesPresent !== undefined) payload.diseases_present = input.diseasesPresent;
  if (input.diseasesOther !== undefined) payload.diseases_other = input.diseasesOther || '';
  if (input.clinicalReportAvailable !== undefined) payload.clinical_report_available = input.clinicalReportAvailable;
  if (input.withdrawalPeriodsAdhered !== undefined) payload.withdrawal_periods_adhered = input.withdrawalPeriodsAdhered;
  if (input.purchaserDocumentsRequired !== undefined) payload.purchaser_documents_required = input.purchaserDocumentsRequired;
  if (input.purchaserDocumentsCompleted !== undefined) payload.purchaser_documents_completed = input.purchaserDocumentsCompleted;
  if (input.feedingSystem !== undefined) payload.feeding_system = input.feedingSystem || '';
  if (input.feedingSystemOther !== undefined) payload.feeding_system_other = input.feedingSystemOther || '';
  if (input.animalByProductsFed !== undefined) payload.animal_by_products_fed = input.animalByProductsFed;
  if (input.animalByProducts !== undefined) payload.animal_by_products = input.animalByProducts;
  if (input.animalByProductsOther !== undefined) payload.animal_by_products_other = input.animalByProductsOther || '';
  if (input.treatedWithMedications !== undefined) payload.treated_with_medications = input.treatedWithMedications;
  if (input.treatedWithAntibiotics !== undefined) payload.treated_with_antibiotics = input.treatedWithAntibiotics;
  if (input.sellerAgentName !== undefined) payload.seller_agent_name = input.sellerAgentName || '';
  if (input.sellerAgentContact !== undefined) payload.seller_agent_contact = input.sellerAgentContact || '';
  if (input.purchaserAgentName !== undefined) payload.purchaser_agent_name = input.purchaserAgentName || '';
  if (input.purchaserAgentContact !== undefined) payload.purchaser_agent_contact = input.purchaserAgentContact || '';

  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Update deal failed (${res.status})`);
  }

  return res.json();
}

export async function deleteDealById(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete deal failed (${res.status})`);
  }
}

export async function fetchEntityGtins(entityId: string): Promise<any[]> {
  const res = await authorizedFetch(`/api/core/gs1-codes/?entity=${encodeURIComponent(entityId)}&gs1_type=GTIN`);
  const data = await handleJson(res);
  return Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
}

export async function fetchAnimals(dealId?: string): Promise<any[]> {
  // Always fetch fresh — no stale-value caching. Accuracy over performance.
  const suffix = dealId ? `?deal=${encodeURIComponent(dealId)}` : '';
  const res = await authorizedFetch(`/api/animals/animals/${suffix}`);
  const data = await handleJson(res);
  return Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
}

// Raw abattoir-slaughter (ABAT_SLA) upload rows for a deal's animals, one per
// animal (latest), from GET /api/animals/animals/slaughter-raw/. `raw_data` is
// the full original CSV row; `columns` is the ordered union of its keys across
// all rows — used to render every raw field as a table column.
export interface DealSlaughterRawRow {
  animal_id: string;
  eid?: string | null;
  vid?: string | null;
  species?: string | null;
  status?: string | null;
  deal_id?: string | null;
  deal_no?: string | null;
  source_filename?: string | null;
  row_number?: number | null;
  raw_data: Record<string, unknown>;
}

export interface DealSlaughterRawResponse {
  count: number;
  columns: string[];
  results: DealSlaughterRawRow[];
}

export async function fetchDealSlaughterRaw(dealId?: string): Promise<DealSlaughterRawResponse> {
  const suffix = dealId ? `?deal=${encodeURIComponent(dealId)}` : '';
  const res = await authorizedFetch(`/api/animals/animals/slaughter-raw/${suffix}`);
  const data = (await handleJson(res)) as any;
  return {
    count: Number(data?.count ?? data?.results?.length ?? 0),
    columns: Array.isArray(data?.columns) ? data.columns : [],
    results: Array.isArray(data?.results) ? data.results : [],
  };
}

export interface AnimalEidMatch {
  eid: string;
  deal_id: string;
}

export async function checkAnimalEids(eids: string[]): Promise<AnimalEidMatch[]> {
  const res = await authorizedFetch('/api/animals/animals/check-eids/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eids }),
  });
  const data = await handleJson(res);
  return Array.isArray((data as any)?.matches) ? (data as any).matches : [];
}

export interface AnimalDealSummary {
  total: number;
  eid_count: number;
  non_eid_count: number;
  species: Record<string, number>;
  breeds?: Record<string, number>;
}

export interface AnimalSummary {
  by_deal: Record<string, AnimalDealSummary>;
  total: number;
  eid_count: number;
  non_eid_count: number;
  species: Record<string, number>;
}

export async function fetchAnimalSummary(dealIds?: string[]): Promise<AnimalSummary> {
  const suffix = dealIds && dealIds.length ? `?deal_ids=${encodeURIComponent(dealIds.join(','))}` : '';
  const res = await authorizedFetch(`/api/animals/animals/summary/${suffix}`);
  return handleJson(res);
}

// --- Abattoir batch-slaughter reconciliation --------------------------------
// Animals reach an abattoir and are slaughtered, but the kill sheet sometimes
// omits the EID so the normal ABAT_SLA upload can't run. These endpoints list
// animals currently at an abattoir (grouped by deal) and mark a deal-batch
// slaughtered with a batch number + average CCM (for a guesstimate dressing %).

export interface AwaitingSlaughterAnimal {
  id: string;
  eid: string | null;
  breed_name: string | null;
  sex: string | null;
  last_live_weight: number | null;
  abattoir_entity_id: string | null;
  abattoir_entity_name: string | null;
}

// One abattoir receive session (one truckload received at an abattoir).
// `session_id` is null for animals sitting at an abattoir with no distinct
// ABAT_REC session (e.g. registered on arrival), in which case the group falls
// back to deal + abattoir. A consolidated receive file can span several deals,
// so `deals` breaks the card down; `deal_id`/`deal_no`/`primary_producer_name`
// stay as the first (representative) deal for backward compatibility.
export interface AwaitingSlaughterSessionDeal {
  deal_id: string | null;
  deal_no: string | null;
  primary_producer_name: string | null;
  animal_count: number;
}

export interface AwaitingSlaughterSession {
  session_id: string | null;
  session_label: string | null;
  receive_date: string | null;
  abattoir_entity_id: string | null;
  abattoir_entity_name: string | null;
  gln: string | null;
  deal_id: string | null;
  deal_no: string | null;
  primary_producer_name: string | null;
  species: string | null;
  animal_count: number;
  deals?: AwaitingSlaughterSessionDeal[];
  animals: AwaitingSlaughterAnimal[];
}

export interface AwaitingSlaughterResponse {
  as_of_date: string;
  total_animals: number;
  sessions: AwaitingSlaughterSession[];
}

export async function fetchAnimalsAwaitingSlaughter(params?: {
  dealId?: string;
  species?: string;
  asOfDate?: string;
}): Promise<AwaitingSlaughterResponse> {
  const query = new URLSearchParams();
  if (params?.dealId) query.set('deal_id', params.dealId);
  if (params?.species) query.set('species', params.species);
  if (params?.asOfDate) query.set('as_of_date', params.asOfDate);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await authorizedFetch(`/api/abattoir/awaiting-slaughter/${suffix}`);
  return handleJson(res);
}

// --- Abattoir slaughter, rebuilt around receive events (receipts) ---------
// The slaughter screen lists abattoir ABAT_REC *receipts* (one card per
// physical receipt, per-deal receives of one dispatch collapsed together) and
// the animals each brought in, split into awaiting vs already slaughtered. The
// operator creates a slaughter from a receipt via markBatchSlaughtered() with
// that receipt's awaiting animal ids.

export interface AbattoirReceiptAnimal {
  id: string;
  eid: string | null;
  breed_name: string | null;
  sex: string | null;
  last_live_weight: number | null;
  status: string;
  awaiting: boolean;
  abattoir_entity_id: string | null;
  abattoir_entity_name: string | null;
}

export interface AbattoirReceiptDeal {
  deal_id: string | null;
  deal_no: string | null;
  primary_producer_name: string | null;
  awaiting_count: number;
}

export interface AbattoirReceipt {
  receipt_key: string;
  receive_event_ids: string[];
  session_id: string | null;
  session_label: string | null;
  receive_date: string | null;
  abattoir_entity_id: string | null;
  abattoir_entity_name: string | null;
  gln: string | null;
  source_entity_name: string | null;
  species: string | null;
  deal_id: string | null;
  deal_no: string | null;
  primary_producer_name: string | null;
  deals: AbattoirReceiptDeal[];
  awaiting_count: number;
  slaughtered_count: number;
  total_count: number;
  animals: AbattoirReceiptAnimal[];
}

export interface AbattoirReceiptsResponse {
  as_of_date: string;
  total_awaiting: number;
  receipts: AbattoirReceipt[];
}

export async function fetchAbattoirReceipts(params?: {
  dealId?: string;
  species?: string;
  asOfDate?: string;
}): Promise<AbattoirReceiptsResponse> {
  const query = new URLSearchParams();
  if (params?.dealId) query.set('deal_id', params.dealId);
  if (params?.species) query.set('species', params.species);
  if (params?.asOfDate) query.set('as_of_date', params.asOfDate);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await authorizedFetch(`/api/abattoir/receipts/${suffix}`);
  return handleJson(res);
}

export interface MarkBatchSlaughteredPayload {
  deal_id: string;
  abattoir_entity_id: string;
  slaughter_batch: string;
  average_ccm: number | string;
  slaughter_date?: string;
  animal_ids?: string[];
  /** The abattoir's batch dressing % (its "Slaughter %"); derives every live weight so dressing reads what the abattoir measured. */
  batch_dressing_percent?: number | string;
  batch_live_mass_kg?: number | string;
}

export interface BatchLiveMassResult {
  slaughter_batch: string;
  dry_run: boolean;
  dressing_percent: number;
  records_updated: number;
  records_without_ccm: number;
  ccm_total_kg: number;
  derived_live_mass_kg: number | null;
  dressing_before: number | null;
  deals: string[];
}

/** Re-derive a slaughter batch's live weights from the abattoir's batch figure. */
export async function applyBatchLiveMass(payload: {
  slaughter_batch: string;
  dressing_percent?: number | string;
  live_mass_kg?: number | string;
  carcass_mass_kg?: number | string;
  dry_run?: boolean;
}): Promise<BatchLiveMassResult> {
  const res = await authorizedFetch('/api/abattoir/batch-live-mass/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export interface MarkBatchSlaughteredResult {
  deal_id: string;
  deal_no: string | null;
  abattoir_entity_id: string;
  slaughter_batch: string;
  average_ccm: number;
  slaughter_date: string;
  animals_marked: number;
  animals_targeted: number;
  average_dressing_percentage: number | null;
  animals_without_live_weight: number;
  animals_without_eid: number;
  data_session_id: string;
  // A receive session can span multiple deals; these summarise a multi-deal batch.
  deals_count?: number;
  deal_nos?: string[];
  animals_without_deal?: number;
  data_session_ids?: string[];
  batch_live_mass?: BatchLiveMassResult | null;
}

export async function markBatchSlaughtered(
  payload: MarkBatchSlaughteredPayload,
): Promise<MarkBatchSlaughteredResult> {
  const res = await authorizedFetch('/api/abattoir/mark-slaughtered/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export interface SystemStats {
  animals: {
    entered: number;
    alive: number;
    slaughtered: number;
    mortalities: number;
    alive_by_species: Record<string, number>;
  };
  deals: {
    total: number;
    active: number;
  };
  glns: {
    total: number;
    active: number;
  };
}

// System-wide, all-time counters for the operations dashboard's lifetime
// stats card. One request instead of piecing counts together client-side —
// the animal register excludes slaughtered/deceased animals, so those figures
// aren't otherwise derivable on the frontend.
export async function fetchSystemStats(params?: {
  species?: string;
  valueChainId?: string;
  /** Scope to Deal Matrix deals (those not run under a Supply Chain Program). */
  scpMatrix?: boolean;
  dealId?: string;
}): Promise<SystemStats> {
  const qs = new URLSearchParams();
  if (params?.species) qs.set('species', params.species);
  if (params?.valueChainId) qs.set('value_chain', params.valueChainId);
  if (params?.scpMatrix) qs.set('scp', 'matrix');
  if (params?.dealId) qs.set('deal', params.dealId);
  const query = qs.toString();
  const res = await authorizedFetch(`/api/animals/stats/${query ? `?${query}` : ''}`);
  return handleJson(res);
}

export interface MortalityTimeseriesPoint {
  month: string; // "YYYY-MM"
  count: number;
}

export interface MortalityTimeseries {
  series: MortalityTimeseriesPoint[];
  total: number;
}

// All-time, month-on-month mortality counts (gaps filled with zero) for the
// performance report's mortality-trend drilldown.
export async function fetchMortalityTimeseries(): Promise<MortalityTimeseries> {
  const res = await authorizedFetch('/api/animals/mortality-timeseries/');
  const data = await handleJson(res);
  return {
    series: Array.isArray((data as any)?.series) ? (data as any).series : [],
    total: Number((data as any)?.total ?? 0),
  };
}

export async function fetchUnprocessedRecords(dealId: string): Promise<any[]> {
  const res = await authorizedFetch(`/api/ingest/unprocessed-rows/?deal_id=${encodeURIComponent(dealId)}`);
  const data = await handleJson(res);
  return Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.results)
      ? (data as any).results
      : [];
}

export interface UnlinkedAnimalsResponse {
  count: number;
  results: any[];
}

// Admin diagnostic: animals not attached to any deal (bare stubs created by a
// non-registration upload for an unregistered animal). Staff-only endpoint.
export async function fetchUnlinkedAnimals(
  opts: { hasEid?: boolean; search?: string } = {}
): Promise<UnlinkedAnimalsResponse> {
  const params = new URLSearchParams();
  if (opts.hasEid) params.set('has_eid', 'true');
  if (opts.search && opts.search.trim()) params.set('search', opts.search.trim());
  const qs = params.toString();
  const res = await authorizedFetch(`/api/animals/animals/unlinked/${qs ? `?${qs}` : ''}`);
  const data = await handleJson(res);
  const results = Array.isArray((data as any)?.results)
    ? (data as any).results
    : Array.isArray(data)
      ? (data as any)
      : [];
  const count = typeof (data as any)?.count === 'number' ? (data as any).count : results.length;
  return { count, results };
}

export async function fetchDealEvents(dealId: string): Promise<any[]> {
  const key = dealId;
  const now = Date.now();
  if (now < (dealEventsCooldownUntil.get(key) || 0)) return dealEventsLastValue.get(key) || [];
  const inFlight = dealEventsInFlight.get(key);
  if (inFlight) return inFlight;

  const request = (async () => {
    try {
      const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/events/`);
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        dealEventsCooldownUntil.set(key, Date.now() + waitMs);
        return dealEventsLastValue.get(key) || [];
      }
      const data = await handleJson(res);
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
      dealEventsLastValue.set(key, rows);
      return rows;
    } catch {
      return dealEventsLastValue.get(key) || [];
    } finally {
      dealEventsInFlight.delete(key);
    }
  })();

  dealEventsInFlight.set(key, request);
  return request;
}

// ---- Deal overview: timeline, financial cost stack, integrations, vaccinations ----

export interface DealTimelineMilestone {
  key: string;
  label: string;
  date: string | null;
}

export interface DealTimeline {
  deal_id: string;
  deal_no: string;
  registration_at: string | null;
  slaughter_at: string | null;
  as_of: string | null;
  end_at: string | null;
  days_in_value_chain: number | null;
  /** Deal-level figure for the dashboard: the deal's own start date through to
   *  the last slaughter, but only once every animal has left the chain —
   *  otherwise it runs to today. Distinct from `days_in_value_chain`, which is
   *  event-based and stops as soon as *any* animal is slaughtered. */
  total_days_in_value_chain: number | null;
  total_days_start: string | null;
  total_days_end: string | null;
  all_animals_out: boolean;
  is_slaughtered: boolean;
  is_completed: boolean;
  /** Average CCM / last live weight, as a percentage. Null when nothing is scoreable. */
  dressing_percentage: number | null;
  dressing_records_total: number;
  dressing_records_scored: number;
  /** Slaughter records excluded from the average because no live weight was captured. */
  dressing_records_without_live_weight: number;
  dressing_records_estimated: number;
  /** Mean cold carcass mass (CCM) in kg, measured records only. */
  avg_carcass_weight: number | null;
  avg_carcass_weight_count: number;
  /** Mean last known live weight in kg, measured records only. */
  avg_last_live_weight: number | null;
  avg_last_live_weight_count: number;
  /** Animals per slaughter grade, highest count first, Ungraded last. */
  grade_counts: { grade: string; count: number }[];
  milestones: DealTimelineMilestone[];
}

export interface DealAdminReport {
  deal_id: string;
  deal_no: string;
  species: string | null;
  performance: Record<string, number | boolean | null>;
  costs: {
    animal_count: number;
    total_cost: number | null;
    per_animal_cost: number | null;
    revenue: number | null;
    profit_loss: number | null;
    per_animal_profit: number | null;
    sources: Record<string, number | null>;
  };
}

export interface DealIntegrationSystem {
  key: string;
  name: string;
  configured: boolean;
  is_active: boolean;
  is_live: boolean;
  current_environment: string | null;
  deal_activity: Record<string, number> | null;
}

export interface DealIntegrations {
  deal_id: string;
  deal_no: string;
  systems: DealIntegrationSystem[];
}

// registration→slaughter/today span + lifecycle milestones for the "days in
// value chain" card (backend deal.views.DealViewSet.timeline).
export async function fetchDealTimeline(dealId: string): Promise<DealTimeline> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/timeline/`);
  return handleJson(res);
}

// (fetchDealAdminReport already exists further down; the DealAdminReport type
// above documents the shape that endpoint returns.)

// per-deal status of the wired integrations (admin-only; backend integrations action).
export async function fetchDealIntegrations(dealId: string): Promise<DealIntegrations> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/integrations/`);
  return handleJson(res);
}

// deal-scoped vaccination/medical records (treatment.DealVaccination).
export async function fetchDealVaccinations(dealId: string): Promise<any[]> {
  const res = await authorizedFetch(`/api/treatment/deal-vaccinations/?deal_id=${encodeURIComponent(dealId)}`);
  const data = await handleJson(res);
  return Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
}

export async function createDataSession(formData: FormData): Promise<any> {
  const res = await authorizedFetch('/api/ingest/upload/', { method: 'POST', body: formData });
  return handleJson(res);
}

export async function createDraftDataSession(formData: FormData): Promise<any> {
  const res = await authorizedFetch('/api/ingest/session/draft/', { method: 'POST', body: formData });
  return handleJson(res);
}

export async function previewDraftDataSession(sessionId: string, previewLimit = 3): Promise<any> {
  const params = new URLSearchParams({ preview_limit: String(previewLimit) });
  const res = await authorizedFetch(`/api/ingest/session/${encodeURIComponent(sessionId)}/preview/?${params.toString()}`);
  return handleJson(res);
}

export interface VaccinationEntryInput {
  medicine_name: string;
  /** Per-medicine tick: remembered on the catalogue product and mirrored to the deal's health declaration. */
  is_antibiotic?: boolean;
  dosage?: string;
  batch_number?: string;
  cost?: string;
  batch_expiry_date?: string;
  withholding_end_date?: string;
  esi_date?: string;
}

/** Cost a confirmed vaccination session from its invoice: per-animal cost spread over the medicines, booked to the ledger, invoice filed. */
export async function submitTreatmentInvoice(
  sessionId: string,
  input: { costPerAnimal: string; referenceNumber?: string; invoiceDate?: string; file?: File | null },
): Promise<{ deal_id: string; medicines: string[]; animals: number; cost_per_animal: string; total: string; document_id: string | null; document_skipped: boolean }> {
  const formData = new FormData();
  formData.append('cost_per_animal', input.costPerAnimal);
  if (input.referenceNumber) formData.append('reference_number', input.referenceNumber);
  if (input.invoiceDate) formData.append('invoice_date', input.invoiceDate);
  if (input.file) formData.append('invoice', input.file);
  const res = await authorizedFetch(`/api/ingest/session/${encodeURIComponent(sessionId)}/treatment-invoice/`, { method: 'POST', body: formData });
  return handleJson(res);
}

export async function confirmDraftDataSession(
  sessionId: string,
  vaccinationEntries?: VaccinationEntryInput[],
  options?: { attachAsRevision?: boolean },
): Promise<any> {
  const hasEntries = Array.isArray(vaccinationEntries) && vaccinationEntries.length > 0;
  const attachAsRevision = Boolean(options?.attachAsRevision);
  // Send a JSON body when we have vaccination entries and/or the operator chose
  // to apply this upload as a delta to an existing event. attach_as_revision
  // only affects a same-event-key (differing content) match; exact re-uploads
  // attach automatically on the backend regardless.
  const body: Record<string, unknown> = {};
  if (hasEntries) body.vaccination_entries = vaccinationEntries;
  if (attachAsRevision) body.attach_as_revision = true;
  const hasBody = Object.keys(body).length > 0;
  const res = await authorizedFetch(`/api/ingest/session/${encodeURIComponent(sessionId)}/confirm/`, {
    method: 'POST',
    ...(hasBody
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      : {}),
  });
  return handleJson(res);
}

// --- Gendac RMIS ATP console (admin-only; the backend gates it on RMIS_ATP_ENABLED) ---

export type RmisAtpConfig = {
  enabled: boolean;
  environment: string;
  base_url: string;
  integration_active: boolean;
};

export type RmisAtpResult = {
  action: string;
  request_id: string;
  environment: string;
  base_url?: string;
  executed_at: string;
  status_code: number | null;
  data: unknown;
  error: string;
};

export interface UatDeal {
  id: string;
  deal_no: string;
  producer: string;
  description: string;
  animals: number;
  documents: number;
  released: number;
}

export interface UatStatus {
  enabled: boolean;
  ready?: boolean;
  farmers?: Array<{ username: string; producer: string; exists: boolean }>;
  deals?: UatDeal[];
}

export interface UatResetResult extends UatStatus {
  created: string[];
  documents_removed: number;
  password: string;
}

/** The UAT menu only exists where the backend allows it (never production). */
export async function fetchUatStatus(): Promise<UatStatus> {
  const res = await authorizedFetch('/api/uat/status/');
  return handleJson(res);
}

export async function resetUatData(): Promise<UatResetResult> {
  const res = await authorizedFetch('/api/uat/reset/', { method: 'POST' });
  return handleJson(res);
}

export async function fetchRmisAtpConfig(): Promise<RmisAtpConfig> {
  const res = await authorizedFetch('/api/integration/rmis/atp/config/');
  return handleJson(res);
}

/** Run one ATP step. The participant token rides in the body, never the URL. */
export async function runRmisAtpStep(input: {
  action: 'send' | 'receive' | 'group' | 'update' | 'query';
  participant_token: string;
  payload?: Record<string, unknown>;
  params?: Record<string, unknown>;
  transfer_id?: string;
}): Promise<RmisAtpResult> {
  const res = await authorizedFetch('/api/integration/rmis/atp/execute/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handleJson(res);
}

export async function fetchFieldMappingsMeta(): Promise<{ event_types: { value: string; label: string }[]; target_fields: { value: string; label: string }[] }> {
  const res = await authorizedFetch('/api/ingest/field-mappings/meta/');
  return handleJson(res);
}

export async function createFieldMapping(payload: { event_type: string; source_field: string; target_field: string }): Promise<any> {
  const res = await authorizedFetch('/api/ingest/field-mappings/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export type FieldMapping = {
  id: string;
  event_type: string;
  source_field: string;
  normalized_source_field: string;
  target_field: string;
  description: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

export type UnmappedField = {
  id: string;
  event_type: string;
  source_field: string;
  normalized_source_field: string;
  sample_value: string;
  occurrence_count: number;
  last_seen_at: string;
};

export type RemapSessionResult = {
  session_id: string;
  session_label: string;
  event_type: string;
  deal_no: string | null;
  created_at: string | null;
  rows_total: number;
  rows_changed: number;
  fields: Record<string, { gained: number; lost: number; changed: number }>;
  sample: { row_number: number; fields: string[]; before: Record<string, any>; after: Record<string, any> } | null;
  rows_written?: number;
  relinked?: { linked_rows: number; unlinked_rows: number; rows_with_eid: number } | null;
};

export type RemapResponse = {
  apply: boolean;
  sessions_matched: number;
  sessions_scanned: number;
  sessions_truncated: boolean;
  session_limit: number | null;
  totals: { rows_total: number; rows_changed: number; rows_written?: number };
  results: RemapSessionResult[];
};

export async function fetchFieldMappings(params: {
  event_type?: string;
  target_field?: string;
  is_active?: string;
  search?: string;
} = {}): Promise<FieldMapping[]> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await authorizedFetch(`/api/ingest/field-mappings/${suffix}`);
  return handleJson(res);
}

export async function updateFieldMapping(
  id: string,
  payload: Partial<Pick<FieldMapping, 'event_type' | 'source_field' | 'target_field' | 'description' | 'is_active'>>,
): Promise<FieldMapping> {
  const res = await authorizedFetch(`/api/ingest/field-mappings/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export async function deleteFieldMapping(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/ingest/field-mappings/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) await handleJson(res);
}

export async function fetchUnmappedFields(params: { event_type?: string; search?: string } = {}): Promise<UnmappedField[]> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await authorizedFetch(`/api/ingest/unmapped-fields/${suffix}`);
  return handleJson(res);
}

// Re-apply current mappings to data already ingested. Without `apply` this is a
// dry run: the backend writes nothing and reports what would change. `apply`
// requires explicit sessionIds — the backend refuses to write across a whole
// event type in one request.
export async function remapIngestedData(payload: {
  event_type?: string;
  session_ids?: string[];
  apply?: boolean;
  limit?: number;
}): Promise<RemapResponse> {
  const res = await authorizedFetch('/api/ingest/field-mappings/remap/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export async function relinkDataSession(sessionId: string): Promise<any> {
  const res = await authorizedFetch(`/api/ingest/session/${encodeURIComponent(sessionId)}/relink/`, {
    method: 'POST',
  });
  return handleJson(res);
}

// --- Smart event creation ------------------------------------------------

export interface EventCategory {
  value: string;
  label: string;
  is_movement: boolean;
  needs_deal: boolean;
  needs_species: boolean;
}

export async function fetchEventCategories(): Promise<EventCategory[]> {
  const res = await authorizedFetch('/api/ingest/event-categories/');
  const data = await handleJson(res);
  return (data?.categories ?? []) as EventCategory[];
}

export interface ExpectedReceiptMatch {
  dispatch_event_id: string;
  // Every sibling dispatch event of the same physical dispatch (one per deal);
  // the backend receives them all when this match is accepted.
  dispatch_event_ids?: string[];
  dispatch_event_label: string;
  entity_event_type_code: string;
  data_session_id: string | null;
  // deal_id/deal_no are only set when the dispatch belongs to a single deal;
  // null when it spans several. deal_ids/deal_nos/deal_count describe the group.
  deal_id: string | null;
  deal_no: string | null;
  deal_ids?: string[];
  deal_nos?: string[];
  deal_count?: number;
  /**
   * Every file the consignment arrived as. A consolidated load is uploaded one
   * file per deal, so this usually has one entry per deal.
   */
  data_session_ids?: string[];
  /** The load's contents, deal by deal, each receivable on its own. */
  deals?: {
    deal_id: string | null;
    deal_no: string | null;
    animal_count: number;
    dispatch_event_id: string;
    data_session_id: string | null;
    source_filename: string | null;
  }[];
  source_entity_id: string | null;
  source_entity_name: string | null;
  destination_entity_id: string | null;
  destination_entity_name: string | null;
  species: string | null;
  gtin_id: string | null;
  event_date: string | null;
  animal_count: number;
}

export async function fetchExpectedReceipts(params: {
  destinationEntityId: string;
  species?: string;
  dealId?: string;
}): Promise<ExpectedReceiptMatch[]> {
  const query = new URLSearchParams({ destination_entity: params.destinationEntityId });
  if (params.species) query.set('species', params.species);
  if (params.dealId) query.set('deal', params.dealId);
  const res = await authorizedFetch(`/api/ingest/expected-receipts/?${query.toString()}`);
  const data = await handleJson(res);
  return (data?.matches ?? []) as ExpectedReceiptMatch[];
}

export interface AnimalRef {
  id: string;
  eid: string | null;
  vid: string | null;
}

export interface ReceiveFromDispatchResult {
  event: any;
  // One receive event per deal in the dispatch (the whole dispatch is received).
  events?: any[];
  received_count: number;
  dispatched_count: number;
  unreceived: AnimalRef[];
  deals_count?: number;
  deal_nos?: string[];
}

/**
 * Accept a dispatch without a file. Pass `dispatchEventIds` to receive a whole
 * consignment (a load that arrived as one file per deal); one receive event per
 * deal is created either way. The receive date defaults to the dispatch's own
 * date, not today.
 */
export async function receiveFromDispatch(payload: {
  dispatchEventId: string;
  /** The consignment's dispatch events. Omit to receive just this one file. */
  dispatchEventIds?: string[];
  destinationEntityId?: string;
  receivedEids?: string[];
  eventDate?: string;
}): Promise<ReceiveFromDispatchResult> {
  const body: Record<string, unknown> = { dispatch_event_id: payload.dispatchEventId };
  if (payload.dispatchEventIds && payload.dispatchEventIds.length > 1) {
    body.dispatch_event_ids = payload.dispatchEventIds;
  }
  if (payload.destinationEntityId) body.destination_entity_id = payload.destinationEntityId;
  if (payload.receivedEids) body.received_eids = payload.receivedEids;
  if (payload.eventDate) body.event_date = payload.eventDate;
  const res = await authorizedFetch('/api/ingest/receive-from-dispatch/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleJson(res);
}

export interface DispatchReceivePreview {
  dispatch_event_id: string;
  dispatch_event_ids: string[];
  data_session_id: string | null;
  source_entity_id: string | null;
  source_entity_name: string | null;
  destination_entity_id: string | null;
  destination_entity_name: string | null;
  species: string | null;
  deals: { deal_id: string; deal_no: string | null; count: number }[];
  deals_count: number;
  total_count: number;
  sample_count: number;
  animals: (AnimalRef & { deal_no?: string | null })[];
}

// Dry-run of receiveFromDispatch: what WOULD be received (source, destination,
// species, deals, count, sample rows) without creating the event. Powers the
// confirm-before-create step of the "Accept animals" flow.
export async function previewReceiveFromDispatch(dispatchEventId: string, opts?: {
  destinationEntityId?: string;
  sample?: number;
  /** Preview the whole consignment, so the count matches what accepting takes. */
  dispatchEventIds?: string[];
}): Promise<DispatchReceivePreview> {
  const qs = new URLSearchParams();
  if (opts?.destinationEntityId) qs.set('destination_entity_id', opts.destinationEntityId);
  if (opts?.sample) qs.set('sample', String(opts.sample));
  if (opts?.dispatchEventIds && opts.dispatchEventIds.length > 1) {
    qs.set('dispatch_event_ids', opts.dispatchEventIds.join(','));
  }
  const query = qs.toString();
  const res = await authorizedFetch(
    `/api/ingest/dispatch/${encodeURIComponent(dispatchEventId)}/receive-preview/${query ? `?${query}` : ''}`,
  );
  return handleJson(res);
}

export async function fetchDispatchReconciliation(dispatchEventId: string): Promise<{
  dispatch_event_id: string;
  dispatched_count: number;
  received_count: number;
  outstanding_count: number;
  received: AnimalRef[];
  outstanding: AnimalRef[];
}> {
  const res = await authorizedFetch(`/api/ingest/dispatch/${encodeURIComponent(dispatchEventId)}/reconciliation/`);
  return handleJson(res);
}

export async function fetchAnimalLocations(params?: {
  dealId?: string;
  entityId?: string;
  entityType?: string;
  species?: string;
}): Promise<AnimalLocationEntry[]> {
  const qs = new URLSearchParams();
  if (params?.dealId) qs.set('deal_id', params.dealId);
  if (params?.entityId) qs.set('entity_id', params.entityId);
  if (params?.entityType) qs.set('entity_type', params.entityType);
  if (params?.species) qs.set('species', params.species);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authorizedFetch(`/api/animals/locations/animals/${suffix}`);
  const data = await handleJson(res);
  return Array.isArray((data as any)?.animals) ? (data as any).animals : [];
}

export interface AnimalLocationPerformanceGroup {
  group_by: 'entity' | 'entity_type';
  animal_count: number;
  metric_animal_count: number;
  date_range?: { start?: string | null; end?: string | null };
  feed_consumed_kg?: number | null;
  total_weight_gain_kg?: number | null;
  avg_adg?: number | null;
  avg_days_in_feedlot?: number | null;
  avg_feedlot_days?: number | null;
  feedlot_days_sample_size?: number | null;
  fcr?: number | null;
  fcr_estimated?: number | null;
  fcr_is_estimated?: boolean;
  daily_feedlot_cost_per_animal?: number | null;
  avg_feedlot_cost_per_animal?: number | null;
  /** Alias of dressing_percentage kept by the API for older clients. */
  slaughter_percentage?: number | null;
  dressing_percentage?: number | null;
  slaughter_record_count?: number | null;
  entity_id?: string;
  entity_name?: string;
  entity_type?: string;
  value_chain_id?: string | null;
  value_chain_name?: string | null;
  gln?: string | null;
  entity_count?: number;
}

export async function fetchAnimalLocationPerformance(params?: {
  dealId?: string;
  entityId?: string;
  entityType?: string;
  species?: string;
  valueChainId?: string;
  groupBy?: 'entity' | 'entity_type';
  scope?: 'all' | 'active' | 'completed';
  onlyActiveDeals?: boolean;
  animalStatus?: 'ACTIVE' | 'SLAUGHTERED' | 'DECEASED';
}): Promise<AnimalLocationPerformanceGroup[]> {
  const qs = new URLSearchParams();
  if (params?.dealId) qs.set('deal_id', params.dealId);
  if (params?.entityId) qs.set('entity_id', params.entityId);
  if (params?.entityType) qs.set('entity_type', params.entityType);
  if (params?.species) qs.set('species', params.species);
  if (params?.valueChainId) qs.set('value_chain', params.valueChainId);
  qs.set('group_by', params?.groupBy || 'entity_type');
  qs.set('scope', params?.scope || 'all');
  if (params?.onlyActiveDeals) qs.set('only_active_deals', 'true');
  if (params?.animalStatus) qs.set('animal_status', params.animalStatus);
  const res = await authorizedFetch(`/api/animals/locations/performance/?${qs.toString()}`);
  const data = await handleJson(res);
  return Array.isArray((data as any)?.groups) ? (data as any).groups : [];
}

export interface AnimalPerformanceRow {
  animal_id: string;
  eid_tag?: string | null;
  vid?: string | null;
  species?: string | null;
  deal_id?: string | null;
  deal_no?: string | null;
  deal_reference_no?: string | null;
  primary_producer_id?: string | null;
  primary_producer_name?: string | null;
  entity_id?: string | null;
  entity_name?: string | null;
  value_chain_id?: string | null;
  value_chain_name?: string | null;
  feedlot_entered_date?: string | null;
  feedlot_departed_date?: string | null;
  first_weight_kg?: number | null;
  last_weight_kg?: number | null;
  days_in_feedlot?: number | null;
  adg_kg_per_day?: number | null;
  fcr?: number | null;
  fcr_estimated?: number | null;
  estimated_feed_kg?: number | null;
  dressing_percentage?: number | null;
}

// Per-animal breakdown of the same membership population the performance cards
// aggregate (every animal ever received at the matched entities), so the table
// under the cards includes animals that have since moved on / been slaughtered.
export async function fetchAnimalPerformanceBreakdown(params?: {
  dealId?: string;
  entityId?: string;
  entityType?: string;
  species?: string;
  valueChainId?: string;
  scope?: 'all' | 'active' | 'completed';
}): Promise<AnimalPerformanceRow[]> {
  const qs = new URLSearchParams();
  if (params?.dealId) qs.set('deal_id', params.dealId);
  if (params?.entityId) qs.set('entity_id', params.entityId);
  if (params?.entityType) qs.set('entity_type', params.entityType);
  if (params?.species) qs.set('species', params.species);
  if (params?.valueChainId) qs.set('value_chain', params.valueChainId);
  qs.set('group_by', 'entity_type');
  qs.set('scope', params?.scope || 'all');
  qs.set('include_animals', 'true');
  const res = await authorizedFetch(`/api/animals/locations/performance/?${qs.toString()}`);
  const data = await handleJson(res);
  return Array.isArray((data as any)?.animals) ? (data as any).animals : [];
}

// --- Availability forecast (predict when meat is available downstream) ---

export type ForecastBasis =
  | 'actual_slaughter'
  | 'actual_feedlot'
  | 'projected_from_dob'
  | 'insufficient_data'
  | 'no_template';

export interface ForecastMilestone {
  date: string;
  source: 'actual' | 'projected';
}

export interface AvailabilityTimelineBucket {
  month: string; // YYYY-MM
  animal_count: number;
  estimated_carcass_weight_kg: number | null;
  by_species: Record<string, number>;
}

export interface AvailabilityForecastAnimal {
  animal_id: string;
  eid_tag?: string | null;
  vid?: string | null;
  species: string;
  deal_id?: string | null;
  deal_no?: string | null;
  available_date: string | null;
  days_until_available: number | null;
  is_available: boolean;
  basis: ForecastBasis;
  estimated_carcass_weight_kg: number | null;
  milestones: Record<string, ForecastMilestone | null>;
}

export interface AvailabilityForecast {
  as_of_date: string;
  total_animals: number;
  forecastable_animals: number;
  unschedulable_animals: number;
  estimated_total_carcass_weight_kg: number | null;
  basis_breakdown: Record<string, number>;
  timeline: AvailabilityTimelineBucket[];
  species_with_templates: string[];
  species_without_templates: string[];
  animals?: AvailabilityForecastAnimal[];
}

export async function fetchAvailabilityForecast(params?: {
  species?: string;
  dealId?: string;
  valueChainId?: string;
  bufferDays?: number;
  includeSlaughtered?: boolean;
  includeAnimals?: boolean;
  asOfDate?: string;
}): Promise<AvailabilityForecast> {
  const qs = new URLSearchParams();
  if (params?.species) qs.set('species', params.species);
  if (params?.dealId) qs.set('deal_id', params.dealId);
  if (params?.valueChainId) qs.set('value_chain', params.valueChainId);
  if (params?.bufferDays != null) qs.set('buffer_days', String(params.bufferDays));
  if (params?.includeSlaughtered) qs.set('include_slaughtered', 'true');
  if (params?.includeAnimals) qs.set('include_animals', 'true');
  if (params?.asOfDate) qs.set('as_of_date', params.asOfDate);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authorizedFetch(`/api/animals/availability-forecast/${suffix}`);
  return handleJson(res);
}

export async function fetchAnimalAvailabilityForecast(
  animalId: string,
  bufferDays?: number
): Promise<AvailabilityForecastAnimal & { dob: string | null; status: string; template_id: string | null }> {
  const qs = new URLSearchParams();
  if (bufferDays != null) qs.set('buffer_days', String(bufferDays));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authorizedFetch(
    `/api/animals/animals/${encodeURIComponent(animalId)}/availability-forecast/${suffix}`
  );
  return handleJson(res);
}

// --- Woolies producer: dashboard notifications + monthly stage report ---

export interface WooliesRegistrationNotification {
  event_id: string;
  data_session_id: string | null;
  session_label: string | null;
  registration_type: string;
  registered_on: string | null;
  deal_id: string | null;
  deal_no: string | null;
  producer_id: string | null;
  producer_name: string | null;
  animal_count: number;
  by_species: Record<string, number>;
  earliest_available: string | null;
  latest_available: string | null;
  estimated_carcass_weight_kg: number | null;
}

export interface WooliesRegistrationNotifications {
  as_of_date: string;
  window_days: number;
  count: number;
  notifications: WooliesRegistrationNotification[];
}

export async function fetchWooliesRegistrationNotifications(
  days?: number
): Promise<WooliesRegistrationNotifications> {
  const qs = new URLSearchParams();
  if (days != null) qs.set('days', String(days));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authorizedFetch(`/api/animals/woolies/registration-notifications/${suffix}`);
  return handleJson(res);
}

export interface WooliesStageMonth {
  month: string; // YYYY-MM
  by_species: Record<string, Record<string, number>>;
  totals: Record<string, number>;
  total_animals: number;
}

export interface WooliesStageAnimal {
  animal_id: string;
  eid_tag: string | null;
  vid: string | null;
  species: string;
  deal_no: string | null;
  producer_name: string | null;
  dob: string | null;
  current_stage: string | null;
  estimated_slaughter_date: string | null;
  slaughter_basis: string | null;
  estimated_available_date: string | null;
  basis: string;
}

export interface WooliesStageForecast {
  as_of_date: string;
  months: number;
  stages: string[];
  species: string[];
  total_animals: number;
  unschedulable_animals: number;
  species_without_templates: string[];
  timeline: WooliesStageMonth[];
  animals?: WooliesStageAnimal[];
}

export async function fetchWooliesStageForecast(params?: {
  months?: number;
  species?: string;
  includeAnimals?: boolean;
}): Promise<WooliesStageForecast> {
  const qs = new URLSearchParams();
  if (params?.months != null) qs.set('months', String(params.months));
  if (params?.species) qs.set('species', params.species);
  if (params?.includeAnimals) qs.set('include_animals', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authorizedFetch(`/api/animals/woolies/stage-forecast/${suffix}`);
  return handleJson(res);
}

export interface ProductionScheduleTemplate {
  id: string;
  species: string;
  name: string;
  weaning_days: number;
  post_weaning_days: number;
  feedlot_days: number;
  processing_buffer_days: number;
  target_live_weight_kg: string | null;
  dressing_percentage: string | null;
  is_active: boolean;
  total_days_to_available: number;
  estimated_carcass_weight_kg: number | null;
}

export interface ProductionScheduleUpsertInput {
  species: string;
  name?: string;
  weaning_days: number;
  post_weaning_days: number;
  feedlot_days: number;
  processing_buffer_days: number;
  target_live_weight_kg?: string | null;
  dressing_percentage?: string | null;
  is_active?: boolean;
}

export async function fetchProductionSchedules(): Promise<ProductionScheduleTemplate[]> {
  const res = await authorizedFetch('/api/animals/production-schedules/');
  const data = await handleJson(res);
  return Array.isArray(data) ? data : ((data as any)?.results ?? []);
}

export async function createProductionSchedule(
  input: ProductionScheduleUpsertInput
): Promise<ProductionScheduleTemplate> {
  const res = await authorizedFetch('/api/animals/production-schedules/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handleJson(res);
}

export async function updateProductionSchedule(
  id: string,
  input: Partial<ProductionScheduleUpsertInput>
): Promise<ProductionScheduleTemplate> {
  const res = await authorizedFetch(`/api/animals/production-schedules/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handleJson(res);
}

export async function deleteProductionSchedule(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/animals/production-schedules/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete schedule failed (${res.status})`);
  }
}

export interface DataQualitySummary {
  average_score: number | null;
  sessions_scored: number;
  total_sessions: number;
  by_grade: { EXCELLENT: number; GOOD: number; FAIR: number; POOR: number };
}

export async function fetchDataQualitySummary(): Promise<DataQualitySummary> {
  const res = await authorizedFetch('/api/ingest/quality/summary/');
  return handleJson(res);
}

// --- Integration systems (admin: control when integrations are live) ---
export type IntegrationEnvironment = 'TEST' | 'PRODUCTION';

export interface IntegrationSystemInfo {
  id: string;
  name: string;
  current_environment: IntegrationEnvironment;
  is_active: boolean;
  is_live: boolean;
  auth_type: string;
  timeout_seconds: number;
  base_url: string | null;
  test_base_url: string | null;
  production_base_url: string | null;
  // Presence flags only — the raw keys are write-only and never returned.
  has_test_api_key: boolean;
  has_production_api_key: boolean;
  updated_at: string;
}

// Only include a key field when the admin actually typed a new value —
// omitting it leaves the stored key unchanged (an empty string would clear it).
export interface IntegrationSystemPatch {
  is_active?: boolean;
  current_environment?: IntegrationEnvironment;
  test_base_url?: string | null;
  production_base_url?: string | null;
  test_api_key?: string;
  production_api_key?: string;
}

export async function fetchIntegrationSystems(): Promise<IntegrationSystemInfo[]> {
  const res = await authorizedFetch('/api/integration/systems/');
  const data = await handleJson(res);
  return Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.results)
      ? (data as any).results
      : [];
}

export async function updateIntegrationSystem(
  id: string,
  patch: IntegrationSystemPatch,
): Promise<IntegrationSystemInfo> {
  const res = await authorizedFetch(`/api/integration/systems/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Update integration failed (${res.status})`);
  }
  return res.json();
}

export interface DealHeadcountSummary {
  by_deal: Record<string, Record<string, number>>;
}

export async function fetchDealHeadcountSummary(dealIds?: string[]): Promise<DealHeadcountSummary> {
  const suffix = dealIds && dealIds.length ? `?deal_ids=${encodeURIComponent(dealIds.join(','))}` : '';
  const res = await authorizedFetch(`/api/deals/headcount-summary/${suffix}`);
  return handleJson(res);
}

export async function fetchDealsAdminOverview(): Promise<DealAdminOverviewItem[]> {
  const res = await authorizedFetch('/api/deals/admin-overview/');
  const data = await handleJson(res);
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.results)
      ? (data as any).results
      : [];

  return Array.isArray(rows)
    ? rows.map((row: any) => ({
        id: String(row.id),
        deal_no: row.deal_no,
        deal_reference_no: row.deal_reference_no,
        deal_date: row.deal_date,
        total_animals: Number(row.total_animals || 0),
        is_completed: Boolean(row.is_completed),
        primary_producer: row.primary_producer
          ? {
              id: String(row.primary_producer.id),
              entity_name: row.primary_producer.entity_name,
            }
          : undefined,
        value_chain: row.value_chain
          ? {
              id: String(row.value_chain.id),
              name: row.value_chain.name,
            }
          : undefined,
      }))
    : [];
}

export async function fetchDealDocumentTypeOptions(): Promise<Array<SelectOption & { code?: string; financeRecordType?: string }>> {
  // Canonical list of document types (core.DocumentType), independent of whether
  // any documents have been uploaded yet. The previous implementation derived
  // the options from existing /api/deals/documents/ rows, so the picker was
  // empty on a fresh environment — a chicken-and-egg problem since the backend
  // requires document_type on upload.
  const res = await authorizedFetch('/api/core/document-types/');
  const data = await handleJson(res);
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.results)
      ? (data as any).results
      : [];

  // `code` is the stable compliance identifier (ID_COPY, GLN_CERT, …) the
  // deal's document-requirements checklist matches on; `name` is free text.
  const optionsById = new Map<string, { label: string; code?: string; financeRecordType?: string }>();
  for (const row of rows) {
    const id = row?.id != null ? String(row.id) : '';
    if (!id) continue;
    const name = String(row?.name || `Document type ${id}`);
    if (!optionsById.has(id)) {
      optionsById.set(id, { label: name, code: row?.code ? String(row.code) : undefined, financeRecordType: row?.finance_record_type ? String(row.finance_record_type) : undefined });
    }
  }

  return Array.from(optionsById.entries()).map(([id, { label, code, financeRecordType }]) => ({ id, label, code, financeRecordType }));
}

const SPECIES_LABELS: Record<string, string> = { SHEEP: 'sheep', GOAT: 'goats', CATTLE: 'cattle' };

function mapDealEventRow(row: any): SelectOption {
  const id = String(row.id);
  const eventType = String(row.event_type || '').replace(/_/g, ' ');
  const datePart = row.event_date
    ? new Date(row.event_date).toLocaleDateString()
    : row.created_at
      ? new Date(row.created_at).toLocaleDateString()
      : '';
  const speciesLabel = row.species
    ? (SPECIES_LABELS[String(row.species).toUpperCase()] || String(row.species).toLowerCase())
    : 'animals';
  const animalCount = row.animal_count ? ` · ${row.animal_count} ${speciesLabel}` : '';
  const label = [eventType, datePart].filter(Boolean).join(' — ') + animalCount;
  const location = row.destination_entity_name ? String(row.destination_entity_name) : undefined;
  return { id, label: label || `Event ${id}`, location, animalCount: Number(row.animal_count || 0) };
}

export async function fetchTransportDealEventOptions(dealId?: string): Promise<SelectOption[]> {
  if (!dealId) return [];

  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/events/`);
  if (!res.ok) return [];

  const data = await res.json();
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.results)
      ? (data as any).results
      : [];

  // Journey.deal_event only accepts receive events (backend enforces FARM_REC / FEED_REC / ABAT_REC)
  const TRANSPORT_EVENT_TYPES = new Set([
    'FARM_REC', 'FEED_REC', 'ABAT_REC',
  ]);

  return rows
    .filter((row: any) => TRANSPORT_EVENT_TYPES.has(String(row.event_type || '')))
    .map(mapDealEventRow);
}

/** Dispatch-type deal events for a deal — optional pairing on Journey.dispatch_event. */
export async function fetchTransportDispatchEventOptions(dealId?: string): Promise<SelectOption[]> {
  if (!dealId) return [];

  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/events/`);
  if (!res.ok) return [];

  const data = await res.json();
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.results)
      ? (data as any).results
      : [];

  const DISPATCH_EVENT_TYPES = new Set([
    'FARM_DIS', 'FEED_DIS', 'ABAT_DIS',
  ]);

  return rows
    .filter((row: any) => DISPATCH_EVENT_TYPES.has(String(row.event_type || '')))
    .map(mapDealEventRow);
}

/** Fetches ALL deal events (no type filter) and returns a id→label map for display purposes. */
export async function fetchDealEventLabels(dealId?: string): Promise<Record<string, string>> {
  if (!dealId) return {};

  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/events/`);
  if (!res.ok) return {};

  const data = await res.json();
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.results)
      ? (data as any).results
      : [];

  return Object.fromEntries(rows.map((row: any) => [String(row.id), mapDealEventRow(row).label]));
}

export interface UpdateDealEventInput {
  eventTypeCode?: string;
  eventDate?: string;
  animalCount?: number;
  sourceEntityId?: string;
  destinationEntityId?: string;
  species?: 'SHEEP' | 'GOAT' | 'CATTLE' | string;
}

// Live on staging as of backend v2.6.26. source_entity/destination_entity/species
// write through to the linked ingest upload session — the backend returns a 400
// if that session is shared with sibling events from the same batch, and a 409 if
// the event is referenced by a transport journey or weighbridge record.
export async function updateDealEvent(dealId: string, eventId: string, input: UpdateDealEventInput): Promise<any> {
  const payload: Record<string, any> = {};
  if (input.eventTypeCode !== undefined) payload.entity_event_type_code = input.eventTypeCode;
  if (input.eventDate !== undefined) payload.event_date = input.eventDate;
  if (input.animalCount !== undefined) payload.animal_count = input.animalCount;
  if (input.sourceEntityId !== undefined) payload.source_entity = input.sourceEntityId;
  if (input.destinationEntityId !== undefined) payload.destination_entity = input.destinationEntityId;
  if (input.species !== undefined) payload.species = input.species;

  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/events/${encodeURIComponent(eventId)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export interface CreateDealEventInput {
  eventTypeCode: string; // receive types only: FARM_REC | FEED_REC | ABAT_REC
  species: 'SHEEP' | 'GOAT' | 'CATTLE' | string;
  eventDate?: string;
  animalCount?: number;
  sourceEntityId?: string;
  destinationEntityId?: string;
}

// Retroactively add a missing receive event to a deal — e.g. an abattoir-receive
// (ABAT_REC) for animals only ever captured at slaughter, so a transport journey
// has an event to attach to. Backend restricts this to receive types and backs
// the event with a synthetic upload session carrying source/destination/species.
// POSTs to the same collection the events list is read from.
export async function createDealEvent(dealId: string, input: CreateDealEventInput): Promise<any> {
  const payload: Record<string, any> = {
    entity_event_type_code: input.eventTypeCode,
    species: input.species,
  };
  if (input.eventDate) payload.event_date = input.eventDate;
  if (input.animalCount !== undefined) payload.animal_count = input.animalCount;
  if (input.sourceEntityId) payload.source_entity = input.sourceEntityId;
  if (input.destinationEntityId) payload.destination_entity = input.destinationEntityId;

  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/events/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

// Soft-delete (void) — kept for later; not wired into any UI yet per current scope
// (edit/patch only). DELETE sets is_voided rather than removing the row; calling
// this on an already-voided event is a no-op 204.
export async function voidDealEvent(dealId: string, eventId: string, reason?: string): Promise<void> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/events/${encodeURIComponent(eventId)}/`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason ? { reason } : {}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Void event failed (${res.status})`);
  }
}

export async function deleteDealEvent(dealId: string, eventId: string): Promise<void> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/events/${encodeURIComponent(eventId)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete event failed (${res.status})`);
  }
}

export async function createAnimal(input: AnimalUpsertInput): Promise<any> {
  const res = await authorizedFetch('/api/animals/animals/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deal: input.dealId,
      species: input.species,
      vid: input.vid || null,
      eid_tag: input.eidTag || null,
      current_entity: input.currentEntity || null,
      status: input.status || 'ACTIVE',
      breed: input.breed || null,
      dob: input.dob || null,
      sex: input.sex || null,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Create animal failed (${res.status})`);
  }

  return res.json();
}

export async function updateAnimal(id: string, input: AnimalUpsertInput): Promise<any> {
  const res = await authorizedFetch(`/api/animals/animals/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deal: input.dealId,
      species: input.species,
      vid: input.vid || null,
      eid_tag: input.eidTag || null,
      current_entity: input.currentEntity || null,
      status: input.status || 'ACTIVE',
      breed: input.breed || null,
      dob: input.dob || null,
      sex: input.sex || null,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Update animal failed (${res.status})`);
  }

  return res.json();
}

export async function deleteAnimalById(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/animals/animals/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete animal failed (${res.status})`);
  }
}

export async function uploadWoolFinance(input: WoolFinanceUploadInput): Promise<any> {
  const formData = new FormData();
  formData.append('deal', input.dealId);
  formData.append('document_type', input.documentTypeId);
  formData.append('sale_value', String(input.saleValue));
  formData.append('sale_date', input.saleDate);
  formData.append('document_scan', input.file);
  if (input.notes) formData.append('notes', input.notes);
  if (input.visibility) formData.append('visibility', input.visibility);

  const res = await authorizedFetch('/api/wool/finances/', {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Wool upload failed (${res.status})`);
  }

  return res.json();
}

export async function uploadTransportJourney(input: TransportJourneyUploadInput): Promise<any> {
  const formData = new FormData();
  formData.append('deal_event', input.dealEventId);
  if (input.dispatchEventId) formData.append('dispatch_event', input.dispatchEventId);
  formData.append('courier', input.courierId);
  formData.append('loading_date', input.loadingDate);
  if (input.offloadingDate) formData.append('offloading_date', input.offloadingDate);
  formData.append('vehicle_registration', input.vehicleRegistration);
  formData.append('start_km', String(input.startKm));
  formData.append('end_km', String(input.endKm));
  if (input.waybillNumber) formData.append('waybill_no', input.waybillNumber);
  if (input.totalCost != null && Number.isFinite(input.totalCost)) {
    formData.append('total_cost', String(input.totalCost));
  }
  if (input.waybillFile) formData.append('waybill', input.waybillFile);
  if (input.invoiceFile) formData.append('invoice', input.invoiceFile);
  if (input.documentVisibility) formData.append('document_visibility', input.documentVisibility);
  if (input.loadingTime) formData.append('loading_time', input.loadingTime);
  if (input.loadingPoint) formData.append('loading_point', input.loadingPoint);
  if (input.offloadingPoint) formData.append('offloading_point', input.offloadingPoint);
  if (input.driverName) formData.append('driver_name', input.driverName);
  if (input.driverContact) formData.append('driver_contact', input.driverContact);
  if (input.removalCertFile) formData.append('removal_certificate', input.removalCertFile);
  if (input.animalsDown != null) formData.append('animals_down', String(input.animalsDown));
  if (input.animalsDownReason) formData.append('animals_down_reason', input.animalsDownReason);

  const res = await authorizedFetch('/api/transport/journeys/', {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Transport upload failed (${res.status})`);
  }

  return res.json();
}

export async function fetchCouriers(): Promise<CourierSummary[]> {
  const now = Date.now();
  if (now < couriersCooldownUntil) {
    return couriersLastValue;
  }
  if (couriersInFlight) {
    return couriersInFlight;
  }

  couriersInFlight = (async () => {
    try {
      const res = await authorizedFetch('/api/transport/couriers/');
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
          ? retryAfterSeconds * 1000
          : 30000;
        couriersCooldownUntil = Date.now() + waitMs;
        return couriersLastValue;
      }

      const data = await handleJson(res);
      const rows = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.results)
          ? (data as any).results
          : [];

      if (!Array.isArray(rows)) return couriersLastValue;

      const mapped = rows.map((row: any) => ({
        id: String(row.id),
        name: String(row.name || 'Unnamed courier'),
        contactNumber: row.contact_number || '',
        email: row.email || '',
        active: row.active,
      }));

      couriersLastValue = mapped;
      return mapped;
    } catch {
      return couriersLastValue;
    } finally {
      couriersInFlight = null;
    }
  })();

  return couriersInFlight;
}

export async function createCourier(input: CreateCourierInput): Promise<CourierSummary> {
  const res = await authorizedFetch('/api/transport/couriers/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name,
      contact_number: input.contactNumber || '',
      email: input.email || '',
      active: true,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Create courier failed (${res.status})`);
  }

  const row = await res.json();
  return {
    id: String(row.id),
    name: String(row.name || input.name),
    contactNumber: row.contact_number || input.contactNumber || '',
    email: row.email || input.email || '',
    active: row.active,
  };
}

export async function updateCourier(id: string, input: CreateCourierInput): Promise<CourierSummary> {
  const res = await authorizedFetch(`/api/transport/couriers/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name,
      contact_number: input.contactNumber || '',
      email: input.email || '',
      active: input.active !== undefined ? input.active : true,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Update courier failed (${res.status})`);
  }

  const row = await res.json();
  return {
    id: String(row.id),
    name: String(row.name || input.name),
    contactNumber: row.contact_number || input.contactNumber || '',
    email: row.email || input.email || '',
    active: row.active,
  };
}

export async function deleteCourier(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/transport/couriers/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete courier failed (${res.status})`);
  }
}

export async function fetchFeedOptions(): Promise<FeedOption[]> {
  const now = Date.now();
  if (now < feedOptionsCooldownUntil) {
    return feedOptionsLastValue;
  }
  if (feedOptionsInFlight) {
    return feedOptionsInFlight;
  }

  feedOptionsInFlight = (async () => {
    try {
      const res = await authorizedFetch('/api/feedlot/feeds/');
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
          ? retryAfterSeconds * 1000
          : 30000;
        feedOptionsCooldownUntil = Date.now() + waitMs;
        return feedOptionsLastValue;
      }

      const data = await handleJson(res);
      const rows = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.results)
          ? (data as any).results
          : [];

      if (!Array.isArray(rows)) return feedOptionsLastValue;

      const mapped = rows.map((row: any) => ({
        id: String(row.id),
        name: String(row.name || 'Unnamed feed'),
        description: row.description || '',
        costPerKg: Number(row.cost_per_kg || 0),
        stockQty: Number(row.stock_qty || 0),
      }));
      feedOptionsLastValue = mapped;
      return mapped;
    } catch {
      return feedOptionsLastValue;
    } finally {
      feedOptionsInFlight = null;
    }
  })();

  return feedOptionsInFlight;
}

export async function createFeedOption(input: CreateFeedOptionInput): Promise<FeedOption> {
  const res = await authorizedFetch('/api/feedlot/feeds/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name,
      description: input.description || '',
      cost_per_kg: input.costPerKg ?? 0,
      stock_qty: input.stockQty ?? 0,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Create feed type failed (${res.status})`);
  }

  const row = await res.json();
  return {
    id: String(row.id),
    name: String(row.name || input.name),
    description: row.description || '',
    costPerKg: Number(row.cost_per_kg || 0),
    stockQty: Number(row.stock_qty || 0),
  };
}

export async function updateFeedOption(id: string, input: CreateFeedOptionInput): Promise<FeedOption> {
  const res = await authorizedFetch(`/api/feedlot/feeds/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name,
      description: input.description || '',
      cost_per_kg: input.costPerKg ?? 0,
      stock_qty: input.stockQty ?? 0,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Update feed type failed (${res.status})`);
  }

  const row = await res.json();
  return {
    id: String(row.id),
    name: String(row.name || input.name),
    description: row.description || '',
    costPerKg: Number(row.cost_per_kg || 0),
    stockQty: Number(row.stock_qty || 0),
  };
}

export async function deleteFeedOption(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/feedlot/feeds/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete feed type failed (${res.status})`);
  }
}

export async function fetchFeedConsumption(dealId?: string): Promise<FeedConsumptionItem[]> {
  const key = dealId || 'all';
  const now = Date.now();
  const cooldownUntil = feedConsumptionCooldownUntil.get(key) || 0;

  if (now < cooldownUntil) {
    return feedConsumptionLastValue.get(key) || [];
  }
  const inFlight = feedConsumptionInFlight.get(key);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
    try {
      const query = dealId ? `?deal=${encodeURIComponent(dealId)}` : '';
      const res = await authorizedFetch(`/api/feedlot/consumption/${query}`);
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
          ? retryAfterSeconds * 1000
          : 30000;
        feedConsumptionCooldownUntil.set(key, Date.now() + waitMs);
        return feedConsumptionLastValue.get(key) || [];
      }

      const data = await handleJson(res);
      const rows = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.results)
          ? (data as any).results
          : [];

      if (!Array.isArray(rows)) return feedConsumptionLastValue.get(key) || [];

      const mapped = rows.map((row: any) => ({
        id: String(row.id),
        feedId: String(row.feed),
        dealId: String(row.deal),
        quantityKg: Number(row.quantity || 0),
        date: String(row.consumption_date || ''),
      }));

      const result = !dealId
        ? mapped
        : mapped.filter((row: any) => String(row.dealId) === String(dealId));

      feedConsumptionLastValue.set(key, result);
      return result;
    } catch {
      return feedConsumptionLastValue.get(key) || [];
    } finally {
      feedConsumptionInFlight.delete(key);
    }
  })();

  feedConsumptionInFlight.set(key, request);
  return request;
}

export async function createFeedConsumption(input: FeedConsumptionInput): Promise<FeedConsumptionItem> {
  const res = await authorizedFetch('/api/feedlot/consumption/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      feed: input.feedId,
      deal: input.dealId,
      quantity: input.quantityKg,
      consumption_date: input.date,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Create feed consumption failed (${res.status})`);
  }

  const row = await res.json();
  return {
    id: String(row.id),
    feedId: String(row.feed),
    dealId: String(row.deal),
    quantityKg: Number(row.quantity || 0),
    date: String(row.consumption_date || ''),
  };
}

export async function updateFeedConsumption(id: string, input: FeedConsumptionInput): Promise<FeedConsumptionItem> {
  const res = await authorizedFetch(`/api/feedlot/consumption/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      feed: input.feedId,
      deal: input.dealId,
      quantity: input.quantityKg,
      consumption_date: input.date,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Update feed consumption failed (${res.status})`);
  }

  const row = await res.json();
  return {
    id: String(row.id),
    feedId: String(row.feed),
    dealId: String(row.deal),
    quantityKg: Number(row.quantity || 0),
    date: String(row.consumption_date || ''),
  };
}

export async function deleteFeedConsumption(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/feedlot/consumption/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete feed consumption failed (${res.status})`);
  }
}

// Password reset (public endpoints)
// Use shared base config; when empty, calls use relative /api via proxy
const API_BASE_URL = (BASE || '').replace(/\/$/, '');

export async function requestPasswordReset(identifier: string): Promise<void> {
  const body = { identifier };
  const res = await fetch(`${API_BASE_URL}/api/auth/password/reset/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  // Always resolve on 200; if service hides existence, treat 200 universally
  if (!res.ok) {
    // Consume text for better error messages
    const text = await res.text();
    throw new Error(text || `Reset request failed (${res.status})`);
  }
}

export async function confirmPasswordReset(args: { uid: string; token: string; new_password: string }): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/auth/password/reset/confirm/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args)
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Reset confirm failed (${res.status})`);
  }
}

function mapEntityContact(row: any): EntityContactSummary {
  const firstName = row?.first_name ? String(row.first_name) : '';
  const lastName = row?.last_name ? String(row.last_name) : '';
  const email = row?.email ? String(row.email) : row?.user_email ? String(row.user_email) : '';
  const phoneNo = row?.phone_no ? String(row.phone_no) : '';

  // `display_name` comes from the backend and is sourced from the contact
  // record. The chain below reproduces it client-side so this works against a
  // backend that predates the field -- the two can ship independently.
  //
  // `user_full_name` is deliberately NOT first: it derives from the optional
  // `user` FK (a portal account), so it is blank for any contact created from a
  // producer document. That is what made Stefan Erasmus render as "Unknown
  // contact" while his name sat correctly on the contact row.
  const resolved =
    String(row?.display_name || '').trim() ||
    `${firstName} ${lastName}`.trim() ||
    String(row?.user_full_name || '').trim() ||
    email ||
    phoneNo;

  return {
    id: String(row?.id || ''),
    entityId: row?.entity != null ? String(row.entity) : undefined,
    userId: row?.user != null ? String(row.user) : row?.user_id != null ? String(row.user_id) : undefined,
    username: row?.user_username ? String(row.user_username) : undefined,
    firstName,
    lastName,
    name: resolved || 'Unnamed contact',
    email,
    phoneNo,
    isPrimary: Boolean(row?.is_primary),
  };
}

function mapEntity(row: any): EntitySummary {
  return {
    id: String(row?.id || ''),
    entityNo: row?.entity_no ? String(row.entity_no) : '',
    entityName: String(row?.entity_name || 'Unnamed entity'),
    entityType: row?.entity_type ? String(row.entity_type) : '',
    valueChainId: row?.value_chain ? String(row.value_chain) : '',
    valueChainName: row?.value_chain_name ? String(row.value_chain_name) : '',
    registeredBusinessName: row?.registered_business_name ? String(row.registered_business_name) : '',
    vatNumber: row?.vat_number ? String(row.vat_number) : '',
    partyKind: row?.party_kind ? String(row.party_kind) as any : '',
    identityNumber: row?.identity_number ? String(row.identity_number) : '',
    entityTypeOther: row?.entity_type_other ? String(row.entity_type_other) : '',
    farmName: row?.farm_name ? String(row.farm_name) : '',
    district: row?.district ? String(row.district) : '',
    province: row?.province ? String(row.province) : '',
    vatRegistered: Boolean(row?.vat_registered),
    glnApplicable: Boolean(row?.gln_applicable),
    wooliesProducer: Boolean(row?.woolies_producer),
    costPerDay: row?.cost_per_day != null ? String(row.cost_per_day) : null,
    addressLine1: row?.address_line1 ? String(row.address_line1) : '',
    addressLine2: row?.address_line2 ? String(row.address_line2) : '',
    city: row?.city ? String(row.city) : '',
    state: row?.state ? String(row.state) : '',
    postalCode: row?.postal_code ? String(row.postal_code) : '',
    country: row?.country ? String(row.country) : '',
    latitude: row?.latitude != null ? String(row.latitude) : '',
    longitude: row?.longitude != null ? String(row.longitude) : '',
    contacts: Array.isArray(row?.contacts) ? row.contacts.map(mapEntityContact) : [],
  };
}

export async function fetchEntities(): Promise<EntitySummary[]> {
  const now = Date.now();
  if (now < entitiesCooldownUntil) return entitiesLastValue;
  if (entitiesInFlight) return entitiesInFlight;

  entitiesInFlight = (async () => {
    try {
      const res = await authorizedFetch('/api/entity/entities/');
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        entitiesCooldownUntil = Date.now() + waitMs;
        return entitiesLastValue;
      }
      const data = await handleJson(res);
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
      const result = rows.map(mapEntity);
      entitiesLastValue = result;
      return result;
    } catch {
      return entitiesLastValue;
    } finally {
      entitiesInFlight = null;
    }
  })();

  return entitiesInFlight;
}

/** Deal-capture party fields, sent only when supplied so an unrelated edit
 *  (renaming an entity, say) does not blank them. */
function entityPartyPayload(input: Partial<CreateEntityInput>): Record<string, any> {
  const payload: Record<string, any> = {};
  if (input.partyKind !== undefined) payload.party_kind = input.partyKind || '';
  if (input.identityNumber !== undefined) payload.identity_number = input.identityNumber || '';
  if (input.entityTypeOther !== undefined) payload.entity_type_other = input.entityTypeOther || '';
  if (input.farmName !== undefined) payload.farm_name = input.farmName || '';
  if (input.district !== undefined) payload.district = input.district || '';
  if (input.province !== undefined) payload.province = input.province || '';
  if (input.vatRegistered !== undefined) payload.vat_registered = Boolean(input.vatRegistered);
  if (input.glnApplicable !== undefined) payload.gln_applicable = Boolean(input.glnApplicable);
  return payload;
}

export async function createEntity(input: CreateEntityInput): Promise<EntitySummary> {
  const res = await authorizedFetch('/api/entity/entities/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      entity_name: input.entityName,
      entity_no: input.entityNo || null,
      entity_type: input.entityType || 'FARM',
      value_chain: input.valueChainId || null,
      registered_business_name: input.registeredBusinessName || '',
      vat_number: input.vatNumber || '',
      ...entityPartyPayload(input),
      woolies_producer: Boolean(input.wooliesProducer),
      cost_per_day: input.costPerDay ? input.costPerDay : null,
      address_line1: input.addressLine1 || '',
      address_line2: input.addressLine2 || '',
      city: input.city || '',
      state: input.state || '',
      postal_code: input.postalCode || '',
      country: input.country || '',
      latitude: input.latitude || null,
      longitude: input.longitude || null,
    }),
  });
  const row = await handleJson(res);
  resetEntitiesCache();
  return mapEntity(row);
}

export async function updateEntity(id: string, input: UpdateEntityInput): Promise<EntitySummary> {
  const payload: Record<string, any> = {};
  if (input.entityName !== undefined) payload.entity_name = input.entityName;
  if (input.entityNo !== undefined) payload.entity_no = input.entityNo || null;
  if (input.entityType !== undefined) payload.entity_type = input.entityType || null;
  if (input.valueChainId !== undefined) payload.value_chain = input.valueChainId || null;
  if (input.registeredBusinessName !== undefined) payload.registered_business_name = input.registeredBusinessName || '';
  if (input.vatNumber !== undefined) payload.vat_number = input.vatNumber || '';
  if (input.wooliesProducer !== undefined) payload.woolies_producer = Boolean(input.wooliesProducer);
  if (input.costPerDay !== undefined) payload.cost_per_day = input.costPerDay ? input.costPerDay : null;
  if (input.addressLine1 !== undefined) payload.address_line1 = input.addressLine1 || '';
  if (input.addressLine2 !== undefined) payload.address_line2 = input.addressLine2 || '';
  if (input.city !== undefined) payload.city = input.city || '';
  if (input.state !== undefined) payload.state = input.state || '';
  if (input.postalCode !== undefined) payload.postal_code = input.postalCode || '';
  if (input.country !== undefined) payload.country = input.country || '';
  if (input.latitude !== undefined) payload.latitude = input.latitude || null;
  if (input.longitude !== undefined) payload.longitude = input.longitude || null;
  Object.assign(payload, entityPartyPayload(input));

  const res = await authorizedFetch(`/api/entity/entities/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const row = await handleJson(res);
  resetEntitiesCache();
  return mapEntity(row);
}

export async function fetchNextEntityNumber(): Promise<string> {
  const res = await authorizedFetch('/api/entity/entities/next-number/');
  const data = await handleJson(res);
  return String(data?.next_entity_no || '');
}

export async function deleteEntity(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/entity/entities/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete entity failed (${res.status})`);
  }
  resetEntitiesCache();
}

export async function fetchEntityContacts(entityId?: string): Promise<EntityContactSummary[]> {
  // Per-entity targeted fetch — bypass dedup
  if (entityId) {
    const res = await authorizedFetch(`/api/entity/entity-contacts/?entity=${encodeURIComponent(entityId)}`);
    const data = await handleJson(res);
    const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
    return rows.map(mapEntityContact);
  }

  const now = Date.now();
  if (now < entityContactsAllCooldownUntil) return entityContactsAllLastValue;
  if (entityContactsAllInFlight) return entityContactsAllInFlight;

  entityContactsAllInFlight = (async () => {
    try {
      const res = await authorizedFetch('/api/entity/entity-contacts/');
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        entityContactsAllCooldownUntil = Date.now() + waitMs;
        return entityContactsAllLastValue;
      }
      const data = await handleJson(res);
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
      const result = rows.map(mapEntityContact);
      entityContactsAllLastValue = result;
      return result;
    } catch {
      return entityContactsAllLastValue;
    } finally {
      entityContactsAllInFlight = null;
    }
  })();

  return entityContactsAllInFlight;
}

export async function createEntityContact(input: CreateEntityContactInput): Promise<EntityContactSummary> {
  const payload: Record<string, any> = {
    entity: input.entityId,
    first_name: input.firstName || '',
    last_name: input.lastName || '',
    email: input.email || '',
    phone_no: input.phoneNo || '',
    is_primary: Boolean(input.isPrimary),
  };
  // A user link is optional. Only attach one when a valid id was supplied.
  if (input.userId != null && input.userId !== '') {
    const userPk = Number(input.userId);
    if (!Number.isInteger(userPk) || userPk <= 0) {
      throw new Error('User ID must be a valid positive integer.');
    }
    payload.user = userPk;
  }
  const res = await authorizedFetch('/api/entity/entity-contacts/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const row = await handleJson(res);
  resetEntityContactsCache();
  resetEntitiesCache();
  return mapEntityContact(row);
}

export async function updateEntityContact(id: string, input: UpdateEntityContactInput): Promise<EntityContactSummary> {
  const payload: Record<string, any> = {};
  if (input.entityId !== undefined) payload.entity = input.entityId;
  if (input.userId !== undefined) {
    if (input.userId === '') {
      // Explicitly unlink the user — contact keeps its own name/email/phone.
      payload.user = null;
    } else {
      const userPk = Number(input.userId);
      if (!Number.isInteger(userPk) || userPk <= 0) {
        throw new Error('User ID must be a valid positive integer.');
      }
      payload.user = userPk;
    }
  }
  if (input.firstName !== undefined) payload.first_name = input.firstName || '';
  if (input.lastName !== undefined) payload.last_name = input.lastName || '';
  if (input.email !== undefined) payload.email = input.email || '';
  if (input.phoneNo !== undefined) payload.phone_no = input.phoneNo || '';
  if (input.isPrimary !== undefined) payload.is_primary = Boolean(input.isPrimary);

  const res = await authorizedFetch(`/api/entity/entity-contacts/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const row = await handleJson(res);
  resetEntityContactsCache();
  resetEntitiesCache();
  return mapEntityContact(row);
}

export async function deleteEntityContact(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/entity/entity-contacts/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete contact failed (${res.status})`);
  }
  resetEntityContactsCache();
  resetEntitiesCache();
}

export async function fetchDealDocumentsForDeal(dealId: string): Promise<DealDocumentSummary[]> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/documents/`);
  const data = await handleJson(res);
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.results)
      ? (data as any).results
      : [];
  return rows.map((row: any) => ({
    id: String(row?.id || ''),
    documentTypeId: row?.document_type != null ? String(row.document_type) : '',
    documentTypeName: row?.document_type_name ? String(row.document_type_name) : '',
    fileUrl: row?.file ? String(row.file) : '',
    referenceNumber: row?.reference_number ? String(row.reference_number) : '',
    dateOfDocument: row?.date_of_document ? String(row.date_of_document) : '',
    saleValue: row?.sale_value != null ? String(row.sale_value) : '',
    notes: row?.notes ? String(row.notes) : '',
    visibility: row?.visibility === 'ADMIN' ? 'ADMIN' : 'ALL',
    sharedWithProducer: !!row?.shared_with_producer,
    uploadedByProducer: !!row?.uploaded_by_producer,
    producerStatedAmount: row?.producer_stated_amount != null ? String(row.producer_stated_amount) : '',
    financeRecordType: row?.finance_record_type ? String(row.finance_record_type) : '',
  }));
}

export async function updateDealDocument(id: string, input: UpdateDealDocumentInput): Promise<DealDocumentSummary> {
  const formData = new FormData();
  if (input.documentType !== undefined) formData.append('document_type', input.documentType);
  if (input.file) formData.append('file', input.file);
  if (input.referenceNumber !== undefined) formData.append('reference_number', input.referenceNumber);
  if (input.dateOfDocument !== undefined) formData.append('date_of_document', input.dateOfDocument);
  if (input.saleValue !== undefined) formData.append('sale_value', input.saleValue);
  if (input.notes !== undefined) formData.append('notes', input.notes);
  if (input.visibility !== undefined) formData.append('visibility', input.visibility);
  if (input.sharedWithProducer !== undefined) formData.append('shared_with_producer', input.sharedWithProducer ? 'true' : 'false');

  const res = await authorizedFetch(`/api/deals/documents/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    body: formData,
  });
  const row = await handleJson(res);
  return {
    id: String(row?.id || ''),
    documentTypeId: row?.document_type != null ? String(row.document_type) : '',
    documentTypeName: row?.document_type_name ? String(row.document_type_name) : '',
    fileUrl: row?.file ? String(row.file) : '',
    referenceNumber: row?.reference_number ? String(row.reference_number) : '',
    dateOfDocument: row?.date_of_document ? String(row.date_of_document) : '',
    saleValue: row?.sale_value != null ? String(row.sale_value) : '',
    notes: row?.notes ? String(row.notes) : '',
    visibility: row?.visibility === 'ADMIN' ? 'ADMIN' : 'ALL',
    sharedWithProducer: !!row?.shared_with_producer,
  };
}

export async function deleteDealDocument(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/deals/documents/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete deal document failed (${res.status})`);
  }
}

function mapDocumentIndexRow(row: any): DocumentIndexRow {
  return {
    id: String(row?.id || ''),
    source: (row?.source || 'deal') as DocumentSource,
    sourceLabel: String(row?.source_label || ''),
    documentType: row?.document_type != null ? String(row.document_type) : undefined,
    documentTypeId: row?.document_type_id != null ? String(row.document_type_id) : undefined,
    title: String(row?.title || '(untitled)'),
    dealId: row?.deal_id != null ? String(row.deal_id) : undefined,
    dealNo: row?.deal_no != null ? String(row.deal_no) : undefined,
    primaryProducerId: row?.primary_producer_id != null ? String(row.primary_producer_id) : undefined,
    primaryProducerName: row?.primary_producer_name != null ? String(row.primary_producer_name) : undefined,
    fileUrl: row?.file_url != null ? String(row.file_url) : undefined,
    fileName: row?.file_name != null ? String(row.file_name) : undefined,
    referenceNumber: row?.reference_number != null ? String(row.reference_number) : undefined,
    documentDate: row?.document_date != null ? String(row.document_date) : undefined,
    saleValue: row?.sale_value != null ? String(row.sale_value) : undefined,
    notes: row?.notes != null ? String(row.notes) : undefined,
    visibility: row?.visibility === 'ADMIN' ? 'ADMIN' : 'ALL',
    createdAt: row?.created_at != null ? String(row.created_at) : undefined,
  };
}

/**
 * Cross-app document index. Aggregates deal documents, transport waybills/
 * invoices, wool sale docs, feedlot invoices, and (when requested) ingest
 * data-session files into one paginated, filterable list.
 */
export async function fetchDocuments(params: FetchDocumentsParams = {}): Promise<DocumentIndexResponse> {
  const qs = new URLSearchParams();
  if (params.sources && params.sources.length) qs.set('source', params.sources.join(','));
  if (params.dealId) qs.set('deal_id', params.dealId);
  if (params.documentType) qs.set('document_type', params.documentType);
  if (params.q) qs.set('q', params.q);
  if (params.dateFrom) qs.set('date_from', params.dateFrom);
  if (params.dateTo) qs.set('date_to', params.dateTo);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('page_size', String(params.pageSize));

  const query = qs.toString();
  const res = await authorizedFetch(`/api/documents/${query ? `?${query}` : ''}`);
  const data = await handleJson(res);
  const rows = Array.isArray((data as any)?.results) ? (data as any).results : [];
  return {
    count: Number((data as any)?.count || 0),
    page: Number((data as any)?.page || 1),
    pageSize: Number((data as any)?.page_size || rows.length),
    numPages: Number((data as any)?.num_pages || 1),
    sources: Array.isArray((data as any)?.sources) ? (data as any).sources : [],
    results: rows.map(mapDocumentIndexRow),
  };
}

export async function fetchTransportJourneys(dealId?: string): Promise<TransportJourneySummary[]> {
  const mapRow = (row: any): TransportJourneySummary => ({
    id: String(row?.id || ''),
    dealEventId: row?.deal_event != null ? String(row.deal_event) : '',
    dispatchEventId: row?.dispatch_event != null ? String(row.dispatch_event) : '',
    loadingDate: row?.loading_date ? String(row.loading_date) : '',
    offloadingDate: row?.offloading_date ? String(row.offloading_date) : '',
    vehicleRegistration: row?.vehicle_registration ? String(row.vehicle_registration) : '',
    courierId: row?.courier != null ? String(row.courier) : '',
    waybillNo: row?.waybill_no ? String(row.waybill_no) : '',
    startKm: parseOptionalNumber(row?.start_km),
    endKm: parseOptionalNumber(row?.end_km),
    waybillUrl: row?.waybill ? String(row.waybill) : '',
    invoiceUrl: row?.invoice ? String(row.invoice) : '',
    totalCost: parseOptionalNumber(row?.total_cost),
    weighbridgeRecordId: row?.weighbridge_record_id != null ? String(row.weighbridge_record_id) : undefined,
    weighbridgeGrossWeightKg: row?.weighbridge_gross_weight_kg != null ? String(row.weighbridge_gross_weight_kg) : undefined,
    weighbridgeTareWeightKg: row?.weighbridge_tare_weight_kg != null ? String(row.weighbridge_tare_weight_kg) : undefined,
    weighbridgeNetWeightKg: row?.weighbridge_net_weight_kg != null ? String(row.weighbridge_net_weight_kg) : undefined,
    weighbridgeRecordedAt: row?.weighbridge_recorded_at ? String(row.weighbridge_recorded_at) : undefined,
    documentVisibility: row?.document_visibility === 'ADMIN' ? 'ADMIN' : 'ALL',
    loadingTime: row?.loading_time ? String(row.loading_time) : '',
    loadingPoint: row?.loading_point ? String(row.loading_point) : '',
    offloadingPoint: row?.offloading_point ? String(row.offloading_point) : '',
    driverName: row?.driver_name ? String(row.driver_name) : '',
    driverContact: row?.driver_contact ? String(row.driver_contact) : '',
    animalsDown: parseOptionalNumber(row?.animals_down),
    animalsDownReason: row?.animals_down_reason ? String(row.animals_down_reason) : '',
    removalCertificateUrl: row?.removal_certificate ? String(row.removal_certificate) : '',
  });

  if (!dealId) {
    const res = await authorizedFetch('/api/transport/journeys/');
    const data = await handleJson(res);
    const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
    return rows.map(mapRow);
  }

  const key = dealId;
  const now = Date.now();
  if (now < (transportJourneysCooldownUntil.get(key) || 0)) return transportJourneysLastValue.get(key) || [];
  const inFlight = transportJourneysInFlight.get(key);
  if (inFlight) return inFlight;

  const request = (async () => {
    try {
      const res = await authorizedFetch(`/api/transport/journeys/?deal=${encodeURIComponent(dealId)}`);
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        transportJourneysCooldownUntil.set(key, Date.now() + waitMs);
        return transportJourneysLastValue.get(key) || [];
      }
      const data = await handleJson(res);
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
      const result = rows.map(mapRow);
      transportJourneysLastValue.set(key, result);
      return result;
    } catch {
      return transportJourneysLastValue.get(key) || [];
    } finally {
      transportJourneysInFlight.delete(key);
    }
  })();

  transportJourneysInFlight.set(key, request);
  return request;
}

export async function updateTransportJourney(id: string, input: UpdateTransportJourneyInput): Promise<TransportJourneySummary> {
  const formData = new FormData();
  if (input.dealEventId !== undefined) formData.append('deal_event', input.dealEventId);
  if (input.dispatchEventId !== undefined) formData.append('dispatch_event', input.dispatchEventId ?? '');
  if (input.courierId !== undefined) formData.append('courier', input.courierId);
  if (input.loadingDate !== undefined) formData.append('loading_date', input.loadingDate);
  if (input.offloadingDate !== undefined) formData.append('offloading_date', input.offloadingDate);
  if (input.vehicleRegistration !== undefined) formData.append('vehicle_registration', input.vehicleRegistration);
  if (input.startKm !== undefined) formData.append('start_km', String(input.startKm));
  if (input.endKm !== undefined) formData.append('end_km', String(input.endKm));
  if (input.waybillNumber !== undefined) formData.append('waybill_no', input.waybillNumber);
  if (input.totalCost !== undefined) formData.append('total_cost', String(input.totalCost));
  if (input.waybillFile) formData.append('waybill', input.waybillFile);
  if (input.invoiceFile) formData.append('invoice', input.invoiceFile);
  if (input.documentVisibility !== undefined) formData.append('document_visibility', input.documentVisibility);
  if (input.loadingTime !== undefined) formData.append('loading_time', input.loadingTime);
  if (input.loadingPoint !== undefined) formData.append('loading_point', input.loadingPoint);
  if (input.offloadingPoint !== undefined) formData.append('offloading_point', input.offloadingPoint);
  if (input.driverName !== undefined) formData.append('driver_name', input.driverName);
  if (input.driverContact !== undefined) formData.append('driver_contact', input.driverContact);
  if (input.removalCertFile) formData.append('removal_certificate', input.removalCertFile);
  if (input.animalsDown !== undefined && input.animalsDown !== null) formData.append('animals_down', String(input.animalsDown));
  if (input.animalsDownReason !== undefined) formData.append('animals_down_reason', input.animalsDownReason);

  const res = await authorizedFetch(`/api/transport/journeys/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    body: formData,
  });
  const row = await handleJson(res);
  return {
    id: String(row?.id || ''),
    dealEventId: row?.deal_event != null ? String(row.deal_event) : '',
    dispatchEventId: row?.dispatch_event != null ? String(row.dispatch_event) : '',
    loadingDate: row?.loading_date ? String(row.loading_date) : '',
    offloadingDate: row?.offloading_date ? String(row.offloading_date) : '',
    vehicleRegistration: row?.vehicle_registration ? String(row.vehicle_registration) : '',
    courierId: row?.courier != null ? String(row.courier) : '',
    waybillNo: row?.waybill_no ? String(row.waybill_no) : '',
    startKm: parseOptionalNumber(row?.start_km),
    endKm: parseOptionalNumber(row?.end_km),
    waybillUrl: row?.waybill ? String(row.waybill) : '',
    invoiceUrl: row?.invoice ? String(row.invoice) : '',
    totalCost: parseOptionalNumber(row?.total_cost),
    weighbridgeRecordId: row?.weighbridge_record_id != null ? String(row.weighbridge_record_id) : undefined,
    weighbridgeGrossWeightKg: row?.weighbridge_gross_weight_kg != null ? String(row.weighbridge_gross_weight_kg) : undefined,
    weighbridgeTareWeightKg: row?.weighbridge_tare_weight_kg != null ? String(row.weighbridge_tare_weight_kg) : undefined,
    weighbridgeNetWeightKg: row?.weighbridge_net_weight_kg != null ? String(row.weighbridge_net_weight_kg) : undefined,
    weighbridgeRecordedAt: row?.weighbridge_recorded_at ? String(row.weighbridge_recorded_at) : undefined,
    documentVisibility: row?.document_visibility === 'ADMIN' ? 'ADMIN' : 'ALL',
  };
}

export async function deleteTransportJourney(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/transport/journeys/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete journey failed (${res.status})`);
  }
}

// ---- Weighbridge Records ----

export interface WeighbridgeRecordSummary {
  id: string;
  dealEventId?: string;
  journeyId?: string;
  journeyVehicleRegistration?: string;
  journeyLoadingDate?: string;
  recordedAt?: string;
  grossWeightKg?: string;
  tareWeightKg?: string;
  /** This deal's livestock weight: its share when the weighing covered several deals, else gross − tare. */
  netWeightKg?: string;
  /** What the bridge measured, gross − tare, regardless of how many deals were aboard. */
  truckNetWeightKg?: string;
  /** Same on every record of one weighing that covered several deals. */
  sharedGroup?: string;
  headCount?: number;
  netShareKg?: string;
  notes?: string;
}

export interface WeighbridgeRecordUpsertInput {
  eventId: string;
  recordedAt: string;
  grossWeightKg: string;
  tareWeightKg?: string;
  notes?: string;
  // Optional link to a transport journey. Pass null to clear an existing link.
  journeyId?: string | null;
  /** One truck, several deals: ties the per-deal rows of one weighing together. */
  sharedGroup?: string;
  headCount?: number;
  /** This deal's slice of the truck's net, by head. */
  netShareKg?: string;
}

function mapWeighbridgeRecord(row: any): WeighbridgeRecordSummary {
  return {
    id: String(row?.id || ''),
    dealEventId: row?.deal_event != null ? String(row.deal_event) : undefined,
    journeyId: row?.journey != null ? String(row.journey) : undefined,
    journeyVehicleRegistration: row?.journey_vehicle_registration ? String(row.journey_vehicle_registration) : undefined,
    journeyLoadingDate: row?.journey_loading_date ? String(row.journey_loading_date) : undefined,
    recordedAt: row?.recorded_at ? String(row.recorded_at) : undefined,
    grossWeightKg: row?.gross_weight_kg != null ? String(row.gross_weight_kg) : undefined,
    tareWeightKg: row?.tare_weight_kg != null ? String(row.tare_weight_kg) : undefined,
    netWeightKg: row?.net_weight_kg != null ? String(row.net_weight_kg) : undefined,
    truckNetWeightKg: row?.truck_net_weight_kg != null ? String(row.truck_net_weight_kg) : undefined,
    sharedGroup: row?.shared_group ? String(row.shared_group) : undefined,
    headCount: row?.head_count != null ? Number(row.head_count) : undefined,
    netShareKg: row?.net_share_kg != null ? String(row.net_share_kg) : undefined,
    notes: row?.notes ? String(row.notes) : undefined,
  };
}

export async function fetchWeighbridgeRecords(dealId?: string): Promise<WeighbridgeRecordSummary[]> {
  const url = dealId ? `/api/transport/weighbridge-records/?deal=${encodeURIComponent(dealId)}` : '/api/transport/weighbridge-records/';
  const res = await authorizedFetch(url);
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapWeighbridgeRecord);
}

export async function createWeighbridgeRecord(input: WeighbridgeRecordUpsertInput): Promise<WeighbridgeRecordSummary> {
  const res = await authorizedFetch('/api/transport/weighbridge-records/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event: input.eventId,
      recorded_at: input.recordedAt,
      gross_weight_kg: input.grossWeightKg,
      tare_weight_kg: input.tareWeightKg || null,
      notes: input.notes || '',
      journey: input.journeyId ?? null,
      shared_group: input.sharedGroup ?? null,
      head_count: input.headCount ?? null,
      net_share_kg: input.netShareKg ?? null,
    }),
  });
  const row = await handleJson(res);
  return mapWeighbridgeRecord(row);
}

export async function updateWeighbridgeRecord(id: string, input: WeighbridgeRecordUpsertInput): Promise<WeighbridgeRecordSummary> {
  const res = await authorizedFetch(`/api/transport/weighbridge-records/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event: input.eventId,
      recorded_at: input.recordedAt,
      gross_weight_kg: input.grossWeightKg,
      tare_weight_kg: input.tareWeightKg || null,
      notes: input.notes || '',
      journey: input.journeyId ?? null,
      shared_group: input.sharedGroup ?? null,
      head_count: input.headCount ?? null,
      net_share_kg: input.netShareKg ?? null,
    }),
  });
  const row = await handleJson(res);
  return mapWeighbridgeRecord(row);
}

export async function deleteWeighbridgeRecord(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/transport/weighbridge-records/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
}

export async function fetchWoolFinances(dealId?: string): Promise<WoolFinanceSummary[]> {
  const mapRow = (row: any): WoolFinanceSummary => ({
    id: String(row?.id || ''),
    dealId: row?.deal != null ? String(row.deal) : '',
    documentTypeId: row?.document_type != null ? String(row.document_type) : '',
    documentTypeName: row?.document_type_name ? String(row.document_type_name) : '',
    saleValue: parseOptionalNumber(row?.sale_value),
    saleDate: row?.sale_date ? String(row.sale_date) : '',
    documentScanUrl: row?.document_scan ? String(row.document_scan) : '',
    visibility: row?.visibility === 'ADMIN' ? 'ADMIN' : 'ALL',
    notes: row?.notes ? String(row.notes) : '',
  });

  if (!dealId) {
    const res = await authorizedFetch('/api/wool/finances/');
    const data = await handleJson(res);
    const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
    return rows.map(mapRow);
  }

  const key = dealId;
  const now = Date.now();
  if (now < (woolFinancesCooldownUntil.get(key) || 0)) return woolFinancesLastValue.get(key) || [];
  const inFlight = woolFinancesInFlight.get(key);
  if (inFlight) return inFlight;

  const request = (async () => {
    try {
      const res = await authorizedFetch(`/api/wool/finances/?deal=${encodeURIComponent(dealId)}`);
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        woolFinancesCooldownUntil.set(key, Date.now() + waitMs);
        return woolFinancesLastValue.get(key) || [];
      }
      const data = await handleJson(res);
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
      const result = rows.map(mapRow).filter((r: WoolFinanceSummary) => String(r.dealId) === String(dealId));
      woolFinancesLastValue.set(key, result);
      return result;
    } catch {
      return woolFinancesLastValue.get(key) || [];
    } finally {
      woolFinancesInFlight.delete(key);
    }
  })();

  woolFinancesInFlight.set(key, request);
  return request;
}

export async function updateWoolFinance(id: string, input: UpdateWoolFinanceInput): Promise<WoolFinanceSummary> {
  const formData = new FormData();
  if (input.dealId !== undefined) formData.append('deal', input.dealId);
  if (input.documentTypeId !== undefined) formData.append('document_type', input.documentTypeId);
  if (input.saleValue !== undefined) formData.append('sale_value', String(input.saleValue));
  if (input.saleDate !== undefined) formData.append('sale_date', input.saleDate);
  if (input.file) formData.append('document_scan', input.file);
  if (input.notes !== undefined) formData.append('notes', input.notes);
  if (input.visibility !== undefined) formData.append('visibility', input.visibility);

  const res = await authorizedFetch(`/api/wool/finances/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    body: formData,
  });
  const row = await handleJson(res);
  return {
    id: String(row?.id || ''),
    dealId: row?.deal != null ? String(row.deal) : '',
    documentTypeId: row?.document_type != null ? String(row.document_type) : '',
    saleValue: parseOptionalNumber(row?.sale_value),
    saleDate: row?.sale_date ? String(row.sale_date) : '',
    documentScanUrl: row?.document_scan ? String(row.document_scan) : '',
    visibility: row?.visibility === 'ADMIN' ? 'ADMIN' : 'ALL',
    notes: row?.notes ? String(row.notes) : '',
  };
}

export async function deleteWoolFinance(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/wool/finances/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete wool finance failed (${res.status})`);
  }
}

export async function allocateGln(entityId: string, poolEntityId?: string): Promise<GS1CodeSummary> {
  // The /allocate-gln/ endpoint calls model.full_clean() whose ValidationError
  // is not caught by DRF's handler and always produces a 500. Work around it by:
  // 1. Fetching the next available GLN via GET (no side effects)
  // 2. PATCHing just the entity field via the standard update endpoint (uses
  //    DRF serializer validation, no full_clean() → properly handles errors)
  const next = await fetchNextAvailableGs1Code('GLN', poolEntityId);

  const res = await authorizedFetch(`/api/core/gs1-codes/${encodeURIComponent(next.id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      entity: entityId,
      gs1_type: 'GLN',
      gs1_code: next.gs1Code,
      product_type: null,
      associated_gln: null,
    }),
  });
  const row = await handleJson(res);
  return {
    id: String(row.id),
    gs1Code: String(row.gs1_code || ''),
    gs1Type: String(row.gs1_type || ''),
    entityId: row.entity != null ? String(row.entity) : undefined,
    entityName: row.entity_name || '',
    productTypeId: row.product_type != null ? String(row.product_type) : undefined,
    productTypeName: row.product_type_name || '',
    associatedGlnId: row.associated_gln != null ? String(row.associated_gln) : undefined,
    associatedGlnCode: row.associated_gln_code || '',
  };
}

// Fetch the next GLN pool code that is NOT already owned by targetEntityId.
// Pool codes are stored as gs1_type=GLN with product_type=NULL & associated_gln=NULL.
// We can't use next-available because it also returns the entity's own allocated GLN
// (which still has those fields null). Instead query the full available list and skip
// codes already belonging to the target entity.
export async function fetchNextGtinPoolCode(
  targetEntityId: string,
  poolEntityId?: string,
): Promise<GS1CodeSummary> {
  const params = new URLSearchParams({ gs1_type: 'GLN', available_only: 'true' });
  if (poolEntityId) params.set('entity', poolEntityId);
  const res = await authorizedFetch(`/api/core/gs1-codes/?${params.toString()}`);
  const data = await handleJson(res);
  const rows: any[] = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.results)
      ? (data as any).results
      : [];
  const pool = rows.find((row: any) => String(row.entity) !== targetEntityId);
  if (!pool) {
    throw new Error('No GS1 codes are available in the pool. Ask your AVCS administrator to add more codes.');
  }
  return {
    id: String(pool.id),
    gs1Code: String(pool.gs1_code || ''),
    gs1Type: String(pool.gs1_type || ''),
    entityId: pool.entity != null ? String(pool.entity) : undefined,
    entityName: pool.entity_name || '',
    productTypeId: pool.product_type != null ? String(pool.product_type) : undefined,
    productTypeName: pool.product_type_name || '',
    associatedGlnId: pool.associated_gln != null ? String(pool.associated_gln) : undefined,
    associatedGlnCode: pool.associated_gln_code || '',
  };
}

export async function allocateGtin(input: {
  entityId: string;
  productTypeId: string;
  associatedGlnId?: string;
  poolEntityId?: string;
}): Promise<GS1CodeSummary> {
  // /allocate-gtin/ calls full_clean() which raises a Django ValidationError (not DRF)
  // and always produces a 500. Use the same 2-step workaround as allocateGln:
  // 1. Fetch next available GLN pool code, skipping the entity's own allocated GLN
  // 2. PATCH via the standard update endpoint (no full_clean() — uses DRF serializer)
  const next = await fetchNextGtinPoolCode(input.entityId, input.poolEntityId);

  const res = await authorizedFetch(`/api/core/gs1-codes/${encodeURIComponent(next.id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      entity: input.entityId,
      gs1_type: 'GTIN',
      gs1_code: next.gs1Code,
      product_type: input.productTypeId,
      associated_gln: input.associatedGlnId || null,
    }),
  });
  const row = await handleJson(res);
  return {
    id: String(row.id),
    gs1Code: String(row.gs1_code || ''),
    gs1Type: String(row.gs1_type || ''),
    entityId: row.entity != null ? String(row.entity) : undefined,
    entityName: row.entity_name || '',
    productTypeId: row.product_type != null ? String(row.product_type) : undefined,
    productTypeName: row.product_type_name || '',
    associatedGlnId: row.associated_gln != null ? String(row.associated_gln) : undefined,
    associatedGlnCode: row.associated_gln_code || '',
  };
}

export async function fetchNextAvailableGs1Code(
  gs1Type: 'GLN' | 'GTIN',
  poolEntityId?: string
): Promise<GS1CodeSummary> {
  const params = new URLSearchParams({ gs1_type: gs1Type });
  if (poolEntityId) params.set('pool_entity', poolEntityId);
  const res = await authorizedFetch(`/api/core/gs1-codes/next-available/?${params.toString()}`);
  const row = await handleJson(res);
  return {
    id: String(row.id),
    gs1Code: String(row.gs1_code || ''),
    gs1Type: String(row.gs1_type || ''),
    entityId: row.entity != null ? String(row.entity) : undefined,
    entityName: row.entity_name || '',
    productTypeId: row.product_type != null ? String(row.product_type) : undefined,
    productTypeName: row.product_type_name || '',
    associatedGlnId: row.associated_gln != null ? String(row.associated_gln) : undefined,
    associatedGlnCode: row.associated_gln_code || '',
  };
}

export async function fetchGs1Codes(): Promise<GS1CodeSummary[]> {
  const now = Date.now();
  if (now < gs1CodesCooldownUntil) return gs1CodesLastValue;
  if (gs1CodesInFlight) return gs1CodesInFlight;

  gs1CodesInFlight = (async () => {
    try {
      const res = await authorizedFetch('/api/core/gs1-codes/');
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        gs1CodesCooldownUntil = Date.now() + waitMs;
        return gs1CodesLastValue;
      }
      const data = await handleJson(res);
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
      const result = rows.map(mapGs1Code);
      gs1CodesLastValue = result;
      return result;
    } catch {
      return gs1CodesLastValue;
    } finally {
      gs1CodesInFlight = null;
    }
  })();

  return gs1CodesInFlight;
}

export async function fetchProductTypeOptions(): Promise<SelectOption[]> {
  const now = Date.now();
  if (now < productTypesCooldownUntil) return productTypesLastValue;
  if (productTypesInFlight) return productTypesInFlight;

  productTypesInFlight = (async () => {
    try {
      const res = await authorizedFetch('/api/core/product-types/');
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        productTypesCooldownUntil = Date.now() + waitMs;
        return productTypesLastValue;
      }
      const data = await handleJson(res);
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
      const result = rows
        .map((row: any) => ({
          id: String(row?.id || ''),
          label: String(row?.name || row?.product_type_name || row?.code || row?.id || ''),
        }))
        .filter((row: SelectOption) => row.id && row.label);
      productTypesLastValue = result;
      return result;
    } catch {
      return productTypesLastValue;
    } finally {
      productTypesInFlight = null;
    }
  })();

  return productTypesInFlight;
}

function resetProductTypeOptionsCache() {
  productTypesInFlight = null;
  productTypesLastValue = [];
  productTypesCooldownUntil = 0;
}

// ---- Product Types ----

export interface ProductTypeSummary {
  id: string;
  name: string;
}

export interface ProductTypeUpsertInput {
  name: string;
}

function mapProductType(row: any): ProductTypeSummary {
  return {
    id: String(row?.id || ''),
    name: String(row?.name || ''),
  };
}

export async function fetchProductTypes(): Promise<ProductTypeSummary[]> {
  const res = await authorizedFetch('/api/core/product-types/');
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapProductType);
}

export async function createProductType(input: ProductTypeUpsertInput): Promise<ProductTypeSummary> {
  const res = await authorizedFetch('/api/core/product-types/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: input.name }),
  });
  const row = await handleJson(res);
  resetProductTypeOptionsCache();
  return mapProductType(row);
}

export async function updateProductType(id: string, input: ProductTypeUpsertInput): Promise<ProductTypeSummary> {
  const res = await authorizedFetch(`/api/core/product-types/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: input.name }),
  });
  const row = await handleJson(res);
  resetProductTypeOptionsCache();
  return mapProductType(row);
}

export async function deleteProductType(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/core/product-types/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
  resetProductTypeOptionsCache();
}

export async function createGs1Code(input: GS1CodeUpsertInput): Promise<GS1CodeSummary> {
  const payload = {
    gs1_code: input.gs1Code,
    gs1_type: input.gs1Type,
    entity: input.entityId,
    product_type: input.productTypeId || null,
    associated_gln: input.associatedGlnId || null,
  };

  const res = await authorizedFetch('/api/core/gs1-codes/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const row = await handleJson(res);
  resetGs1CodesCache();
  return mapGs1Code(row);
}

export async function updateGs1Code(id: string, input: GS1CodeUpsertInput): Promise<GS1CodeSummary> {
  const payload = {
    gs1_code: input.gs1Code,
    gs1_type: input.gs1Type,
    entity: input.entityId,
    product_type: input.productTypeId || null,
    associated_gln: input.associatedGlnId || null,
  };

  const res = await authorizedFetch(`/api/core/gs1-codes/${id}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const row = await handleJson(res);
  resetGs1CodesCache();
  return mapGs1Code(row);
}

export async function deleteGs1Code(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/core/gs1-codes/${id}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete failed (${res.status})`);
  }
  resetGs1CodesCache();
}

// ---- Breeds ----

export interface BreedSummary {
  id: string;
  name: string;
  species: 'SHEEP' | 'GOAT' | 'CATTLE';
}

export async function fetchBreeds(): Promise<BreedSummary[]> {
  const res = await authorizedFetch('/api/animals/breeds/');
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map((r: any) => ({ id: String(r.id), name: String(r.name || ''), species: r.species }));
}

export async function createBreed(input: { name: string; species: string }): Promise<BreedSummary> {
  const res = await authorizedFetch('/api/animals/breeds/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const r = await handleJson(res);
  return { id: String(r.id), name: String(r.name || ''), species: r.species };
}

export async function updateBreed(id: string, input: { name: string; species: string }): Promise<BreedSummary> {
  const res = await authorizedFetch(`/api/animals/breeds/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const r = await handleJson(res);
  return { id: String(r.id), name: String(r.name || ''), species: r.species };
}

export async function deleteBreed(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/animals/breeds/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
}

// ---- Value Chains ----

export interface ValueChainSummary {
  id: string;
  name: string;
  isActive: boolean;
  description: string;
  // An AVCS Supply Chain Programme is a value chain with isScp set — one
  // record, not two. The fields below it are programme rules and mean nothing
  // when isScp is false.
  isScp: boolean;
  defaultPurchaserId?: string;
  defaultPurchaserName?: string;
  requiresRfid: boolean;
  requiresGln: boolean;
  /**
   * Every catalogue key resolved to REQUIRED / OPTIONAL / HIDDEN — what this
   * programme demands of its deals. The server resolves the catalogue
   * defaults for us, so this is complete and needs no merging here.
   */
  fieldRequirements: Record<string, FieldRequirementState>;
}

export type FieldRequirementState = 'REQUIRED' | 'OPTIONAL' | 'HIDDEN';

export interface ValueChainUpsertInput {
  name: string;
  isActive?: boolean;
  description?: string;
  isScp?: boolean;
  defaultPurchaserId?: string | null;
  requiresRfid?: boolean;
  requiresGln?: boolean;
  /**
   * A *partial* map. Only the keys sent are applied, so a client that knows
   * about fewer fields than the server cannot reset the rest.
   */
  fieldRequirements?: Record<string, FieldRequirementState>;
}

/** One row of the field matrix, as the catalogue endpoint describes it. */
export interface FieldCatalogueEntry {
  key: string;
  label: string;
  group: string;
  groupLabel: string;
  /** FIELD (a capture field), DOCUMENT (an upload) or RULE (a programme rule). */
  kind: 'FIELD' | 'DOCUMENT' | 'RULE';
  default: FieldRequirementState;
  helpText: string;
  /** Which states this row offers — a RULE offers two, everything else three. */
  states: FieldRequirementState[];
}

export interface FieldCatalogueGroup {
  key: string;
  label: string;
  entries: FieldCatalogueEntry[];
}

function mapValueChain(row: any): ValueChainSummary {
  return {
    id: String(row?.id || ''),
    name: String(row?.name || ''),
    isActive: row?.is_active !== false,
    description: row?.description ? String(row.description) : '',
    isScp: row?.is_scp === true,
    defaultPurchaserId: row?.default_purchaser ? String(row.default_purchaser) : undefined,
    defaultPurchaserName: row?.default_purchaser_name ? String(row.default_purchaser_name) : undefined,
    requiresRfid: row?.requires_rfid !== false,
    requiresGln: row?.requires_gln !== false,
    fieldRequirements: (row?.field_requirements && typeof row.field_requirements === 'object')
      ? (row.field_requirements as Record<string, FieldRequirementState>)
      : {},
  };
}

/**
 * Every capture field, document and rule a programme can demand.
 *
 * Served from backend code rather than stored, so it is fetched rather than
 * mirrored here — a field added to the capture flow appears in the matrix
 * without a frontend release.
 */
export async function fetchFieldCatalogue(): Promise<FieldCatalogueGroup[]> {
  const res = await authorizedFetch('/api/core/value-chains/field-catalogue/');
  const data = await handleJson(res);
  const groups = Array.isArray((data as any)?.groups) ? (data as any).groups : [];
  return groups.map((group: any) => ({
    key: String(group?.key || ''),
    label: String(group?.label || group?.key || ''),
    entries: (Array.isArray(group?.entries) ? group.entries : []).map((entry: any) => ({
      key: String(entry?.key || ''),
      label: String(entry?.label || entry?.key || ''),
      group: String(entry?.group || ''),
      groupLabel: String(entry?.group_label || ''),
      kind: (entry?.kind === 'DOCUMENT' || entry?.kind === 'RULE') ? entry.kind : 'FIELD',
      default: (entry?.default || 'OPTIONAL') as FieldRequirementState,
      helpText: entry?.help_text ? String(entry.help_text) : '',
      states: (Array.isArray(entry?.states) && entry.states.length
        ? entry.states
        : ['REQUIRED', 'OPTIONAL', 'HIDDEN']) as FieldRequirementState[],
    })),
  }));
}

export async function fetchValueChains(): Promise<ValueChainSummary[]> {
  const res = await authorizedFetch('/api/core/value-chains/');
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapValueChain);
}

/** The AVCS SCPs — what the programme selector on the overview offers. */
export async function fetchSupplyChainProgrammes(): Promise<ValueChainSummary[]> {
  const res = await authorizedFetch('/api/core/value-chains/?is_scp=true');
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapValueChain);
}

export async function fetchValueChainOptions(): Promise<SelectOption[]> {
  const now = Date.now();
  if (now < valueChainOptionsCooldownUntil) return valueChainOptionsLastValue;
  if (valueChainOptionsInFlight) return valueChainOptionsInFlight;

  valueChainOptionsInFlight = (async () => {
    try {
      const res = await authorizedFetch('/api/core/value-chains/?is_active=true');
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        valueChainOptionsCooldownUntil = Date.now() + waitMs;
        return valueChainOptionsLastValue;
      }
      const data = await handleJson(res);
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
      const result = rows
        .map((row: any) => ({ id: String(row?.id || ''), label: String(row?.name || row?.id || '') }))
        .filter((row: SelectOption) => row.id && row.label);
      valueChainOptionsLastValue = result;
      return result;
    } catch {
      return valueChainOptionsLastValue;
    } finally {
      valueChainOptionsInFlight = null;
    }
  })();

  return valueChainOptionsInFlight;
}

function resetValueChainOptionsCache() {
  valueChainOptionsInFlight = null;
  valueChainOptionsLastValue = [];
  valueChainOptionsCooldownUntil = 0;
}

/** SCP fields, sent only when supplied so an ordinary edit leaves them alone. */
function valueChainScpPayload(input: ValueChainUpsertInput): Record<string, any> {
  const payload: Record<string, any> = {};
  if (input.isScp !== undefined) payload.is_scp = Boolean(input.isScp);
  if (input.defaultPurchaserId !== undefined) {
    payload.default_purchaser = input.defaultPurchaserId || null;
  }
  if (input.requiresRfid !== undefined) payload.requires_rfid = Boolean(input.requiresRfid);
  if (input.requiresGln !== undefined) payload.requires_gln = Boolean(input.requiresGln);
  // Sent only when non-empty. An empty object would be a no-op server-side,
  // but omitting it keeps an ordinary rename from carrying a matrix payload.
  if (input.fieldRequirements && Object.keys(input.fieldRequirements).length) {
    payload.field_requirements = input.fieldRequirements;
  }
  return payload;
}

export async function createValueChain(input: ValueChainUpsertInput): Promise<ValueChainSummary> {
  const res = await authorizedFetch('/api/core/value-chains/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name,
      is_active: input.isActive !== false,
      description: input.description || '',
      ...valueChainScpPayload(input),
    }),
  });
  const row = await handleJson(res);
  resetValueChainOptionsCache();
  return mapValueChain(row);
}

export async function updateValueChain(id: string, input: ValueChainUpsertInput): Promise<ValueChainSummary> {
  const res = await authorizedFetch(`/api/core/value-chains/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name,
      is_active: input.isActive !== false,
      description: input.description || '',
      ...valueChainScpPayload(input),
    }),
  });
  const row = await handleJson(res);
  resetValueChainOptionsCache();
  return mapValueChain(row);
}

export async function deleteValueChain(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/core/value-chains/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
  resetValueChainOptionsCache();
}

// ---- Deal reporting (v2.2.9) ----

export async function fetchDealBenchmark(dealId: string): Promise<any> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/benchmark/`);
  return handleJson(res);
}

export async function fetchDealAdminReport(dealId: string): Promise<any> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/admin-report/`);
  return handleJson(res);
}

export async function fetchDealCostReports(params?: { dealId?: string; entityId?: string }): Promise<any[]> {
  const query = new URLSearchParams();
  if (params?.dealId) query.set('deal_id', params.dealId);
  if (params?.entityId) query.set('entity_id', params.entityId);
  const qs = query.toString() ? `?${query.toString()}` : '';
  const res = await authorizedFetch(`/api/deals/cost-reports/${qs}`);
  const data = await handleJson(res);
  return Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
}

export async function fetchAnimalCostSummary(animalId: string): Promise<any> {
  const res = await authorizedFetch(`/api/animals/animals/${encodeURIComponent(animalId)}/cost-summary/`);
  return handleJson(res);
}

export async function fetchAnimal(animalId: string): Promise<any> {
  const res = await authorizedFetch(`/api/animals/animals/${encodeURIComponent(animalId)}/`);
  return handleJson(res);
}

export interface AnimalEventRow {
  id: string;
  event_type?: string;
  event_category?: string;
  event_date?: string | null;
  animal_count?: number;
  location_entity_name?: string | null;
  location_gln?: string | null;
  source_entity_name?: string | null;
  destination_entity_name?: string | null;
  data_session?: string | null;
}

export async function fetchAnimalEvents(animalId: string): Promise<AnimalEventRow[]> {
  const res = await authorizedFetch(`/api/animals/animals/${encodeURIComponent(animalId)}/events/`);
  const data = await handleJson(res);
  return Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
}

export interface AnimalPerformance {
  last_weight?: { weight_kg?: number; weigh_date?: string; weight_type?: string } | null;
  feedlot_receive_weight?: number | null;
  feedlot_receive_date?: string | null;
  days_on_feed?: number | null;
  weight_gain_kg?: number | null;
  adg_kg_per_day?: number | null;
  dressing_percentage?: number | null;
  ccm?: number | null;
  last_live_weight?: number | null;
  fcr?: number | null;
  fcr_is_estimated?: boolean;
  fcr_is_deal_level?: boolean;
}

export async function fetchAnimalPerformance(animalId: string): Promise<AnimalPerformance> {
  const res = await authorizedFetch(`/api/animals/animals/${encodeURIComponent(animalId)}/performance/`);
  return handleJson(res) as Promise<AnimalPerformance>;
}

export interface AnimalWeightRecord {
  id: string;
  weight: number;
  weigh_date: string;
  weight_type?: string;
  current_entity?: string | null;
}

export async function fetchAnimalWeightHistory(animalId: string): Promise<AnimalWeightRecord[]> {
  const res = await authorizedFetch(`/api/animals/weight-history/?animal=${encodeURIComponent(animalId)}`);
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map((r: any) => ({
    id: String(r.id),
    weight: Number(r.weight),
    weigh_date: String(r.weigh_date || ''),
    weight_type: r.weight_type ? String(r.weight_type) : undefined,
    current_entity: r.current_entity ?? null,
  }));
}

// ---- Raw ingest provenance (source data rows) ----

export interface RawDataRowProvenance {
  id: string;
  data_session_id?: string | null;
  session_id?: string | null;
  original_filename?: string | null;
  event_type?: string | null;
  deal_id?: string | null;
  deal_no?: string | null;
  row_number?: number | null;
  animal_id?: string | null;
  /** EID the linked animal carries *now* — may differ from this row's own raw EID. */
  animal_eid?: string | null;
  /** True when the linked animal has been soft-deleted (its FK survives the delete). */
  animal_deleted?: boolean | null;
  /** Deal the linked animal currently sits on. */
  animal_deal_id?: string | null;
  /**
   * What was recorded about identifying this animal, present only when the row
   * itself carries no EID. A blank identifier is not always missing data:
   * A260006's slaughter row 53 is blank because the animal was never tagged,
   * and is identified by its Cavalier sequence number and body number instead.
   * Oldest note first.
   */
  animal_identifier_notes?: Array<{ note: string; created_at?: string | null }> | null;
  /**
   * Which revision of its file this row came from. A re-upload creates a new
   * DataSession attached as a revision of the first and leaves the superseded
   * rows in place, so a query by animal returns one row per revision. Without
   * these, a consumer cannot tell a live row from a superseded one.
   */
  revision_number?: number | null;
  is_current_revision?: boolean | null;
  /** Stable identity of the whole revision group (the root session's id). */
  revision_root_id?: string | null;
  is_processed?: boolean;
  processing_errors?: string | null;
  raw_data?: Record<string, any> | null;
  mapped_data?: Record<string, any> | null;
  created_at?: string | null;
}

/** One revision of the file behind an event, from `EventSerializer.revisions`. */
export interface EventRevision {
  data_session: string;
  revision_number: number;
  original_filename?: string | null;
  uploaded_at?: string | null;
  row_count?: number | null;
  is_current: boolean;
}

/**
 * The session to read an event's rows from.
 *
 * An event's `data_session` is the revision *root* and is never re-pointed —
 * ingest attaches the Event to `canonical_session()` so its id survives a
 * re-upload. Reading `data_session` therefore renders revision 1 forever
 * (deal A260006's Slaughter data tab showed revision 1's rows, columns and
 * filename against a file on revision 4). `current_data_session` is the head.
 */
export function eventCurrentSessionId(evt: any): string {
  if (!evt) return '';
  return String(evt.current_data_session || evt.data_session || '');
}

/** The raw ingest row(s) a single animal was produced from, each with its source file. */
export async function fetchAnimalRawRows(animalId: string): Promise<RawDataRowProvenance[]> {
  const res = await authorizedFetch(`/api/animals/animals/${encodeURIComponent(animalId)}/raw-rows/`);
  const data = await handleJson(res);
  return Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
}

// ---- Animals behind a single deal event ----

export interface DealEventAnimal {
  id: string;
  eid?: string | null;
  vid?: string | null;
  species?: string | null;
  sex?: string | null;
  other_id_1?: string | null;
  other_id_2?: string | null;
  current_entity?: string | null;
  current_gln?: string | null;
  status?: string | null;
}

/** The animals linked to one deal event — e.g. which animals a vaccination
 *  event actually covered, rather than just the recorded head count. */
export async function fetchDealEventAnimals(dealId: string, eventId: string): Promise<DealEventAnimal[]> {
  const res = await authorizedFetch(
    `/api/deals/${encodeURIComponent(dealId)}/events/${encodeURIComponent(eventId)}/animals/`,
  );
  const data = (await handleJson(res)) as any;
  return Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
}

// ---- Carcass reconciliation (untagged carcasses <-> unaccounted animals) ----

export interface UnaccountedAnimal {
  id: string;
  eid: string | null;
  vid: string | null;
  species: string | null;
  status: string | null;
  current_entity: string | null;
}

export interface UnmatchedCarcass {
  row_id: string;
  row_number: number;
  file: string;
  invalid_eid: string | null;
  sequence_number: string;
  ccm: string | null;
  hcm: string | null;
  live_weight: string | null;
}

export interface CarcassReconciliation {
  deal_id: string;
  deal_no: string;
  unaccounted_animals: UnaccountedAnimal[];
  unmatched_carcasses: UnmatchedCarcass[];
  /** True when every unaccounted animal can be paired with a measured carcass. */
  can_pair_all: boolean;
}

export interface CarcassReconciliationResult {
  paired: { animal_id: string; vid: string | null; row_id: string | null; is_estimated: boolean }[];
  estimated: { animal_id: string; is_estimated: boolean }[];
  errors: { animal_id?: string; row_id?: string; detail: string }[];
}

export async function fetchCarcassReconciliation(dealId: string): Promise<CarcassReconciliation> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/carcass-reconciliation/`);
  return handleJson(res);
}

/** Apply pairings (measured weights) and/or estimates (flagged, excluded from dressing %). */
export async function applyCarcassReconciliation(
  dealId: string,
  payload: { pairs?: { animal_id: string; row_id: string }[]; estimate_animal_ids?: string[] },
): Promise<CarcassReconciliationResult> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/carcass-reconciliation/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

// ---- EID claims made during ingest (review queue) ----

/** An EID that ingest attached to an animal it matched on VID/other_id. */
export interface EidAssignment {
  id: string;
  animal: string;
  animal_vid: string | null;
  animal_other_id_1: string | null;
  animal_species: string | null;
  animal_deal_id: string | null;
  animal_deal_no: string | null;
  eid: string | null;
  /** Which identifier the animal gained. A merge runs in both directions. */
  claimed_field: 'EID' | 'VID' | 'OTHER_ID';
  claimed_value: string;
  /** Which column identified the animal, read before the claim was applied. */
  matched_on: 'EID' | 'VID' | 'OTHER_ID';
  matched_value: string;
  data_session: string | null;
  session_label: string | null;
  status: 'PENDING' | 'CONFIRMED' | 'REVERTED';
  reviewed_at: string | null;
  reviewed_by_username: string | null;
  created_at: string;
}

export async function fetchEidAssignments(params: { deal?: string; status?: string } = {}): Promise<EidAssignment[]> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => { if (value) query.set(key, value); });
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await authorizedFetch(`/api/ingest/eid-assignments/${suffix}`);
  const data = (await handleJson(res)) as any;
  return Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
}

/** Accept the merge (keeps the tag) or undo it (hands the tag back). */
export async function reviewEidAssignment(id: string, action: 'confirm' | 'revert'): Promise<EidAssignment> {
  const res = await authorizedFetch(
    `/api/ingest/eid-assignments/${encodeURIComponent(id)}/${action}/`,
    { method: 'POST' },
  );
  return handleJson(res);
}

export interface LinkAnimalsResult {
  deal_id: string;
  deal_no: string;
  linked: string[];
  already_on_deal: string[];
  not_found: string[];
  moved_from_other_deal: string[];
}

/** Thrown when an animal is already on another deal — carries what it clashes with. */
export class LinkAnimalConflict extends Error {
  conflicts: { animal_id: string; eid: string | null; current_deal_no: string | null }[];
  constructor(message: string, conflicts: any[]) {
    super(message);
    this.name = 'LinkAnimalConflict';
    this.conflicts = conflicts || [];
  }
}

/** Bring animals into a deal. `force` also moves ones sitting on another deal. */
export async function linkAnimalsToDeal(
  dealId: string,
  animalIds: string[],
  force = false,
): Promise<LinkAnimalsResult> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/link-animal/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ animal_ids: animalIds, force }),
  });
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    throw new LinkAnimalConflict(body?.detail || 'Already on another deal.', body?.conflicts);
  }
  return handleJson(res);
}

// ---- EID reconciliation (link a scanned EID to an un-tagged animal) ----

/** An animal a raw row could be: on the row's deal, carrying no EID yet. */
export interface RowLinkCandidate {
  id: string;
  vid?: string | null;
  other_id_1?: string | null;
  species?: string | null;
  sex?: string | null;
  dob?: string | null;
  status?: string | null;
  current_entity?: string | null;
  created_at?: string | null;
  /**
   * Field names that hold the same value on the row and this animal
   * (`vid`/`sex`/`dob`/`other_id_1`). A hint for the eye only — the backend
   * deliberately does not rank or auto-select, and an empty list is normal.
   */
  agreement: string[];
}

export interface RowLinkCandidatesResponse {
  count: number;
  deal_id: string | null;
  deal_no?: string | null;
  scanned_eid: string;
  results: RowLinkCandidate[];
}

/** Structured failure from the link endpoint, so the UI can tell "already
 *  tagged" apart from "that tag belongs to another animal — merge them". */
export class RowLinkError extends Error {
  conflict?: string;
  holderAnimalId?: string;
  holderDeleted?: boolean;
  constructor(message: string, body?: any) {
    super(message);
    this.name = 'RowLinkError';
    this.conflict = body?.conflict;
    this.holderAnimalId = body?.holder_animal_id;
    this.holderDeleted = body?.holder_deleted;
  }
}

async function rowLinkRequest(path: string, init?: RequestInit): Promise<any> {
  const res = await authorizedFetch(path, init);
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* non-JSON error page */ }
  if (!res.ok) {
    throw new RowLinkError(body?.error || text || `Request failed (${res.status})`, body);
  }
  return body;
}

/** Animals this row's scanned EID could be assigned to. */
export async function fetchRowLinkCandidates(rowId: string): Promise<RowLinkCandidatesResponse> {
  const data = await rowLinkRequest(`/api/ingest/rows/${encodeURIComponent(rowId)}/link-candidates/`);
  return {
    count: Number(data?.count || 0),
    deal_id: data?.deal_id ?? null,
    deal_no: data?.deal_no ?? null,
    scanned_eid: String(data?.scanned_eid || ''),
    results: Array.isArray(data?.results) ? data.results : [],
  };
}

/** Assign the row's scanned EID to `animalId` and rebuild the session's event. */
export async function linkRowToAnimal(rowId: string, animalId: string): Promise<any> {
  return rowLinkRequest(`/api/ingest/rows/${encodeURIComponent(rowId)}/link-animal/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ animal_id: animalId }),
  });
}

/** Undo a manual link: hand the tag back and detach the row. */
export async function unlinkRowFromAnimal(rowId: string): Promise<any> {
  return rowLinkRequest(`/api/ingest/rows/${encodeURIComponent(rowId)}/link-animal/`, {
    method: 'DELETE',
  });
}

export interface SessionRawRowsResponse {
  count: number;
  limit: number;
  session: {
    id: string;
    session_id?: string | null;
    original_filename?: string | null;
    event_type?: string | null;
    deal_id?: string | null;
    deal_no?: string | null;
    row_count?: number | null;
  } | null;
  results: RawDataRowProvenance[];
}

/** Every raw ingest row belonging to one data session (file) — backs the event-history "View raw data" view. */
export async function fetchSessionRawRows(sessionId: string): Promise<SessionRawRowsResponse> {
  const res = await authorizedFetch(`/api/ingest/session/${encodeURIComponent(sessionId)}/rows/`);
  const data = await handleJson(res);
  return {
    count: Number((data as any)?.count || 0),
    limit: Number((data as any)?.limit || 0),
    session: (data as any)?.session ?? null,
    results: Array.isArray((data as any)?.results) ? (data as any).results : [],
  };
}

// ---- Medicines ----

export interface MedicineSummary {
  id: string;
  name: string;
  brandName: string;
  producer: string;
  withholdingPeriod: number;
  sku: string;
  stockQty: number;
}

function mapMedicine(r: any): MedicineSummary {
  return {
    id: String(r.id),
    name: String(r.name || ''),
    brandName: String(r.brand_name || ''),
    producer: String(r.producer || ''),
    withholdingPeriod: Number(r.withholding_period ?? 0),
    sku: String(r.sku || ''),
    stockQty: Number(r.stock_qty ?? 0),
  };
}

export async function fetchMedicines(): Promise<MedicineSummary[]> {
  const res = await authorizedFetch('/api/treatment/medicines/');
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapMedicine);
}

// Medicine.cost is intentionally never sent here -- it's optional on the
// backend master record; actual administration cost is captured per
// vaccination event (batch/dosage/cost), not on the medicine catalog entry.
export async function createMedicine(input: Omit<MedicineSummary, 'id'>): Promise<MedicineSummary> {
  const res = await authorizedFetch('/api/treatment/medicines/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name, brand_name: input.brandName, producer: input.producer,
      withholding_period: input.withholdingPeriod, sku: input.sku,
      stock_qty: input.stockQty,
    }),
  });
  return mapMedicine(await handleJson(res));
}

export async function updateMedicine(id: string, input: Omit<MedicineSummary, 'id'>): Promise<MedicineSummary> {
  const res = await authorizedFetch(`/api/treatment/medicines/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name, brand_name: input.brandName, producer: input.producer,
      withholding_period: input.withholdingPeriod, sku: input.sku,
      stock_qty: input.stockQty,
    }),
  });
  return mapMedicine(await handleJson(res));
}

export async function deleteMedicine(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/treatment/medicines/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
}

// ---- Equipment ----

export interface EquipmentSummary {
  id: string;
  deviceType: string;
  serialNumber: string;
  issueDate: string;
  returnDate: string;
  notes: string;
  isActive: boolean;
  cost: number | null;
  dealId: string | null;
}

function mapEquipment(r: any): EquipmentSummary {
  return {
    id: String(r.id),
    deviceType: String(r.device_type || ''),
    serialNumber: String(r.serial_number || ''),
    issueDate: String(r.issue_date || ''),
    returnDate: String(r.return_date || ''),
    notes: String(r.notes || ''),
    isActive: Boolean(r.is_active),
    cost: r.cost != null ? Number(r.cost) : null,
    dealId: r.deal != null ? String(r.deal) : null,
  };
}

export async function fetchEquipmentList(): Promise<EquipmentSummary[]> {
  const res = await authorizedFetch('/api/finance/equipment/');
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapEquipment);
}

export async function createEquipment(input: Omit<EquipmentSummary, 'id'>): Promise<EquipmentSummary> {
  const res = await authorizedFetch('/api/finance/equipment/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      device_type: input.deviceType, serial_number: input.serialNumber,
      issue_date: input.issueDate, return_date: input.returnDate || null,
      notes: input.notes, is_active: input.isActive,
      cost: input.cost ?? null, deal: input.dealId ?? null,
    }),
  });
  return mapEquipment(await handleJson(res));
}

export async function updateEquipment(id: string, input: Omit<EquipmentSummary, 'id'>): Promise<EquipmentSummary> {
  const res = await authorizedFetch(`/api/finance/equipment/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      device_type: input.deviceType, serial_number: input.serialNumber,
      issue_date: input.issueDate, return_date: input.returnDate || null,
      notes: input.notes, is_active: input.isActive,
      cost: input.cost ?? null, deal: input.dealId ?? null,
    }),
  });
  return mapEquipment(await handleJson(res));
}

export async function deleteEquipment(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/finance/equipment/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
}

// ---- Wool Classes ----

export interface WoolClassSummary {
  id: string;
  code: string;
  name: string;
  description: string;
}

// Document types (core.DocumentType) — the master list the document pickers
// read. `code` is the stable compliance identifier the completion gate
// reasons about (blank for ordinary filing types); `allowedExtensions` is a
// comma-separated list the upload dropzones enforce.
export interface DocumentTypeRecord {
  id: string;
  name: string;
  code: string;
  description: string;
  allowedExtensions: string;
  /** The ledger row a value on this document books (SALE, FEED, PURCHASE …); '' behaves as a sale. */
  financeRecordType: string;
}

function mapDocumentType(row: any): DocumentTypeRecord {
  return {
    id: String(row?.id || ''),
    name: String(row?.name || ''),
    code: String(row?.code || ''),
    description: String(row?.description || ''),
    allowedExtensions: String(row?.allowed_extensions || ''),
    financeRecordType: String(row?.finance_record_type || ''),
  };
}

type DocumentTypeInput = { name: string; code?: string; description?: string; allowedExtensions: string; financeRecordType?: string };
const documentTypePayload = (input: DocumentTypeInput) => ({
  name: input.name,
  code: input.code || '',
  description: input.description || '',
  allowed_extensions: input.allowedExtensions,
  finance_record_type: input.financeRecordType || '',
});

export async function fetchDocumentTypes(): Promise<DocumentTypeRecord[]> {
  const res = await authorizedFetch('/api/core/document-types/');
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapDocumentType);
}

export async function createDocumentType(input: DocumentTypeInput): Promise<DocumentTypeRecord> {
  const res = await authorizedFetch('/api/core/document-types/', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(documentTypePayload(input)),
  });
  return mapDocumentType(await handleJson(res));
}

export async function updateDocumentType(id: string, input: DocumentTypeInput): Promise<DocumentTypeRecord> {
  const res = await authorizedFetch(`/api/core/document-types/${encodeURIComponent(id)}/`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(documentTypePayload(input)),
  });
  return mapDocumentType(await handleJson(res));
}

export async function deleteDocumentType(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/core/document-types/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
}

// Deal feed records (feedlot.FeedlotFinance): what was fed to one deal, the
// kilograms (counted as measured feed for FCR), the cost (mirrored onto the
// ledger as FEED) and the invoice — uploaded, or an already-filed deal document.
export interface DealFeedRecord {
  id: string;
  dealId: string;
  entityId: string | null;
  entityName: string;
  animalsAllocated: number;
  animalDays: number;
  description: string;
  periodStart: string;
  periodEnd: string;
  quantityKg: number | null;
  cost: number;
  invoiceDate: string;
  documentTypeName: string;
  sourceDocumentId: string | null;
  sourceDocumentName: string | null;
  evidenceUrl: string | null;
}

function mapDealFeedRecord(row: any): DealFeedRecord {
  return {
    id: String(row?.id || ''),
    dealId: String(row?.deal || ''),
    entityId: row?.entity ? String(row.entity) : null,
    entityName: String(row?.entity_name || ''),
    animalsAllocated: Number(row?.animals_allocated || 0),
    animalDays: Number(row?.animal_days || 0),
    description: String(row?.description || ''),
    periodStart: String(row?.period_start || ''),
    periodEnd: String(row?.period_end || ''),
    quantityKg: row?.quantity_kg == null ? null : Number(row.quantity_kg),
    cost: Number(row?.feedlot_cost || 0),
    invoiceDate: String(row?.invoice_date || ''),
    documentTypeName: String(row?.document_type_name || ''),
    sourceDocumentId: row?.source_document ? String(row.source_document) : null,
    sourceDocumentName: row?.source_document_name ? String(row.source_document_name) : null,
    evidenceUrl: row?.evidence_url ? String(row.evidence_url) : null,
  };
}

/** Per-animal feed for a deal: the split stored at close, or a provisional one as of today. */
export interface DealFeedAllocationRow {
  animal: string;
  eid: string | null;
  vid: string | null;
  days: number;
  kg: number | null;
  cost: number | null;
  firstWeight: number | null;
  lastWeight: number | null;
  gainKg: number | null;
  fcr: number | null;
  covered: boolean;
}
export interface DealFeedAllocation {
  stored: boolean;
  provisional: boolean;
  asOf: string | null;
  dealIsCompleted: boolean;
  totals: { kg: number; cost: number; gainKg: number; fcr: number | null; animals: number };
  sources: { kind: string; id: string; label: string; entity: string | null; cost: number; kg: number | null; animals: number; animalDays: number }[];
  animals: DealFeedAllocationRow[];
  unallocated: { animal: string; eid: string | null; vid: string | null }[];
}

export async function fetchDealFeedAllocation(dealId: string, options: { preview?: boolean } = {}): Promise<DealFeedAllocation> {
  const suffix = options.preview ? '?preview=1' : '';
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/feed-allocation/${suffix}`);
  const d = await handleJson(res);
  const num = (v: any) => (v === null || v === undefined ? null : Number(v));
  return {
    stored: Boolean(d.stored),
    provisional: Boolean(d.provisional),
    asOf: d.as_of ? String(d.as_of) : null,
    dealIsCompleted: Boolean(d.deal_is_completed),
    totals: { kg: Number(d.totals?.kg || 0), cost: Number(d.totals?.cost || 0), gainKg: Number(d.totals?.gain_kg || 0), fcr: num(d.totals?.fcr), animals: Number(d.totals?.animals || 0) },
    sources: (d.sources || []).map((s: any) => ({ kind: String(s.kind), id: String(s.id), label: String(s.label || ''), entity: s.entity ? String(s.entity) : null, cost: Number(s.cost || 0), kg: num(s.kg), animals: Number(s.animals || 0), animalDays: Number(s.animal_days || 0) })),
    animals: (d.animals || []).map((r: any) => ({
      animal: String(r.animal), eid: r.eid ? String(r.eid) : null, vid: r.vid ? String(r.vid) : null, days: Number(r.days || 0),
      kg: num(r.kg), cost: num(r.cost), firstWeight: num(r.first_weight), lastWeight: num(r.last_weight), gainKg: num(r.gain_kg), fcr: num(r.fcr), covered: Boolean(r.covered),
    })),
    unallocated: (d.unallocated || []).map((r: any) => ({ animal: String(r.animal), eid: r.eid ? String(r.eid) : null, vid: r.vid ? String(r.vid) : null })),
  };
}

export async function fetchDealFeedRecords(dealId: string): Promise<DealFeedRecord[]> {
  const res = await authorizedFetch(`/api/feedlot/finances/?deal_id=${encodeURIComponent(dealId)}`);
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapDealFeedRecord).filter((r: DealFeedRecord) => !r.dealId || r.dealId === dealId);
}

export interface DealFeedRecordInput {
  dealId: string;
  /** Where the feed was eaten; the animals' stays there set the period and the split. */
  entityId?: string;
  description: string;
  periodStart?: string;
  periodEnd?: string;
  quantityKg?: string;
  cost: string;
  invoiceDate: string;
  /** Link a document already filed on the deal… */
  sourceDocumentId?: string;
  /** …or upload a new one with its type. */
  documentTypeId?: string;
  file?: File | null;
  visibility?: 'ALL' | 'ADMIN';
}

export async function createDealFeedRecord(input: DealFeedRecordInput): Promise<DealFeedRecord> {
  const formData = new FormData();
  formData.append('deal', input.dealId);
  if (input.entityId) formData.append('entity', input.entityId);
  formData.append('description', input.description);
  if (input.periodStart) formData.append('period_start', input.periodStart);
  if (input.periodEnd) formData.append('period_end', input.periodEnd);
  if (input.quantityKg) formData.append('quantity_kg', input.quantityKg);
  formData.append('feedlot_cost', input.cost);
  formData.append('invoice_date', input.invoiceDate);
  if (input.sourceDocumentId) formData.append('source_document', input.sourceDocumentId);
  if (input.documentTypeId) formData.append('document_type', input.documentTypeId);
  if (input.file) formData.append('invoice_document', input.file);
  if (input.visibility) formData.append('visibility', input.visibility);
  const res = await authorizedFetch('/api/feedlot/finances/', { method: 'POST', body: formData });
  return mapDealFeedRecord(await handleJson(res));
}

export async function deleteDealFeedRecord(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/feedlot/finances/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
}

export async function fetchWoolClasses(): Promise<WoolClassSummary[]> {
  const res = await authorizedFetch('/api/wool/classes/');
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map((r: any) => ({ id: String(r.id), code: String(r.code || ''), name: String(r.name || ''), description: String(r.description || '') }));
}

export async function createWoolClass(input: { code: string; name: string; description?: string }): Promise<WoolClassSummary> {
  const res = await authorizedFetch('/api/wool/classes/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const r = await handleJson(res);
  return { id: String(r.id), code: String(r.code || ''), name: String(r.name || ''), description: String(r.description || '') };
}

export async function updateWoolClass(id: string, input: { code: string; name: string; description?: string }): Promise<WoolClassSummary> {
  const res = await authorizedFetch(`/api/wool/classes/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const r = await handleJson(res);
  return { id: String(r.id), code: String(r.code || ''), name: String(r.name || ''), description: String(r.description || '') };
}

export async function deleteWoolClass(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/wool/classes/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
}

// ---- Wool Sessions ----

export interface WoolSessionSummary {
  id: string;
  deal: string;
  deal_no?: string;
  session_reference: string;
  batch_id?: string;
  organization_name?: string;
  organization_gln?: string;
  wool_gtin?: string;
  rws_compliance?: string;
  start_date?: string;
  end_date?: string;
  notes?: string;
  baling_status?: 'UNBALED' | 'IN_PROGRESS' | 'BALED';
  baled_at?: string;
  is_baled?: boolean;
  bale_count?: number;
  sheared_animal_count?: number;
}

function mapWoolSession(r: any): WoolSessionSummary {
  return {
    id: String(r.id),
    deal: String(r.deal || ''),
    deal_no: r.deal_no ? String(r.deal_no) : undefined,
    session_reference: String(r.session_reference || ''),
    batch_id: r.batch_id ? String(r.batch_id) : undefined,
    organization_name: r.organization_name ? String(r.organization_name) : undefined,
    organization_gln: r.organization_gln ? String(r.organization_gln) : undefined,
    wool_gtin: r.wool_gtin ? String(r.wool_gtin) : undefined,
    rws_compliance: r.rws_compliance ? String(r.rws_compliance) : undefined,
    start_date: r.start_date ? String(r.start_date) : undefined,
    end_date: r.end_date ? String(r.end_date) : undefined,
    notes: r.notes ? String(r.notes) : undefined,
    baling_status: r.baling_status || undefined,
    baled_at: r.baled_at ? String(r.baled_at) : undefined,
    is_baled: typeof r.is_baled === 'boolean' ? r.is_baled : undefined,
    bale_count: r.bale_count != null ? Number(r.bale_count) : undefined,
    sheared_animal_count: r.sheared_animal_count != null ? Number(r.sheared_animal_count) : undefined,
  };
}

export async function fetchBalingSessions(dealId?: string): Promise<WoolSessionSummary[]> {
  const params = new URLSearchParams({ not_baled: 'true' });
  if (dealId) params.set('deal', dealId);
  const res = await authorizedFetch(`/api/wool/sessions/?${params.toString()}`);
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapWoolSession);
}

export async function fetchWoolSessions(dealId?: string): Promise<WoolSessionSummary[]> {
  if (!dealId) {
    const res = await authorizedFetch('/api/wool/sessions/');
    const data = await handleJson(res);
    const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
    return rows.map(mapWoolSession);
  }

  const key = dealId;
  const now = Date.now();
  if (now < (woolSessionsCooldownUntil.get(key) || 0)) return woolSessionsLastValue.get(key) || [];
  const inFlight = woolSessionsInFlight.get(key);
  if (inFlight) return inFlight;

  const request = (async () => {
    try {
      const res = await authorizedFetch(`/api/wool/sessions/?deal=${encodeURIComponent(dealId)}`);
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        woolSessionsCooldownUntil.set(key, Date.now() + waitMs);
        return woolSessionsLastValue.get(key) || [];
      }
      const data = await handleJson(res);
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
      const result = rows.map(mapWoolSession);
      woolSessionsLastValue.set(key, result);
      return result;
    } catch {
      return woolSessionsLastValue.get(key) || [];
    } finally {
      woolSessionsInFlight.delete(key);
    }
  })();

  woolSessionsInFlight.set(key, request);
  return request;
}

export async function createWoolSession(input: { deal: string; session_reference: string; organization_name?: string; start_date?: string; end_date?: string; notes?: string }): Promise<WoolSessionSummary> {
  const res = await authorizedFetch('/api/wool/sessions/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return mapWoolSession(await handleJson(res));
}

// ---- Wool Bales ----

export interface WoolBaleSummary {
  id: string;
  deal?: string;
  session?: string;
  session_reference?: string;
  batch_id?: string;
  bale_number: string;
  bale_weight?: number;
  cleaned_weight?: number;
  tare_weight?: number;
  gross_weight?: number;
  net_weight?: number;
  wool_class?: string;
  wool_class_code?: string;
  wool_class_name?: string;
  wool_breed?: string;
  wool_breed_name?: string;
  notes?: string;
  date_created?: string;
}

function mapWoolBale(r: any): WoolBaleSummary {
  return {
    id: String(r.id),
    deal: r.deal ? String(r.deal) : undefined,
    session: r.session ? String(r.session) : undefined,
    session_reference: r.session_reference ? String(r.session_reference) : undefined,
    batch_id: r.batch_id ? String(r.batch_id) : undefined,
    bale_number: String(r.bale_number || ''),
    bale_weight: r.bale_weight != null ? Number(r.bale_weight) : undefined,
    cleaned_weight: r.cleaned_weight != null ? Number(r.cleaned_weight) : undefined,
    tare_weight: r.tare_weight != null ? Number(r.tare_weight) : undefined,
    gross_weight: r.gross_weight != null ? Number(r.gross_weight) : undefined,
    net_weight: r.net_weight != null ? Number(r.net_weight) : undefined,
    wool_class: r.wool_class ? String(r.wool_class) : undefined,
    wool_class_code: r.wool_class_code ? String(r.wool_class_code) : undefined,
    wool_class_name: r.wool_class_name ? String(r.wool_class_name) : undefined,
    wool_breed: r.wool_breed ? String(r.wool_breed) : undefined,
    wool_breed_name: r.wool_breed_name ? String(r.wool_breed_name) : undefined,
    notes: r.notes ? String(r.notes) : undefined,
    date_created: r.date_created ? String(r.date_created) : undefined,
  };
}

export async function fetchWoolBales(sessionId?: string): Promise<WoolBaleSummary[]> {
  const url = sessionId ? `/api/wool/bales/?session=${encodeURIComponent(sessionId)}` : '/api/wool/bales/';
  const res = await authorizedFetch(url);
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapWoolBale);
}

export interface WoolBaleInput {
  deal?: string;
  session: string;
  bale_number?: string;
  bale_weight?: number;
  cleaned_weight?: number;
  tare_weight?: number;
  wool_class?: string;
  wool_breed?: string;
  notes?: string;
}

export async function createWoolBale(input: WoolBaleInput): Promise<WoolBaleSummary> {
  const res = await authorizedFetch('/api/wool/bales/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return mapWoolBale(await handleJson(res));
}

export async function updateWoolBale(id: string, input: Partial<WoolBaleInput>): Promise<WoolBaleSummary> {
  const res = await authorizedFetch(`/api/wool/bales/${encodeURIComponent(id)}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return mapWoolBale(await handleJson(res));
}

export async function deleteWoolBale(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/wool/bales/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Delete failed (${res.status})`); }
}

export interface WoolBalePrintRow {
  id: string;
  bale_number: string;
  gross_weight?: number | null;
  net_weight?: number | null;
  tare_weight?: number | null;
  wool_class_code?: string;
  wool_class_name?: string;
  wool_breed?: string;
  qr_value: string;
  qr_png_data_uri: string;
}

export interface WoolBalePrintSheet {
  session_id: string;
  deal_no: string;
  general: {
    organization_name?: string;
    organization_gln?: string;
    wool_gtin?: string;
    rws_compliance?: string;
  };
  traceability: {
    session_reference?: string;
    batch_id?: string;
    start_date?: string | null;
    end_date?: string | null;
    baling_status?: string;
    baled_at?: string | null;
  };
  bales: WoolBalePrintRow[];
  totals: { gross_weight: number; net_weight: number; bale_count: number };
}

export async function fetchBalePrintSheet(sessionId: string): Promise<WoolBalePrintSheet> {
  const res = await authorizedFetch(`/api/wool/sessions/${encodeURIComponent(sessionId)}/print-sheet/`);
  return handleJson(res) as Promise<WoolBalePrintSheet>;
}

export async function finishBaling(sessionId: string): Promise<WoolSessionSummary & { virtual_routez?: any }> {
  const res = await authorizedFetch(`/api/wool/sessions/${encodeURIComponent(sessionId)}/finish-baling/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  return handleJson(res) as Promise<WoolSessionSummary & { virtual_routez?: any }>;
}

// ---- Admin user management ----

export interface UserSummary {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  force_password_change: boolean;
  has_rmis_token?: boolean;
  has_rmis_test_token?: boolean;
  has_rmis_production_token?: boolean;
  activation_pending?: boolean;
  activation_email_sent?: boolean;
}

export async function fetchAdminUsers(): Promise<UserSummary[]> {
  const now = Date.now();
  if (now < adminUsersCooldownUntil) return adminUsersLastValue;
  if (adminUsersInFlight) return adminUsersInFlight;

  adminUsersInFlight = (async () => {
    try {
      const res = await authorizedFetch('/api/auth/users/');
      if (res.status === 429) {
        const retryAfterSeconds = Number(res.headers.get('Retry-After') || 0);
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 30000;
        adminUsersCooldownUntil = Date.now() + waitMs;
        return adminUsersLastValue;
      }
      const result = await handleJson(res);
      adminUsersLastValue = result;
      return result;
    } catch {
      return adminUsersLastValue;
    } finally {
      adminUsersInFlight = null;
    }
  })();

  return adminUsersInFlight;
}

export async function createAdminUser(input: {
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
  entity_contact?: string;
  rmis_test_token?: string;
  rmis_production_token?: string;
}): Promise<UserSummary> {
  const res = await authorizedFetch('/api/auth/users/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handleJson(res);
}

export async function adminSendActivation(id: number): Promise<{ detail: string }> {
  const res = await authorizedFetch(`/api/auth/users/${id}/send-activation/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return handleJson(res);
}

export async function adminSetUserPassword(id: number, newPassword: string): Promise<void> {
  const res = await authorizedFetch(`/api/auth/users/${id}/set-password/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_password: newPassword }),
  });
  await handleJson(res);
}

export async function adminUpdateUser(
  id: number,
  input: {
    email?: string;
    first_name?: string;
    last_name?: string;
    is_active?: boolean;
    is_staff?: boolean;
    rmis_test_token?: string;
    rmis_production_token?: string;
  },
): Promise<UserSummary> {
  const res = await authorizedFetch(`/api/auth/users/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handleJson(res);
}

// --- Virtual Routez entity provisioning -------------------------------------
// Standing an entity up on Virtual Routez was manual, and the cost of that
// showed: two producers were configured by hand with event UIDs derived from a
// convention nobody checked against the partner, and 233 of their events were
// rejected without anyone seeing it. The backend now builds the profile, its
// event UIDs and the partner-side workstations/person from one template.

/** `status` is the one to branch on. A row merely *existing* says nothing
 *  about whether it is right: Damspruit's rows all exist and all carry UIDs
 *  the partner rejects. */
export type VrzEventStatus = 'will_add' | 'matches' | 'differs';

export type VrzPlannedEventUid = {
  eventType: string;
  /** What the template says it should be. */
  eventUid: string;
  workstationUid: string;
  /** What is stored today; empty when no row exists. */
  currentEventUid: string;
  currentWorkstationUid: string;
  status: VrzEventStatus;
  alreadyConfigured: boolean;
};

export type VrzProvisionPlan = {
  provisionable: boolean;
  reason?: string;
  entity?: string;
  entityType?: string;
  profileExists?: boolean;
  workstationUid?: string;
  personUid?: string;
  contact?: string | null;
  workstations?: string[];
  eventUids?: VrzPlannedEventUid[];
};

export type VrzProvisionStep = { step: string; status: string; detail: string };

export type VrzProvisionResult = {
  ok: boolean;
  reason?: string;
  profileId?: string;
  steps: VrzProvisionStep[];
};

function mapVrzPlan(data: any): VrzProvisionPlan {
  if (!data?.provisionable) {
    return { provisionable: false, reason: String(data?.reason || 'This entity cannot be provisioned.') };
  }
  return {
    provisionable: true,
    reason: data.reason,
    entity: data.entity,
    entityType: data.entity_type,
    profileExists: Boolean(data.profile_exists),
    workstationUid: data.workstation_uid || '',
    personUid: data.person_uid || '',
    contact: data.contact ?? null,
    workstations: Array.isArray(data.workstations) ? data.workstations.map(String) : [],
    eventUids: Array.isArray(data.event_uids)
      ? data.event_uids.map((row: any) => ({
          eventType: String(row.event_type || ''),
          eventUid: String(row.event_uid || ''),
          workstationUid: String(row.workstation_uid || ''),
          currentEventUid: String(row.current_event_uid || ''),
          currentWorkstationUid: String(row.current_workstation_uid || ''),
          status: (row.status || 'will_add') as VrzEventStatus,
          alreadyConfigured: Boolean(row.already_configured),
        }))
      : [],
  };
}

/** What provisioning would create. Does not contact Virtual Routez, so it is
 *  safe to call just to render a confirmation. */
export async function fetchVrzProvisionPlan(entityId: string): Promise<VrzProvisionPlan> {
  const res = await authorizedFetch(
    `/api/integration/virtual-routez/entities/${encodeURIComponent(entityId)}/provision/`,
  );
  return mapVrzPlan(await handleJson(res));
}

/** Provision the entity. `createRemote: false` does the AVCS-side wiring only,
 *  for an entity whose Virtual Routez tenant key is not in hand yet.
 *
 *  The endpoint answers 207 when some steps failed and some succeeded — a
 *  partial result is still useful, so that is read as a result, not an error. */
export async function provisionEntityOnVrz(
  entityId: string,
  options: { createRemote?: boolean; apiKey?: string; realign?: boolean; overwriteApiKey?: boolean } = {},
): Promise<VrzProvisionResult> {
  const res = await authorizedFetch(
    `/api/integration/virtual-routez/entities/${encodeURIComponent(entityId)}/provision/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        create_remote: options.createRemote !== false,
        ...(options.realign ? { realign: true } : {}),
        ...(options.apiKey ? { api_key: options.apiKey } : {}),
        ...(options.overwriteApiKey ? { overwrite_api_key: true } : {}),
      }),
    },
  );

  if (!res.ok && res.status !== 207) {
    const text = await res.text();
    throw new Error(text || `Virtual Routez provisioning failed (${res.status})`);
  }

  const data = await res.json();
  return {
    ok: Boolean(data?.ok),
    reason: data?.reason,
    profileId: data?.profile_id,
    steps: Array.isArray(data?.steps)
      ? data.steps.map((s: any) => ({
          step: String(s.step || ''),
          status: String(s.status || ''),
          detail: String(s.detail || ''),
        }))
      : [],
  };
}

// --- Virtual Routez onboarding: request email + partner-facing link --------
// Org creation and API-key issuing on Virtual Routez is a manual, partner-staff
// process -- an admin there creates the org from a template and generates the
// key by hand. These functions cover the AVCS-side request/track half
// (authenticated) and the partner-facing link itself (unauthenticated: the
// Virtual Routez staff opening it have no AVCS login).

export type VrzOnboardingStatus = 'NOT_REQUESTED' | 'REQUESTED' | 'KEY_RECEIVED' | 'VERIFIED';

export type VrzConnectionTestResult = {
  ok: boolean;
  message?: string;
  onboardingStatus?: VrzOnboardingStatus;
  lastVerifiedAt?: string | null;
  lastVerificationError?: string;
  /** A successful verification revokes the onboarding link, so the page cannot
   *  re-read itself afterwards and must render the result it already holds. */
  linkClosed?: boolean;
};

function mapVrzConnectionTestResult(data: any): VrzConnectionTestResult {
  return {
    ok: Boolean(data?.ok),
    message: data?.message || data?.detail,
    onboardingStatus: data?.onboarding_status,
    lastVerifiedAt: data?.last_verified_at ?? null,
    lastVerificationError: data?.last_verification_error || '',
    linkClosed: Boolean(data?.link_closed),
  };
}

export type VrzEntityOnboardingProfile = {
  hasApiKey: boolean;
  onboardingStatus: VrzOnboardingStatus;
  onboardingRequestedAt: string | null;
  lastVerifiedAt: string | null;
  lastVerificationError: string;
};

/** The entity's Virtual Routez onboarding state, or `null` if nothing has
 *  been configured for it yet (no profile exists at all). */
export async function fetchVrzEntityOnboardingProfile(entityId: string): Promise<VrzEntityOnboardingProfile | null> {
  const res = await authorizedFetch(
    `/api/integration/virtual-routez/profiles/?entity=${encodeURIComponent(entityId)}`,
  );
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : (data?.results || []);
  const row = rows[0];
  if (!row) return null;
  return {
    hasApiKey: Boolean(row.has_api_key),
    onboardingStatus: row.onboarding_status || 'NOT_REQUESTED',
    onboardingRequestedAt: row.onboarding_requested_at || null,
    lastVerifiedAt: row.last_verified_at || null,
    lastVerificationError: row.last_verification_error || '',
  };
}

/** Test whether an entity's configured Virtual Routez API key actually works. */
export async function testVrzConnection(entityId: string): Promise<VrzConnectionTestResult> {
  const res = await authorizedFetch(
    `/api/integration/virtual-routez/entities/${encodeURIComponent(entityId)}/test-connection/`,
    { method: 'POST' },
  );
  return mapVrzConnectionTestResult(await handleJson(res));
}

export type VrzOnboardingRequestResult = {
  ok: boolean;
  onboardingStatus: VrzOnboardingStatus;
  onboardingRequestedAt?: string;
  recipientEmail: string;
  onboardingUrl: string;
  linkExpiresAt: string;
  emailSent: boolean;
  emailError?: string;
};

/** Kick off the manual onboarding handshake: stamps the entity REQUESTED,
 *  issues a fresh partner-facing link, and emails it (with the entity's
 *  details) to Virtual Routez staff. */
export async function requestVrzOnboarding(
  entityId: string,
  options: { recipientEmail?: string } = {},
): Promise<VrzOnboardingRequestResult> {
  const res = await authorizedFetch(
    `/api/integration/virtual-routez/entities/${encodeURIComponent(entityId)}/request-onboarding/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options.recipientEmail ? { recipient_email: options.recipientEmail } : {}),
    },
  );
  const data = await handleJson(res);
  return {
    ok: Boolean(data?.ok),
    onboardingStatus: data?.onboarding_status,
    onboardingRequestedAt: data?.onboarding_requested_at,
    recipientEmail: String(data?.recipient_email || ''),
    onboardingUrl: String(data?.onboarding_url || ''),
    linkExpiresAt: String(data?.link_expires_at || ''),
    emailSent: Boolean(data?.email_sent),
    emailError: data?.email_error || '',
  };
}

export type VrzOnboardingGtin = { code: string; productType: string };

export type VrzOnboardingLinkInfo = {
  entityName: string;
  entityType: string;
  entityNo: string;
  registeredBusinessName: string;
  farmName: string;
  /** The Supply Chain Programme this party belongs to. */
  supplyChain: string;
  addressLines: string[];
  latitude: string;
  longitude: string;
  glns: string[];
  gtins: VrzOnboardingGtin[];
  /** Fields AVCS does not hold, named so the partner does not invent them. */
  missing: string[];
  hasApiKey: boolean;
  onboardingStatus: VrzOnboardingStatus;
  expiresAt: string;
};

/** Virtual Routez staff have no AVCS account and no context for a raw stack
 *  trace or a proxy's HTML 404 page -- both are realistic failure modes for
 *  this specific link (an environment where the route is not deployed yet,
 *  or a misconfigured proxy), so unlike the rest of this file's error
 *  handling, a non-JSON body here must never reach the screen verbatim. */
function extractPublicApiErrorDetail(raw: string): string {
  const trimmed = (raw || '').trim();
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed.detail === 'string') return parsed.detail;
  } catch {
    // not JSON -- fall through
  }
  if (trimmed.startsWith('<') || trimmed.toLowerCase().startsWith('<!doctype')) {
    return 'The server could not be reached. Please try again shortly, or ask AVCS for a new link.';
  }
  return trimmed;
}

/** Unauthenticated: the Virtual Routez staff member opening the link from the
 *  onboarding request email has no AVCS login, so this uses a plain `fetch`
 *  rather than `authorizedFetch` (same pattern as password reset). */
export async function fetchVrzOnboardingLink(token: string): Promise<VrzOnboardingLinkInfo> {
  const res = await fetch(`${BASE}/api/integration/virtual-routez/onboarding/${encodeURIComponent(token)}/`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(extractPublicApiErrorDetail(text) || `This link is invalid or has expired (${res.status}).`);
  }
  const data = await res.json();
  return {
    entityName: String(data.entity_name || ''),
    entityType: String(data.entity_type || ''),
    entityNo: String(data.entity_no || ''),
    registeredBusinessName: String(data.registered_business_name || ''),
    farmName: String(data.farm_name || ''),
    supplyChain: String(data.supply_chain || ''),
    addressLines: Array.isArray(data.address_lines) ? data.address_lines.map(String) : [],
    latitude: String(data.latitude || ''),
    longitude: String(data.longitude || ''),
    glns: Array.isArray(data.glns) ? data.glns.map(String) : [],
    gtins: Array.isArray(data.gtins)
      ? data.gtins.map((g: any) => ({ code: String(g?.code || ''), productType: String(g?.product_type || '') }))
      : [],
    missing: Array.isArray(data.missing) ? data.missing.map(String) : [],
    hasApiKey: Boolean(data.has_api_key),
    onboardingStatus: data.onboarding_status,
    expiresAt: String(data.expires_at || ''),
  };
}

export async function submitVrzOnboardingKey(
  token: string,
  apiKey: string,
): Promise<{ onboardingStatus: VrzOnboardingStatus }> {
  const res = await fetch(`${BASE}/api/integration/virtual-routez/onboarding/${encodeURIComponent(token)}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ api_key: apiKey }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(extractPublicApiErrorDetail(text) || `Could not save the key (${res.status}).`);
  }
  const data = await res.json();
  return { onboardingStatus: data.onboarding_status };
}

/** Same read-only Virtual Routez check as the admin "Test connection" button,
 *  offered here so partner staff get instant feedback that their copy-paste
 *  actually worked, without waiting to hear back from AVCS. */
export async function testVrzOnboardingLink(token: string): Promise<VrzConnectionTestResult> {
  const res = await fetch(`${BASE}/api/integration/virtual-routez/onboarding/${encodeURIComponent(token)}/test/`, {
    method: 'POST',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(extractPublicApiErrorDetail(text) || `Could not test the key (${res.status}).`);
  }
  return mapVrzConnectionTestResult(await res.json());
}


// =============================================================================
// Shared feedlot costs (split across deals by animal-days) and the per-animal
// cost model
// =============================================================================

export type SharedFinanceBasis = 'ANIMAL_DAYS' | 'HEADCOUNT' | 'RECEIVED_BATCH' | 'SESSION_ANIMALS' | 'EVENT_ANIMALS';
const SHARED_FINANCE_BASES: SharedFinanceBasis[] = ['ANIMAL_DAYS', 'HEADCOUNT', 'RECEIVED_BATCH', 'SESSION_ANIMALS', 'EVENT_ANIMALS'];

/** One event a shared record can be split over (the EVENT_ANIMALS picker). */
export interface SharedFinanceEventOption {
  id: string;
  dealId: string | null;
  dealNo: string | null;
  dealName: string | null;
  code: string;
  eventDate: string | null;
  animalCount: number;
  entityName: string | null;
}

function mapSharedFinanceEvent(row: any): SharedFinanceEventOption {
  return {
    id: String(row.id),
    dealId: row.deal ? String(row.deal) : null,
    dealNo: row.deal_no ? String(row.deal_no) : null,
    dealName: row.deal_name ? String(row.deal_name) : null,
    code: String(row.code || ''),
    eventDate: row.event_date ? String(row.event_date) : null,
    animalCount: Number(row.animal_count || 0),
    entityName: row.entity_name ? String(row.entity_name) : null,
  };
}

/**
 * Events an invoice can be split over. Without `dealId`/`entityId` the newest
 * 300 events with animals across every deal come back, so a transport invoice
 * covering several deals' loads can pick across them.
 */
export async function fetchSharedFinanceEvents(params: { dealId?: string; entityId?: string; codes?: string[]; start?: string; end?: string } = {}): Promise<SharedFinanceEventOption[]> {
  const query = new URLSearchParams();
  if (params.dealId) query.set('deal', params.dealId);
  if (params.entityId) query.set('entity', params.entityId);
  if (params.codes && params.codes.length > 0) query.set('codes', params.codes.join(','));
  if (params.start) query.set('start', params.start);
  if (params.end) query.set('end', params.end);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await authorizedFetch(`/api/feedlot/shared-finances/events/${suffix}`);
  const data = await handleJson(res);
  return (Array.isArray(data) ? data : []).map(mapSharedFinanceEvent);
}

export interface SharedFinanceAllocationRow {
  id: string;
  dealId: string;
  dealNo: string;
  dealName: string;
  units: number;
  animalCount: number;
  share: number;
  amount: number;
  quantityKg: number | null;
}

export interface SharedFinanceRecord {
  id: string;
  /** Empty for a record split by events, which needs no entity. */
  entityId: string;
  entityName: string;
  recordType: string;
  recordTypeLabel: string;
  amount: number;
  quantityKg: number | null;
  invoiceDate: string;
  periodStart: string;
  periodEnd: string;
  basis: SharedFinanceBasis;
  basisLabel: string;
  basisSessions: { id: string; label: string; eventType: string }[];
  /** For the EVENT_ANIMALS basis: the events whose animals share the record. */
  basisEvents: SharedFinanceEventOption[];
  /** The already-filed deal document the record points at, if any. */
  sourceDocumentId: string | null;
  sourceDocumentUrl: string | null;
  sourceDocumentName: string | null;
  description: string;
  referenceNumber: string;
  documentUrl: string | null;
  allocatedAt: string | null;
  allocationTotalUnits: number | null;
  allocations: SharedFinanceAllocationRow[];
  /**
   * PROVISIONAL means the money is captured in full but each deal's share is
   * a running animal-days figure, recomputed as of `provisionalAsOf` and
   * locked to FINAL once every deal it charges has closed.
   */
  allocationState: 'FINAL' | 'PROVISIONAL';
  allocationStateLabel: string;
  isProvisional: boolean;
  /** False for a record split over named sessions or events: already settled. */
  canSplitProvisionally: boolean;
  provisionalAsOf: string | null;
  /** One line of ready wording for a provisional record; null when final. */
  provisionalNote: string | null;
  /** Deals on the split that have not closed -- what keeps it provisional. */
  openDealCount: number;
  /** Set by the server when the record saved but reached no deal. */
  warning?: string;
}

export interface SharedFinancePreviewRow {
  dealId: string;
  dealNo: string | null;
  dealName: string | null;
  units: number;
  animalCount: number;
  share: number;
  amount: number | null;
  quantityKg: number | null;
}

export interface SharedFinancePreview {
  totalUnits: number;
  unallocatedUnits: number;
  unallocatedAnimals: number;
  rows: SharedFinancePreviewRow[];
}

export interface SharedFinanceInput {
  /** Optional for the EVENT_ANIMALS basis. */
  entityId?: string;
  recordType: string;
  amount: string;
  quantityKg?: string;
  invoiceDate: string;
  /** Derived from the events when omitted for the EVENT_ANIMALS basis. */
  periodStart?: string;
  periodEnd?: string;
  basis: SharedFinanceBasis;
  /** For the SESSION_ANIMALS basis: the recorded sessions whose animals share the record. */
  basisSessionIds?: string[];
  /** For the EVENT_ANIMALS basis: the events whose animals share the record. */
  basisEventIds?: string[];
  /** An already-filed deal document the record came from — instead of uploading the file again. */
  sourceDocumentId?: string;
  description: string;
  referenceNumber?: string;
  document?: File | null;
}

export interface SharedFinanceSessionOption {
  id: string;
  label: string;
  eventTypes: string[];
  eventDate: string | null;
  animalCount: number;
  deals: Record<string, number>;
}

export async function fetchSharedFinanceSessions(params: { entityId: string; start?: string; end?: string }): Promise<SharedFinanceSessionOption[]> {
  const query = new URLSearchParams({ entity: params.entityId });
  if (params.start) query.set('start', params.start);
  if (params.end) query.set('end', params.end);
  const res = await authorizedFetch(`/api/feedlot/shared-finances/sessions/?${query.toString()}`);
  const data = await handleJson(res);
  return (Array.isArray(data) ? data : []).map((row: any) => ({
    id: String(row.id),
    label: String(row.label || ''),
    eventTypes: Array.isArray(row.event_types) ? row.event_types.map(String) : [],
    eventDate: row.event_date ? String(row.event_date) : null,
    animalCount: Number(row.animal_count || 0),
    deals: row.deals || {},
  }));
}

function mapSharedAllocation(row: any): SharedFinanceAllocationRow {
  return {
    id: String(row.id),
    dealId: String(row.deal),
    dealNo: String(row.deal_no || ''),
    dealName: String(row.deal_name || ''),
    units: Number(row.units || 0),
    animalCount: Number(row.animal_count || 0),
    share: Number(row.share || 0),
    amount: Number(row.amount || 0),
    quantityKg: row.quantity_kg === null || row.quantity_kg === undefined ? null : Number(row.quantity_kg),
  };
}

function mapSharedFinance(row: any): SharedFinanceRecord {
  return {
    id: String(row.id),
    entityId: row.entity ? String(row.entity) : '',
    entityName: String(row.entity_name || ''),
    recordType: String(row.record_type || ''),
    recordTypeLabel: String(row.record_type_display || row.record_type || ''),
    amount: Number(row.amount || 0),
    quantityKg: row.quantity_kg === null || row.quantity_kg === undefined ? null : Number(row.quantity_kg),
    invoiceDate: String(row.invoice_date || ''),
    periodStart: String(row.period_start || ''),
    periodEnd: String(row.period_end || ''),
    basis: (SHARED_FINANCE_BASES.includes(String(row.basis) as SharedFinanceBasis) ? (row.basis as SharedFinanceBasis) : 'ANIMAL_DAYS'),
    basisSessions: Array.isArray(row.basis_session_labels)
      ? row.basis_session_labels.map((x: any) => ({ id: String(x.id), label: String(x.label || ''), eventType: String(x.event_type || '') }))
      : [],
    basisEvents: Array.isArray(row.basis_event_labels) ? row.basis_event_labels.map(mapSharedFinanceEvent) : [],
    sourceDocumentId: row.source_document ? String(row.source_document) : null,
    sourceDocumentUrl: row.source_document_url ? String(row.source_document_url) : null,
    sourceDocumentName: row.source_document_name ? String(row.source_document_name) : null,
    basisLabel: String(row.basis_display || ''),
    description: String(row.description || ''),
    referenceNumber: String(row.reference_number || ''),
    documentUrl: row.document ? String(row.document) : null,
    allocatedAt: row.allocated_at ? String(row.allocated_at) : null,
    allocationTotalUnits: row.allocation_total_units === null || row.allocation_total_units === undefined ? null : Number(row.allocation_total_units),
    allocations: Array.isArray(row.allocations) ? row.allocations.map(mapSharedAllocation) : [],
    allocationState: row.allocation_state === 'PROVISIONAL' ? 'PROVISIONAL' : 'FINAL',
    allocationStateLabel: String(row.allocation_state_display || ''),
    isProvisional: Boolean(row.is_provisional),
    canSplitProvisionally: row.can_split_provisionally === undefined ? Boolean(row.entity) : Boolean(row.can_split_provisionally),
    provisionalAsOf: row.provisional_as_of ? String(row.provisional_as_of) : null,
    provisionalNote: row.provisional_note ? String(row.provisional_note) : null,
    openDealCount: Number(row.open_deal_count || 0),
    warning: row.warning ? String(row.warning) : undefined,
  };
}

export async function fetchSharedFinances(params: { entityId?: string; dealId?: string } = {}): Promise<SharedFinanceRecord[]> {
  const query = new URLSearchParams();
  if (params.entityId) query.set('entity', params.entityId);
  if (params.dealId) query.set('deal', params.dealId);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await authorizedFetch(`/api/feedlot/shared-finances/${suffix}`);
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : (data?.results || []);
  return rows.map(mapSharedFinance);
}

export async function previewSharedFinance(input: Omit<SharedFinanceInput, 'description' | 'invoiceDate' | 'recordType' | 'document' | 'referenceNumber'>): Promise<SharedFinancePreview> {
  const res = await authorizedFetch('/api/feedlot/shared-finances/preview/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      entity: input.entityId || null,
      basis: input.basis,
      period_start: input.periodStart || null,
      period_end: input.periodEnd || null,
      amount: input.amount,
      quantity_kg: input.quantityKg ? input.quantityKg : null,
      basis_sessions: input.basisSessionIds || [],
      basis_events: input.basisEventIds || [],
    }),
  });
  const data = await handleJson(res);
  return {
    totalUnits: Number(data.total_units || 0),
    unallocatedUnits: Number(data.unallocated_units || 0),
    unallocatedAnimals: Number(data.unallocated_animals || 0),
    rows: (data.rows || []).map((row: any) => ({
      dealId: String(row.deal),
      dealNo: row.deal_no ? String(row.deal_no) : null,
      dealName: row.deal_name ? String(row.deal_name) : null,
      units: Number(row.units || 0),
      animalCount: Number(row.animal_count || 0),
      share: Number(row.share || 0),
      amount: row.amount === null || row.amount === undefined ? null : Number(row.amount),
      quantityKg: row.quantity_kg === null || row.quantity_kg === undefined ? null : Number(row.quantity_kg),
    })),
  };
}

export async function createSharedFinance(input: SharedFinanceInput): Promise<SharedFinanceRecord> {
  const formData = new FormData();
  if (input.entityId) formData.append('entity', input.entityId);
  formData.append('record_type', input.recordType);
  formData.append('amount', input.amount);
  if (input.quantityKg) formData.append('quantity_kg', input.quantityKg);
  formData.append('invoice_date', input.invoiceDate);
  if (input.periodStart) formData.append('period_start', input.periodStart);
  if (input.periodEnd) formData.append('period_end', input.periodEnd);
  formData.append('basis', input.basis);
  (input.basisSessionIds || []).forEach((id) => formData.append('basis_sessions', id));
  (input.basisEventIds || []).forEach((id) => formData.append('basis_events', id));
  if (input.sourceDocumentId) formData.append('source_document', input.sourceDocumentId);
  formData.append('description', input.description);
  if (input.referenceNumber) formData.append('reference_number', input.referenceNumber);
  if (input.document) formData.append('document', input.document);
  const res = await authorizedFetch('/api/feedlot/shared-finances/', { method: 'POST', body: formData });
  return mapSharedFinance(await handleJson(res));
}

export async function deleteSharedFinance(id: string): Promise<void> {
  const res = await authorizedFetch(`/api/feedlot/shared-finances/${encodeURIComponent(id)}/`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) {
    const text = await res.text();
    throw new Error(text || `Delete failed (${res.status})`);
  }
}

/** Recompute on the record's stored basis and lock the split as final. */
export async function reallocateSharedFinance(id: string): Promise<SharedFinanceRecord> {
  const res = await authorizedFetch(`/api/feedlot/shared-finances/${encodeURIComponent(id)}/reallocate/`, { method: 'POST' });
  return mapSharedFinance(await handleJson(res));
}

/**
 * Split by animal-days as of `asOf` (today when omitted) and mark the result
 * provisional -- for an invoice that arrived while animals are still on site.
 */
export async function splitSharedFinanceProvisionally(id: string, asOf?: string): Promise<SharedFinanceRecord> {
  const res = await authorizedFetch(`/api/feedlot/shared-finances/${encodeURIComponent(id)}/provisional/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(asOf ? { as_of: asOf } : {}),
  });
  return mapSharedFinance(await handleJson(res));
}

export interface CostModelSource {
  kind: 'ledger' | 'document' | 'wool_sale' | 'medication' | 'shared' | 'journey' | 'feedlot_invoice' | 'shearing_session' | 'estimate' | string;
  label: string;
  reference: string | null;
  date: string | null;
  amount: number | null;
  documentUrl: string | null;
  documentName: string | null;
  detail: string | null;
}

export interface CostModelLine {
  key: string;
  label: string;
  group: 'cost' | 'income';
  total: number;
  perAnimal: number | null;
  sources: CostModelSource[];
}

export interface DealCostModel {
  dealId: string;
  dealNo: string;
  animalCount: number;
  animals: { active: number; slaughtered: number; sold: number; deceased: number };
  totals: {
    cost: number;
    costPerAnimal: number | null;
    income: number;
    incomePerAnimal: number | null;
    margin: number;
    marginPerAnimal: number | null;
    marginPercent: number | null;
  };
  costLines: CostModelLine[];
  incomeLines: CostModelLine[];
  performance: {
    avgDaysInFeedlot: number | null;
    avgAdgKg: number | null;
    totalWeightGainKg: number | null;
    weightGainPerAnimalKg: number | null;
    feedConsumedKg: number | null;
    feedPerAnimalKg: number | null;
    feedCostPerKg: number | null;
    feedCostPerKgGain: number | null;
    fcr: number | null;
    fcrEstimated: number | null;
    fcrIsEstimated: boolean;
    dailyFeedlotFeePerAnimal: number | null;
    dressingPercentage: number | null;
    avgCarcassWeightKg: number | null;
    carcassesWeighed: number;
    carcassesFromBatchLiveMass: number;
    carcassIncomePerKg: number | null;
  };
}

const numOrNull = (value: unknown): number | null => (value === null || value === undefined || value === '' ? null : Number(value));

function mapCostModelLine(row: any): CostModelLine {
  return {
    key: String(row.key),
    label: String(row.label),
    group: row.group === 'income' ? 'income' : 'cost',
    total: Number(row.total || 0),
    perAnimal: numOrNull(row.per_animal),
    sources: (row.sources || []).map((src: any) => ({
      kind: String(src.kind || 'ledger'),
      label: String(src.label || ''),
      reference: src.reference ? String(src.reference) : null,
      date: src.date ? String(src.date) : null,
      amount: numOrNull(src.amount),
      documentUrl: src.document_url ? String(src.document_url) : null,
      documentName: src.document_name ? String(src.document_name) : null,
      detail: src.detail ? String(src.detail) : null,
    })),
  };
}

export async function fetchDealCostModel(dealId: string): Promise<DealCostModel> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/cost-model/`);
  const data = await handleJson(res);
  const p = data.performance || {};
  const t = data.totals || {};
  return {
    dealId: String(data.deal_id),
    dealNo: String(data.deal_no || ''),
    animalCount: Number(data.animal_count || 0),
    animals: {
      active: Number(data.animals?.active || 0),
      slaughtered: Number(data.animals?.slaughtered || 0),
      sold: Number(data.animals?.sold || 0),
      deceased: Number(data.animals?.deceased || 0),
    },
    totals: {
      cost: Number(t.cost || 0),
      costPerAnimal: numOrNull(t.cost_per_animal),
      income: Number(t.income || 0),
      incomePerAnimal: numOrNull(t.income_per_animal),
      margin: Number(t.margin || 0),
      marginPerAnimal: numOrNull(t.margin_per_animal),
      marginPercent: numOrNull(t.margin_percent),
    },
    costLines: (data.cost_lines || []).map(mapCostModelLine),
    incomeLines: (data.income_lines || []).map(mapCostModelLine),
    performance: {
      avgDaysInFeedlot: numOrNull(p.avg_days_in_feedlot),
      avgAdgKg: numOrNull(p.avg_adg_kg),
      totalWeightGainKg: numOrNull(p.total_weight_gain_kg),
      weightGainPerAnimalKg: numOrNull(p.weight_gain_per_animal_kg),
      feedConsumedKg: numOrNull(p.feed_consumed_kg),
      feedPerAnimalKg: numOrNull(p.feed_per_animal_kg),
      feedCostPerKg: numOrNull(p.feed_cost_per_kg),
      feedCostPerKgGain: numOrNull(p.feed_cost_per_kg_gain),
      fcr: numOrNull(p.fcr),
      fcrEstimated: numOrNull(p.fcr_estimated),
      fcrIsEstimated: Boolean(p.fcr_is_estimated),
      dailyFeedlotFeePerAnimal: numOrNull(p.daily_feedlot_fee_per_animal),
      dressingPercentage: numOrNull(p.dressing_percentage),
      avgCarcassWeightKg: numOrNull(p.avg_carcass_weight_kg),
      carcassesWeighed: Number(p.carcasses_weighed || 0),
      carcassesFromBatchLiveMass: Number(p.carcasses_from_batch_live_mass || 0),
      carcassIncomePerKg: numOrNull(p.carcass_income_per_kg),
    },
  };
}

// --- Margin summary: the cost model pooled across the deals a dashboard filter selects ---

export interface MarginSummaryDealRow {
  dealId: string;
  dealNo: string;
  dealReferenceNo: string | null;
  primaryProducer: string | null;
  valueChain: string | null;
  species: string | null;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  animalCount: number;
  animals: { active: number; slaughtered: number; sold: number; deceased: number };
  avgAdgKg: number | null;
  avgDaysInFeedlot: number | null;
  fcr: number | null;
  fcrEstimated: number | null;
  fcrIsEstimated: boolean;
  dressingPercentage: number | null;
  feedCost: number;
  feedCostPerAnimal: number | null;
  woolIncome: number;
  woolIncomePerAnimal: number | null;
  costPerAnimal: number | null;
  incomePerAnimal: number | null;
  margin: number;
  marginPerAnimal: number | null;
  marginPercent: number | null;
}

export interface MarginSummary {
  dealCount: number;
  animalCount: number;
  animals: { active: number; slaughtered: number; sold: number; deceased: number };
  totals: {
    cost: number;
    costPerAnimal: number | null;
    income: number;
    incomePerAnimal: number | null;
    margin: number;
    marginPerAnimal: number | null;
    marginPercent: number | null;
    feedCost: number;
    feedCostPerAnimal: number | null;
    woolIncome: number;
    woolIncomePerAnimal: number | null;
  };
  /** Same keys as the deal cost model's lines, pooled; no per-line sources. */
  costLines: Omit<CostModelLine, 'sources'>[];
  incomeLines: Omit<CostModelLine, 'sources'>[];
  performance: {
    avgDaysInFeedlot: number | null;
    avgAdgKg: number | null;
    adgCount: number;
    totalWeightGainKg: number | null;
    weightGainPerAnimalKg: number | null;
    feedConsumedKg: number | null;
    feedPerAnimalKg: number | null;
    feedCostPerKg: number | null;
    feedCostPerKgGain: number | null;
    fcr: number | null;
    fcrEstimated: number | null;
    fcrIsEstimated: boolean;
    dressingPercentage: number | null;
    avgCarcassWeightKg: number | null;
    carcassesWeighed: number;
    carcassIncomePerKg: number | null;
  };
  deals: MarginSummaryDealRow[];
  scope: { includeClosed: boolean };
}

function mapPooledLine(row: any): Omit<CostModelLine, 'sources'> {
  return {
    key: String(row.key),
    label: String(row.label),
    group: row.group === 'income' ? 'income' : 'cost',
    total: Number(row.total || 0),
    perAnimal: numOrNull(row.per_animal),
  };
}

/**
 * `GET /api/deals/margin-summary/` — Willem's programme view. Takes the same
 * filter bar as `fetchSystemStats`. Active deals only unless `includeClosed`;
 * a named `dealId` reports that deal whatever its status.
 */
export async function fetchMarginSummary(params?: {
  species?: string;
  valueChainId?: string;
  scpMatrix?: boolean;
  dealId?: string;
  includeClosed?: boolean;
  /** Deals whose `deal_date` falls in this window (inclusive, YYYY-MM-DD). */
  startedFrom?: string;
  startedTo?: string;
}): Promise<MarginSummary> {
  const qs = new URLSearchParams();
  if (params?.species) qs.set('species', params.species);
  if (params?.valueChainId) qs.set('value_chain', params.valueChainId);
  if (params?.scpMatrix) qs.set('scp', 'matrix');
  if (params?.dealId) qs.set('deal', params.dealId);
  if (params?.includeClosed) qs.set('include_closed', 'true');
  if (params?.startedFrom) qs.set('started_from', params.startedFrom);
  if (params?.startedTo) qs.set('started_to', params.startedTo);
  const query = qs.toString();
  const res = await authorizedFetch(`/api/deals/margin-summary/${query ? `?${query}` : ''}`);
  const data = await handleJson(res);
  const t = data.totals || {};
  const p = data.performance || {};
  const animals = (raw: any) => ({
    active: Number(raw?.active || 0),
    slaughtered: Number(raw?.slaughtered || 0),
    sold: Number(raw?.sold || 0),
    deceased: Number(raw?.deceased || 0),
  });
  return {
    dealCount: Number(data.deal_count || 0),
    animalCount: Number(data.animal_count || 0),
    animals: animals(data.animals),
    totals: {
      cost: Number(t.cost || 0),
      costPerAnimal: numOrNull(t.cost_per_animal),
      income: Number(t.income || 0),
      incomePerAnimal: numOrNull(t.income_per_animal),
      margin: Number(t.margin || 0),
      marginPerAnimal: numOrNull(t.margin_per_animal),
      marginPercent: numOrNull(t.margin_percent),
      feedCost: Number(t.feed_cost || 0),
      feedCostPerAnimal: numOrNull(t.feed_cost_per_animal),
      woolIncome: Number(t.wool_income || 0),
      woolIncomePerAnimal: numOrNull(t.wool_income_per_animal),
    },
    costLines: (data.cost_lines || []).map(mapPooledLine),
    incomeLines: (data.income_lines || []).map(mapPooledLine),
    performance: {
      avgDaysInFeedlot: numOrNull(p.avg_days_in_feedlot),
      avgAdgKg: numOrNull(p.avg_adg_kg),
      adgCount: Number(p.adg_count || 0),
      totalWeightGainKg: numOrNull(p.total_weight_gain_kg),
      weightGainPerAnimalKg: numOrNull(p.weight_gain_per_animal_kg),
      feedConsumedKg: numOrNull(p.feed_consumed_kg),
      feedPerAnimalKg: numOrNull(p.feed_per_animal_kg),
      feedCostPerKg: numOrNull(p.feed_cost_per_kg),
      feedCostPerKgGain: numOrNull(p.feed_cost_per_kg_gain),
      fcr: numOrNull(p.fcr),
      fcrEstimated: numOrNull(p.fcr_estimated),
      fcrIsEstimated: Boolean(p.fcr_is_estimated),
      dressingPercentage: numOrNull(p.dressing_percentage),
      avgCarcassWeightKg: numOrNull(p.avg_carcass_weight_kg),
      carcassesWeighed: Number(p.carcasses_weighed || 0),
      carcassIncomePerKg: numOrNull(p.carcass_income_per_kg),
    },
    deals: (data.deals || []).map((row: any): MarginSummaryDealRow => ({
      dealId: String(row.deal_id),
      dealNo: String(row.deal_no || ''),
      dealReferenceNo: row.deal_reference_no ? String(row.deal_reference_no) : null,
      primaryProducer: row.primary_producer ? String(row.primary_producer) : null,
      valueChain: row.value_chain ? String(row.value_chain) : null,
      species: row.species ? String(row.species) : null,
      status: row.status === 'COMPLETED' || row.status === 'CANCELLED' ? row.status : 'ACTIVE',
      animalCount: Number(row.animal_count || 0),
      animals: animals(row.animals),
      avgAdgKg: numOrNull(row.avg_adg_kg),
      avgDaysInFeedlot: numOrNull(row.avg_days_in_feedlot),
      fcr: numOrNull(row.fcr),
      fcrEstimated: numOrNull(row.fcr_estimated),
      fcrIsEstimated: Boolean(row.fcr_is_estimated),
      dressingPercentage: numOrNull(row.dressing_percentage),
      feedCost: Number(row.feed_cost || 0),
      feedCostPerAnimal: numOrNull(row.feed_cost_per_animal),
      woolIncome: Number(row.wool_income || 0),
      woolIncomePerAnimal: numOrNull(row.wool_income_per_animal),
      costPerAnimal: numOrNull(row.cost_per_animal),
      incomePerAnimal: numOrNull(row.income_per_animal),
      margin: Number(row.margin || 0),
      marginPerAnimal: numOrNull(row.margin_per_animal),
      marginPercent: numOrNull(row.margin_percent),
    })),
    scope: { includeClosed: Boolean(data.scope?.include_closed) },
  };
}

// --- Deal close-out overrides -------------------------------------------------
// Closing a deal past its compliance gate with a superuser's approval. The
// request snapshots the gaps open at the time; approval waives only those.

export type CompletionOverrideStatus = 'PENDING' | 'APPROVED' | 'DECLINED';

export interface DealCompletionOverride {
  id: string;
  dealId: string;
  dealNo: string;
  status: CompletionOverrideStatus;
  statusLabel: string;
  reason: string;
  waivedRequirements: DealDocumentRequirement[];
  requestedById: string | null;
  requestedByName: string | null;
  requestedAt: string | null;
  decidedById: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string;
  notifiedEmails: string[];
  appliedAt: string | null;
}

export interface CompletionOverrideReview extends DealCompletionOverride {
  canDecide: boolean;
  dealSummary: {
    dealNo: string;
    dealReferenceNo: string | null;
    primaryProducer: string | null;
    purchaser: string | null;
    valueChain: string | null;
    dealDate: string | null;
    totalAnimals: number | null;
    status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  };
}

export function mapCompletionOverride(row: any): DealCompletionOverride {
  const status: CompletionOverrideStatus =
    row?.status === 'APPROVED' || row?.status === 'DECLINED' ? row.status : 'PENDING';
  return {
    id: String(row?.id ?? ''),
    dealId: row?.deal != null ? String(row.deal) : '',
    dealNo: String(row?.deal_no ?? ''),
    status,
    statusLabel: String(row?.status_label ?? status),
    reason: String(row?.reason ?? ''),
    waivedRequirements: Array.isArray(row?.waived_requirements)
      ? row.waived_requirements.map((r: any) => ({
          party: r?.party === 'SELLER' || r?.party === 'PURCHASER' ? r.party : '',
          code: String(r?.code ?? ''),
          label: String(r?.label ?? r?.code ?? ''),
          reason: String(r?.reason ?? ''),
          satisfied: Boolean(r?.satisfied),
          kind: r?.kind === 'FIELD' ? 'FIELD' : 'DOCUMENT',
          waived: true,
        }))
      : [],
    requestedById: row?.requested_by != null ? String(row.requested_by) : null,
    requestedByName: row?.requested_by_name ? String(row.requested_by_name) : null,
    requestedAt: row?.requested_at ? String(row.requested_at) : null,
    decidedById: row?.decided_by != null ? String(row.decided_by) : null,
    decidedByName: row?.decided_by_name ? String(row.decided_by_name) : null,
    decidedAt: row?.decided_at ? String(row.decided_at) : null,
    decisionNote: String(row?.decision_note ?? ''),
    notifiedEmails: Array.isArray(row?.notified_emails) ? row.notified_emails.map(String) : [],
    appliedAt: row?.applied_at ? String(row.applied_at) : null,
  };
}

/** A deal's override history, newest first. */
export async function fetchDealCompletionOverrides(dealId: string): Promise<DealCompletionOverride[]> {
  const res = await authorizedFetch(`/api/deals/completion-overrides/?deal=${encodeURIComponent(dealId)}`);
  const data = await handleJson(res);
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
  return rows.map(mapCompletionOverride);
}

/** Ask to close the deal without its outstanding requirements. Emails every superuser. */
export async function requestDealCompletionOverride(dealId: string, reason: string): Promise<DealCompletionOverride> {
  const res = await authorizedFetch('/api/deals/completion-overrides/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deal: dealId, reason }),
  });
  return mapCompletionOverride(await handleJson(res));
}

/** What a review link from the approval email points at. 404 when bad or expired. */
export async function fetchCompletionOverrideByToken(token: string): Promise<CompletionOverrideReview> {
  const res = await authorizedFetch(`/api/deals/completion-overrides/by-token/?token=${encodeURIComponent(token)}`);
  const data: any = await handleJson(res);
  const d = data?.deal_summary || {};
  return {
    ...mapCompletionOverride(data),
    canDecide: data?.can_decide === true,
    dealSummary: {
      dealNo: String(d.deal_no ?? ''),
      dealReferenceNo: d.deal_reference_no ? String(d.deal_reference_no) : null,
      primaryProducer: d.primary_producer ? String(d.primary_producer) : null,
      purchaser: d.purchaser ? String(d.purchaser) : null,
      valueChain: d.value_chain ? String(d.value_chain) : null,
      dealDate: d.deal_date ? String(d.deal_date) : null,
      totalAnimals: d.total_animals != null ? Number(d.total_animals) : null,
      status: d.status === 'COMPLETED' || d.status === 'CANCELLED' ? d.status : 'ACTIVE',
    },
  };
}

/** Superusers only. Approve waives exactly the snapshotted requirements. */
export async function decideCompletionOverride(
  overrideId: string,
  decision: 'APPROVED' | 'DECLINED',
  note: string,
): Promise<DealCompletionOverride> {
  const res = await authorizedFetch(`/api/deals/completion-overrides/${encodeURIComponent(overrideId)}/decide/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision, note }),
  });
  return mapCompletionOverride(await handleJson(res));
}

// --- Animal re-tag / EID replacement -------------------------------------

export type RetagReason = 'LOST' | 'DAMAGED' | 'MIS_SCAN' | 'OTHER';

export const RETAG_REASONS: { value: RetagReason; label: string }[] = [
  { value: 'LOST', label: 'Tag lost' },
  { value: 'DAMAGED', label: 'Tag damaged or unreadable' },
  { value: 'MIS_SCAN', label: 'Mis-scan corrected' },
  { value: 'OTHER', label: 'Other' },
];

export interface AnimalRetagRecord {
  id: string;
  animalId: string;
  animalDealNo: string | null;
  oldEid: string;
  newEid: string;
  retagDate: string;
  reason: RetagReason;
  reasonLabel: string;
  notes: string;
  /** The duplicate record the new EID had created, absorbed and soft-deleted. */
  mergedAnimalId: string | null;
  mergedEventCount: number;
  mergedWeightCount: number;
  fromSuggestion: boolean;
}

function mapAnimalRetag(row: any): AnimalRetagRecord {
  return {
    id: String(row.id),
    animalId: String(row.animal || ''),
    animalDealNo: row.animal_deal_no ? String(row.animal_deal_no) : null,
    oldEid: String(row.old_eid || ''),
    newEid: String(row.new_eid || ''),
    retagDate: String(row.retag_date || ''),
    reason: (row.reason || 'LOST') as RetagReason,
    reasonLabel: String(row.reason_display || ''),
    notes: String(row.notes || ''),
    mergedAnimalId: row.merged_animal_id ? String(row.merged_animal_id) : null,
    mergedEventCount: Number(row.merged_event_count || 0),
    mergedWeightCount: Number(row.merged_weight_count || 0),
    fromSuggestion: Boolean(row.from_suggestion),
  };
}

/** One (missing animal, new EID) pair the detector thinks is the same animal. */
export interface RetagCandidate {
  missingAnimalId: string;
  missingEid: string;
  missingVid: string | null;
  lastSeen: string | null;
  lastWeight: number | null;
  adgUsed: number | null;
  adgSource: string;
  newAnimalId: string;
  newEid: string;
  newFirstSeen: string | null;
  observedWeight: number | null;
  expectedWeight: number | null;
  deviationKg: number | null;
  score: number;
  reasons: string[];
}

const num = (value: any): number | null => (value === null || value === undefined ? null : Number(value));

function mapRetagCandidate(row: any): RetagCandidate {
  return {
    missingAnimalId: String(row.missing_animal_id || ''),
    missingEid: String(row.missing_eid || ''),
    missingVid: row.missing_vid ? String(row.missing_vid) : null,
    lastSeen: row.last_seen ? String(row.last_seen) : null,
    lastWeight: num(row.last_weight),
    adgUsed: num(row.adg_used),
    adgSource: String(row.adg_source || ''),
    newAnimalId: String(row.new_animal_id || ''),
    newEid: String(row.new_eid || ''),
    newFirstSeen: row.new_first_seen ? String(row.new_first_seen) : null,
    observedWeight: num(row.observed_weight),
    expectedWeight: num(row.expected_weight),
    deviationKg: num(row.deviation_kg),
    score: Number(row.score || 0),
    reasons: Array.isArray(row.reasons) ? row.reasons.map((r: any) => String(r)) : [],
  };
}

/**
 * Replace an animal's EID, keeping its history. If the new EID already created
 * its own animal record, that record is absorbed and soft-deleted.
 */
export async function retagAnimal(
  animalId: string,
  input: { newEid: string; retagDate?: string; reason?: RetagReason; notes?: string; fromSuggestion?: boolean },
): Promise<AnimalRetagRecord> {
  const res = await authorizedFetch(`/api/animals/animals/${encodeURIComponent(animalId)}/retag/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      new_eid: input.newEid,
      retag_date: input.retagDate || undefined,
      reason: input.reason || undefined,
      notes: input.notes || undefined,
      from_suggestion: input.fromSuggestion || undefined,
    }),
  });
  return mapAnimalRetag(await handleJson(res));
}

/** An animal's tag history, oldest tag first. */
export async function fetchAnimalRetags(animalId: string): Promise<AnimalRetagRecord[]> {
  const res = await authorizedFetch(`/api/animals/animals/${encodeURIComponent(animalId)}/retags/`);
  const data = await handleJson(res);
  return (Array.isArray(data) ? data : []).map(mapAnimalRetag);
}

/** Likely re-tags nobody has recorded yet. Read-only: every row is confirmed by hand. */
export async function fetchRetagCandidates(
  params: { dealId?: string; since?: string; maxDeviationKg?: number; limit?: number } = {},
): Promise<{ count: number; maxDeviationKg: string; candidates: RetagCandidate[] }> {
  const query = new URLSearchParams();
  if (params.dealId) query.set('deal', params.dealId);
  if (params.since) query.set('since', params.since);
  if (params.maxDeviationKg !== undefined) query.set('max_deviation_kg', String(params.maxDeviationKg));
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await authorizedFetch(`/api/animals/animals/retag-candidates/${suffix}`);
  const data = await handleJson(res);
  return {
    count: Number(data?.count || 0),
    maxDeviationKg: String(data?.max_deviation_kg || ''),
    candidates: Array.isArray(data?.candidates) ? data.candidates.map(mapRetagCandidate) : [],
  };
}

// --- Ownership flow -------------------------------------------------------

export interface OwnershipParty {
  id: string;
  name: string;
  entityType: string;
  entityTypeOther: string;
  registeredBusinessName: string;
}

export interface OwnershipOwner extends OwnershipParty {
  /** Null on the first owner: the deal does not say when they acquired them. */
  ownedFrom: string | null;
  /** Null on the current owner. */
  ownedUntil: string | null;
  /**
   * What this party still holds: what came in, less what it passed on. Not
   * always zero for a middle party -- animals that came in and never went out
   * are the ones that died on its watch. Null when a head count was never
   * stated, since guessing zero would claim a holding nobody counted.
   */
  currentlyOwned: number | null;
  /** Carcasses off the line. Only set for an abattoir owner. */
  carcassCount: number | null;
}

export interface OwnershipHopTransfer {
  id: string;
  transferDate: string;
  headCount: number | null;
  notes: string;
  isDerived: boolean;
  documentName: string | null;
  documentUrl: string | null;
}

/** One handover relationship. Batched sales between the same pair collapse here. */
export interface OwnershipHop {
  fromEntity: OwnershipParty;
  toEntity: OwnershipParty;
  firstDate: string;
  lastDate: string;
  /** Null when no handover under this hop stated one. */
  headCount: number | null;
  transferCount: number;
  isDerived: boolean;
  transfers: OwnershipHopTransfer[];
}

/**
 * One stage of the animals' life: a place they were, or the transit between
 * two places -- with who owned them there.
 */
export interface OwnershipStage {
  kind: 'PLACE' | 'TRANSIT';
  /** The place, on a PLACE stage. Null in transit. */
  entity: OwnershipParty | null;
  /** The leg's ends, on a TRANSIT stage. */
  fromEntity: OwnershipParty | null;
  toEntity: OwnershipParty | null;
  firstDate: string;
  lastDate: string;
  headCount: number | null;
  /** The courier, when a journey was recorded for the leg. */
  courier: { id: string; name: string } | null;
  /** Who owned them when the stage opened. Null when no handover is recorded. */
  owner: OwnershipParty | null;
  /** Set when ownership changed while the animals were at this stage. */
  ownerChangedTo: OwnershipParty | null;
  ownerChangedOn: string | null;
}

export interface OwnershipFlow {
  dealId: string;
  dealNo: string | null;
  owners: OwnershipOwner[];
  hops: OwnershipHop[];
  currentOwner: OwnershipOwner | null;
  /** What the chain is missing, in words. Shown, never papered over. */
  gaps: string[];
  /** Where the animals physically were, in order, with the owner at each stage. */
  journey: OwnershipStage[];
}

function mapOwnershipParty(row: any): OwnershipParty {
  return {
    id: String(row?.id || ''),
    name: String(row?.name || ''),
    entityType: String(row?.entity_type || ''),
    entityTypeOther: String(row?.entity_type_other || ''),
    registeredBusinessName: String(row?.registered_business_name || ''),
  };
}

export async function fetchOwnershipFlow(dealId: string): Promise<OwnershipFlow> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/ownership-flow/`);
  const data = await handleJson(res);
  return {
    dealId: String(data?.deal_id || dealId),
    dealNo: data?.deal_no ? String(data.deal_no) : null,
    owners: (Array.isArray(data?.owners) ? data.owners : []).map((row: any) => ({
      ...mapOwnershipParty(row),
      ownedFrom: row?.owned_from ? String(row.owned_from) : null,
      ownedUntil: row?.owned_until ? String(row.owned_until) : null,
      currentlyOwned: row?.currently_owned === null || row?.currently_owned === undefined
        ? null
        : Number(row.currently_owned),
      carcassCount: row?.carcass_count === null || row?.carcass_count === undefined
        ? null
        : Number(row.carcass_count),
    })),
    hops: (Array.isArray(data?.hops) ? data.hops : []).map((row: any) => ({
      fromEntity: mapOwnershipParty(row?.from_entity),
      toEntity: mapOwnershipParty(row?.to_entity),
      firstDate: String(row?.first_date || ''),
      lastDate: String(row?.last_date || ''),
      headCount: row?.head_count === null || row?.head_count === undefined ? null : Number(row.head_count),
      transferCount: Number(row?.transfer_count || 0),
      isDerived: Boolean(row?.is_derived),
      transfers: (Array.isArray(row?.transfers) ? row.transfers : []).map((t: any) => ({
        id: String(t?.id || ''),
        transferDate: String(t?.transfer_date || ''),
        headCount: t?.head_count === null || t?.head_count === undefined ? null : Number(t.head_count),
        notes: String(t?.notes || ''),
        isDerived: Boolean(t?.is_derived),
        documentName: t?.document_name ? String(t.document_name) : null,
        documentUrl: t?.document_url ? String(t.document_url) : null,
      })),
    })),
    currentOwner: data?.current_owner
      ? {
          ...mapOwnershipParty(data.current_owner),
          ownedFrom: data.current_owner?.owned_from ? String(data.current_owner.owned_from) : null,
          ownedUntil: data.current_owner?.owned_until ? String(data.current_owner.owned_until) : null,
          currentlyOwned: data.current_owner?.currently_owned === null || data.current_owner?.currently_owned === undefined
            ? null
            : Number(data.current_owner.currently_owned),
          carcassCount: data.current_owner?.carcass_count === null || data.current_owner?.carcass_count === undefined
            ? null
            : Number(data.current_owner.carcass_count),
        }
      : null,
    gaps: Array.isArray(data?.gaps) ? data.gaps.map((g: any) => String(g)) : [],
    journey: (Array.isArray(data?.journey) ? data.journey : []).map((row: any) => ({
      kind: row?.kind === 'TRANSIT' ? 'TRANSIT' : 'PLACE',
      entity: row?.entity ? mapOwnershipParty(row.entity) : null,
      fromEntity: row?.from_entity ? mapOwnershipParty(row.from_entity) : null,
      toEntity: row?.to_entity ? mapOwnershipParty(row.to_entity) : null,
      firstDate: String(row?.first_date || ''),
      lastDate: String(row?.last_date || ''),
      headCount: row?.head_count === null || row?.head_count === undefined ? null : Number(row.head_count),
      courier: row?.courier ? { id: String(row.courier.id), name: String(row.courier.name) } : null,
      owner: row?.owner ? mapOwnershipParty(row.owner) : null,
      ownerChangedTo: row?.owner_changed_to ? mapOwnershipParty(row.owner_changed_to) : null,
      ownerChangedOn: row?.owner_changed_on ? String(row.owner_changed_on) : null,
    })) as OwnershipStage[],
  };
}

export interface OwnershipTransferInput {
  fromEntityId: string;
  toEntityId: string;
  transferDate: string;
  headCount?: number | null;
  notes?: string;
}

export async function createOwnershipTransfer(dealId: string, input: OwnershipTransferInput): Promise<void> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/ownership-transfers/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      from_entity: input.fromEntityId,
      to_entity: input.toEntityId,
      transfer_date: input.transferDate,
      head_count: input.headCount ?? null,
      notes: input.notes || '',
    }),
  });
  await handleJson(res);
}

export async function deleteOwnershipTransfer(dealId: string, transferId: string): Promise<void> {
  const res = await authorizedFetch(
    `/api/deals/${encodeURIComponent(dealId)}/ownership-transfers/${encodeURIComponent(transferId)}/`,
    { method: 'DELETE' },
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(await res.text() || `Delete ownership transfer failed (${res.status})`);
  }
}

// --- Feedlot occupancy fee ------------------------------------------------

/**
 * The feedlot's standing charge, in two disjoint halves: booked for animals
 * that have left, accruing for animals still standing there. Disjoint by
 * construction, so the two add up without charging an animal twice.
 */
export interface FeedlotFee {
  dealId: string;
  asOf: string;
  bookedTotal: number;
  booked: { id: string; transactionDate: string | null; amount: number; description: string }[];
  accruingTotal: number;
  accruingAnimalCount: number;
  accruingAnimalDays: number;
  accruingFeedlots: {
    entityId: string;
    entityName: string;
    ratePerDay: number;
    animalCount: number;
    animalDays: number;
    amount: number;
  }[];
  total: number;
}

export async function fetchFeedlotFee(dealId: string): Promise<FeedlotFee> {
  const res = await authorizedFetch(`/api/deals/${encodeURIComponent(dealId)}/feedlot-fee/`);
  const data = await handleJson(res);
  return {
    dealId: String(data?.deal_id || dealId),
    asOf: String(data?.as_of || ''),
    bookedTotal: Number(data?.booked_total || 0),
    booked: (Array.isArray(data?.booked) ? data.booked : []).map((row: any) => ({
      id: String(row?.id || ''),
      transactionDate: row?.transaction_date ? String(row.transaction_date) : null,
      amount: Number(row?.amount || 0),
      description: String(row?.description || ''),
    })),
    accruingTotal: Number(data?.accruing_total || 0),
    accruingAnimalCount: Number(data?.accruing_animal_count || 0),
    accruingAnimalDays: Number(data?.accruing_animal_days || 0),
    accruingFeedlots: (Array.isArray(data?.accruing_feedlots) ? data.accruing_feedlots : []).map((row: any) => ({
      entityId: String(row?.entity_id || ''),
      entityName: String(row?.entity_name || ''),
      ratePerDay: Number(row?.rate_per_day || 0),
      animalCount: Number(row?.animal_count || 0),
      animalDays: Number(row?.animal_days || 0),
      amount: Number(row?.amount || 0),
    })),
    total: Number(data?.total || 0),
  };
}
